// In-app Dev Console v2 — manage dev cycles from the browser (internal only).
//
// Chat with a Cursor cloud agent that works on the app repo: it clones,
// edits, and opens a PR on a Cursor-hosted VM (never on prod infra). The
// workspace is tabbed — Chat, Activity (full event timeline), Terminal (agent
// shell + deploy output), PR & Deploy, Details (usage/cost/raw doc) — with
// live status: elapsed timer, last-activity pulse, per-session cost.
// Session management: stop, retry (re-run a failed/finished prompt), rename,
// delete. Sidebar has search + status filters.
//
// Access is triple-gated on the `internal: true` custom claim: nav visibility
// (shells), Firestore rules (read-only, internal only), and the backend API.
import React, { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { onAuthChanged, getClaims, getIdToken } from '../data/authPlane';
import { isPg } from '../data/supabaseClient';
import { isUploadConfigured } from '../data/uploadService';
import {
  collectFromFileList, collectFromDataTransfer, collectFromPaste, pickFolderEntries, applySkips, checkLimits, uploadAttachments,
} from '../dev/attachments';
import AttachmentTray, { QuestionsCard } from '../dev/AttachmentTray';
import DictateButton from '../dev/DictateButton';
import { enablePush, disablePush, pushSupported } from '../dev/push';
import { buildEnvelope, envelopeBlock, takeReport } from '../dev/devContext';
import { useDoc, useOrdered, setActiveDevSession } from '../dev/liveDocs';
import {
  ModeSelector, RoutingModeSelector, RepoSelector, ModelSelector, ModelParamsSelector, AdvancedAgentOptions,
  defaultParamsFor, buildParamsArray, agentOptionsPayload, DEFAULT_AGENT_OPTIONS,
  loadStoredConfig, saveStoredConfig, restoreModelPick, FALLBACK_MODES,
} from '../dev/AgentConfig';
import './DevConsole.css';
import { kitEnv } from '../data/kitEnv.js';
import EcosystemFrame, { EcosystemTicker } from '../components/three/EcosystemFrame';
import { deriveEcosystem } from '../components/three/ecosystemState';
import { deriveCommand, commandInspector, topologyColumns } from '../components/three/commandState';
import CommandView from '../components/command/CommandView';
import useCommandToggle, { CommandToggle } from '../components/command/useCommandToggle';
import { appPanels } from '../components/command/appHud';
import useTelemetry, { TelemetryControls, ActivityStrip } from '../components/command/useTelemetry';
import { trafficPanel, anomalyPanel, AnomalyTable, FlowTable, RuntimeRows } from '../components/command/telemetryHud';
import InfraPanel from '../components/infra/InfraPanel';
import GraphPanel from '../components/graph/GraphPanel';
import RoutinesPanel from '../components/graph/RoutinesPanel';
import IdePanel from '../components/ide/IdePanel';

const API = kitEnv('UPLOAD_API', process.env.REACT_APP_UPLOAD_API || '');

// ---- auth-claims hook (exported for the shells' nav gating) -----------------
// `internal: true` gates the internal surfaces (Dev Console, Cost Report).
// `appRole` ('admin' | 'product' | 'tester' | 'waitlist') manages pre-launch
// visibility: 'waitlist' hides pre-launch surfaces. No role = tester
// behavior, since every existing account is an invited tester.
export function useClaims() {
  const [claims, setClaims] = useState({ internal: false, role: null });
  useEffect(() => {
    let alive = true;
    // Dual-plane: getClaims() normalizes Firebase ID-token claims and Supabase
    // app_metadata to the same { internal, appRole, … } shape; onAuthChanged
    // fires immediately with the current user on both planes.
    const check = async () => {
      try {
        const c = await getClaims();
        if (alive) setClaims({ internal: c.internal === true, role: c.appRole || null });
      } catch {
        if (alive) setClaims({ internal: false, role: null });
      }
    };
    const unsub = onAuthChanged(() => check());
    return () => { alive = false; unsub(); };
  }, []);
  return claims;
}

export function useInternalClaim() {
  return useClaims().internal;
}

async function api(path, body, method) {
  // Firebase ID token | Supabase access_token — the backend accepts both.
  const token = await getIdToken();
  const res = await fetch(`${API}${path}`, {
    method: method || (body === undefined ? 'GET' : 'POST'),
    headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
    ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok) {
    // The Dev Console error contract (backend shared/devOps/devErrors.js):
    // `error` is the plain headline, the rest drives the action button.
    const err = new Error(json.error || `${path}: ${res.status}`);
    Object.assign(err, { status: res.status, code: json.code || null, action: json.action || null, retryable: json.retryable, detail: json.detail || null, requestId: json.requestId || null });
    throw err;
  }
  return json;
}

// ---- mermaid (lazy) ----------------------------------------------------------
let mermaidReady = null;
async function getMermaid() {
  if (!mermaidReady) {
    mermaidReady = import('mermaid').then((m) => {
      const mermaid = m.default;
      mermaid.initialize({ startOnLoad: false, securityLevel: 'strict', theme: 'dark' });
      return mermaid;
    });
  }
  return mermaidReady;
}

let mermaidSeq = 0;
function Mermaid({ code }) {
  const [svg, setSvg] = useState(null);
  const [err, setErr] = useState(null);
  useEffect(() => {
    let alive = true;
    getMermaid()
      .then((mermaid) => mermaid.render(`devmmd-${(mermaidSeq += 1)}`, code))
      .then(({ svg: out }) => { if (alive) setSvg(out); })
      .catch((e) => { if (alive) setErr(e.message); });
    return () => { alive = false; };
  }, [code]);
  if (err) return <pre className="devc-mono">{code}</pre>;
  if (!svg) return <div className="devc-dim">Rendering diagram…</div>;
  // Mermaid output with securityLevel 'strict' is sanitized SVG.
  // eslint-disable-next-line react/no-danger
  return <div className="devc-mermaid" dangerouslySetInnerHTML={{ __html: svg }} />;
}

// Markdown with ```mermaid fences rendered as live diagrams.
const mdComponents = {
  code({ inline, className, children, ...props }) {
    const text = String(children).replace(/\n$/, '');
    if (!inline && /language-mermaid/.test(className || '')) return <Mermaid code={text} />;
    return <code className={className} {...props}>{children}</code>;
  },
};

// ---- status chip + helpers -----------------------------------------------------
const CHIP = {
  starting: ['Starting', 'blue'], running: ['Running', 'blue'], cancelling: ['Cancelling', 'amber'],
  finished: ['Finished', 'green'], error: ['Error', 'red'], 'startup-failed': ['Failed to start', 'red'],
  cancelled: ['Cancelled', 'gray'],
};
const Chip = ({ status }) => {
  const [label, tone] = CHIP[status] || [status || '—', 'gray'];
  return <span className={`devc-chip ${tone}`}>{label}</span>;
};

// What the run is doing right now, from the session's `phase` (written by
// the sovereign runner and the launch path) — the header and the working row
// say this instead of only how long it has been.
const PHASE_LABEL = {
  launch: 'Launching runner',
  queued: 'Waiting for a free runner slot',
  mode: 'Choosing how to work',
  plan: 'Planning (no edits)',
  runner: 'Runner started',
  clone: 'Cloning repo',
  branch: 'Preparing branch',
  kit: 'Checking out kit reference',
  agent: 'Agent working',
  'agent-done': 'Agent finished, saving work',
  push: 'Committing and pushing',
  pr: 'Opening pull request',
  done: 'Done',
  failed: 'Failed',
};
// After this long without any event or heartbeat the UI says so plainly —
// the backend marks the session failed after dev.runnerStaleMinutes.
const STALL_MS = 90 * 1000;

function phaseLine(session) {
  if (!session) return '';
  const base = PHASE_LABEL[session.phase] || (session.status === 'starting' ? 'Waiting for the runner' : 'Working');
  if (session.waitingFor === 'capacity') return `${PHASE_LABEL.queued}${session.queuePosition ? ` · position ${session.queuePosition}` : ''}`;
  if (session.phase === 'agent') {
    const n = session.toolCalls || 0;
    const tools = n ? `${n} tool call${n === 1 ? '' : 's'}` : 'reading the repo';
    return `${base} · ${tools}${session.lastTool ? ` · ${session.lastTool}` : ''}`;
  }
  return base;
}

// The one argument worth showing for a tool call: the path, command or
// query — whichever the tool took.
function toolArg(t) {
  let arg = t.args;
  try { const j = typeof arg === 'string' ? JSON.parse(arg) : arg; arg = j?.path || j?.file || j?.filePath || j?.target_file || j?.command || j?.cmd || j?.pattern || j?.query || j?.url || ''; } catch { /* raw */ }
  return arg ? String(arg) : '';
}

// Consecutive tool events fold into one compact row in the chat (the
// Activity tab keeps the full detail): "7 tool calls — shell ×2, edit ×3…".
function ToolGroup({ items }) {
  const [open, setOpen] = useState(false);
  const counts = {};
  for (const t of items) counts[t.name || 'tool'] = (counts[t.name || 'tool'] || 0) + 1;
  const summary = Object.entries(counts).map(([k, v]) => (v > 1 ? `${k} ×${v}` : k)).join(', ');
  const running = items.some((t) => t.status === 'running');
  return (
    <div className={`devc-toolgroup${running ? ' is-running' : ''}`}>
      <button type="button" className="devc-toolgroup-head" onClick={() => setOpen(!open)} aria-expanded={open}>
        <span className={`devc-step-dot${running ? ' live' : ''}`} aria-hidden="true" />
        {items.length} tool call{items.length === 1 ? '' : 's'} — {summary}
        <span className="devc-toolgroup-chev" aria-hidden="true">{open ? '▾' : '▸'}</span>
      </button>
      {open && (
        <ol className="devc-toolgroup-list">
          {items.map((t) => {
            const arg = toolArg(t);
            return (
              <li key={t.id}>
                <span className="devc-toolgroup-name">{t.name}</span>
                {arg ? <code>{String(arg).slice(0, 140)}</code> : null}
                {t.status === 'running' && <span className="devc-chip blue">running</span>}
                {t.status === 'error' && <span className="devc-chip red">error</span>}
              </li>
            );
          })}
        </ol>
      )}
    </div>
  );
}

const LIVE = ['starting', 'running', 'cancelling'];
const FAILED = ['error', 'startup-failed', 'cancelled'];

// Short name for the layer repo (the last meaningful path segment —
// "layer-texas-gold-card", not a CodeCommit console URL). Full URL goes in
// the title attribute.
function repoLabel(url) {
  if (!url) return '';
  try {
    const u = new URL(url);
    const parts = u.pathname.split('/').filter((p) => p && !['browse', 'tree', 'blob', '-'].includes(p));
    const last = parts[parts.length - 1] || u.host;
    return last.replace(/\.git$/, '');
  } catch {
    // scp-style remotes (git@host:org/repo.git)
    return String(url).replace(/\.git$/, '').split(/[/:]/).filter(Boolean).pop() || String(url);
  }
}

// Sidebar badge from the session's flat ship summary (shared/devOps/ship.js
// summarizeShip): what the session needs from a person, at a glance.
const NEEDS_YOU = ['answer', 'approve', 'ship', 'retry', 'followup', 'kitdev', 'release'];
function shipBadge(s) {
  const n = s.shipNext;
  if (!n) return s.deployJobId ? <span className="devc-chip gray">deploy</span> : null;
  if (s.shipRunning) return <span className={`devc-chip ${s.shipStalled ? 'amber' : 'blue'}`} title={n.label}>{s.shipStalled ? 'deploy stalled' : 'deploying'}</span>;
  if (n.action === 'blocked') return <span className="devc-chip red" title={n.label}>blocked</span>;
  if (NEEDS_YOU.includes(n.action)) return <span className="devc-chip amber" title={n.label}>needs you</span>;
  if (n.action === 'done') return <span className="devc-chip green" title={n.label}>shipped{n.env === 'uat' ? ' · UAT' : ''}</span>;
  return null;
}

const fmtDur = (ms) => {
  if (ms == null || ms < 0) return '—';
  const s = Math.floor(ms / 1000);
  if (s < 60) return `${s}s`;
  if (s < 3600) return `${Math.floor(s / 60)}m ${s % 60}s`;
  return `${Math.floor(s / 3600)}h ${Math.floor((s % 3600) / 60)}m`;
};
const fmtAgo = (ts) => {
  if (!ts) return '';
  const s = Math.floor((Date.now() - ts) / 1000);
  if (s < 5) return 'just now';
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return new Date(ts).toLocaleDateString();
};
const fmtTok = (n) => (n == null ? '—' : n >= 1e6 ? `${(n / 1e6).toFixed(1)}M` : n >= 1e3 ? `${(n / 1e3).toFixed(1)}k` : String(n));
const costUsd = (u) => {
  if (!u) return null;
  const cents = u.chargedCents ?? u.rawCostCents;
  return cents == null ? null : cents / 100;
};

// Re-render on a 1s tick while `on` (elapsed timers, "Xs ago" labels).
function useTick(on) {
  const [, setN] = useState(0);
  useEffect(() => {
    if (!on) return undefined;
    const t = setInterval(() => setN((n) => n + 1), 1000);
    return () => clearInterval(t);
  }, [on]);
}

// Live doc / collection hooks: dev/liveDocs.js (shared with the dock's
// dev-session bridge).

// ---- deploy card ---------------------------------------------------------------
// Explicit push actions instead of a target picker: Push client (SPA build +
// publish), Push server (backend fast overlay, optional full image rebuild),
// Push rules (firebase plane only — on supabase, schema rides the server
// push). Each pushes to the selected environment; UAT-first, production is
// the deliberate promote. On stamped instances UAT is a second full instance
// sold as an add-on — no add-on renders the switch disabled with an upsell.
function DeployCard({ session, sessionId, setup }) {
  const uat = setup?.uat || { mode: 'service', configured: true };
  const uatReady = uat.mode === 'service' || !!uat.configured;
  const [env, setEnv] = useState(uatReady ? 'uat' : 'prod');
  const [fullRebuild, setFullRebuild] = useState(false);
  const [busy, setBusy] = useState(null); // which action is in flight
  const [err, setErr] = useState(null);
  const job = useDoc(session?.deployJobId ? `devJobs/${session.deployJobId}` : null);
  const showRules = !isPg();
  // Full rebuild on ECS UAT would leak into prod (shared image) — instance
  // UAT builds its own image, so the option stays live there.
  const fullAllowed = env !== 'uat' || uat.mode === 'instance';
  // What already reached the selected environment (ship.js shipPlan): a push
  // that landed reads "✓ pushed" and stays unclickable, so coming back after
  // a long deploy you cannot fire the same one twice (member feedback,
  // bug 0U40zdn3 2026-10-19). A failed or stalled push stays live.
  const [plan, setPlan] = useState(null);
  const planStamp = [session?.shipAt, session?.deployJobId, session?.shipJobId, session?.prMerged, job?.status].join('|');
  useEffect(() => {
    if (!session?.prUrl) { setPlan(null); return undefined; }
    let alive = true;
    api(`/dev/sessions/${sessionId}/ship?env=${env}`)
      .then((p) => { if (alive) setPlan(p); })
      .catch(() => { if (alive) setPlan(null); });
    return () => { alive = false; };
  }, [sessionId, session?.prUrl, env, planStamp]);
  const landed = (stepId) => plan?.env === env && plan?.steps?.some((s) => s.id === stepId && s.status === 'done');
  // Ship advice is filenames only (plan.deploy). The patch walk is the
  // diff viewer, and only when someone asks for it — loading it here
  // throttled the merge.
  const advice = plan?.deploy || null;
  useEffect(() => {
    if (!advice) return;
    setFullRebuild(!!advice.full && fullAllowed);
  }, [advice, fullAllowed]);

  const push = async (label, targets, envOverride) => {
    setBusy(label); setErr(null);
    try {
      await api('/dev/deploy', { sessionId, targets, env: envOverride || env });
    } catch (e) {
      setErr(e);
    } finally {
      setBusy(null);
    }
  };

  if (!session?.prUrl) {
    return (
      <div className="devc-empty">
        <h3>No pull request yet</h3>
        <p>
          When the agent finishes a change it opens a PR against the repo — it will show up here with
          one-click Merge &amp; Deploy. {LIVE.includes(session?.status) ? 'The agent is still working…' : ''}
        </p>
      </div>
    );
  }
  const running = busy || job?.status === 'running' || job?.status === 'queued';
  const verb = session.prMerged ? 'Push' : 'Merge & push';
  const envLabel = env === 'prod' ? 'Prod' : 'UAT';
  const clientDone = landed('client');
  const serverDone = landed('server') && !(fullRebuild && fullAllowed); // a full rebuild is a different push
  const rulesDone = landed('schema');
  const doneTitle = `Already pushed to ${envLabel} for this pull request — use the Ship strip to retry a failed step`;
  const known = !!(advice && Array.isArray(advice.targets));
  const hot = (on) => (known && on ? (env === 'prod' ? 'danger' : 'primary') : '');
  const promoteTargets = () => {
    if (!advice?.targets?.length) return ['frontend', 'backend-fast', ...(showRules ? ['rules'] : [])];
    return advice.targets
      .map((t) => (t === 'backend-full' && !fullAllowed ? 'backend-fast' : t))
      .filter((t) => t !== 'rules' || showRules);
  };
  const promoteBits = known ? [
    advice.client ? 'client' : '',
    advice.server ? (advice.full && fullAllowed ? 'server (full rebuild)' : 'server') : '',
    advice.rules && showRules ? 'rules' : '',
  ].filter(Boolean) : [];
  return (
    <div className="devc-card">
      <div className="devc-card-title">
        Pull request
        {session.prMerged && <span className="devc-chip green">Merged</span>}
        {job && <span className={`devc-chip ${job.status === 'done' ? 'green' : job.status === 'error' ? 'red' : 'blue'}`}>{job.env === 'uat' ? 'UAT' : 'Prod'} deploy: {job.stage || job.status}</span>}
      </div>
      <a href={session.prUrl} target="_blank" rel="noopener noreferrer" className="devc-link">{session.prUrl}</a>
      {session.branch && <div className="devc-dim">branch {session.branch}</div>}
      <div className="devc-env-pick">
        <label className={`${env === 'uat' ? 'active' : ''} ${uatReady ? '' : 'devc-dim'}`}>
          <input
            type="radio"
            name="devc-env"
            disabled={!uatReady}
            checked={env === 'uat'}
            onChange={() => setEnv('uat')}
          />
          <strong>UAT</strong> — {uat.mode === 'instance' ? 'your staging instance' : 'staging tier'} (default landing zone)
        </label>
        <label className={env === 'prod' ? 'active devc-env-prod' : ''}>
          <input type="radio" name="devc-env" checked={env === 'prod'} onChange={() => setEnv('prod')} />
          <strong>Production</strong> — live app (promote after UAT checks)
        </label>
      </div>
      {uat.mode === 'instance' && !uat.configured && (
        <div className="devc-upsell">
          <strong>Add a UAT environment</strong> — a full second instance of your app
          (<code>-uat</code> URL, own database) to test changes on before they touch
          production.{' '}
          <a href="https://nventr.io/orgs" target="_blank" rel="noopener noreferrer">
            Add UAT from your org page →
          </a>
        </div>
      )}
      {advice?.reason && <p className="devc-dim" style={{ margin: '10px 0 0' }}>{advice.reason}</p>}
      <div className="devc-push-row" data-agent-guard>
        <button
          className={`devc-btn ${clientDone ? '' : hot(advice?.client)}`}
          disabled={running || clientDone}
          onClick={() => push('client', ['frontend'])}
          title={clientDone ? doneTitle : 'Build the SPA from merged main and publish it'}
        >
          {busy === 'client' ? 'Pushing…' : clientDone ? `client → ${envLabel} ✓ pushed` : `${verb} client → ${envLabel}`}
        </button>
        <button
          className={`devc-btn ${serverDone ? '' : hot(advice?.server)}`}
          disabled={running || serverDone || (fullRebuild && !fullAllowed)}
          onClick={() => push('server', [fullRebuild && fullAllowed ? 'backend-full' : 'backend-fast'])}
          title={serverDone ? doneTitle : 'Deploy the backend from merged main (fast overlay unless full rebuild is checked)'}
        >
          {busy === 'server' ? 'Pushing…' : serverDone ? `server → ${envLabel} ✓ pushed` : `${verb} server → ${envLabel}`}
        </button>
        {showRules && (
          <button
            className={`devc-btn ${rulesDone ? '' : hot(advice?.rules)}`}
            disabled={running || rulesDone}
            onClick={() => push('rules', ['rules'])}
            title={rulesDone ? doneTitle : 'Publish Firestore security rules'}
          >
            {busy === 'rules' ? 'Pushing…' : rulesDone ? `rules → ${envLabel} ✓ pushed` : 'Push rules'}
          </button>
        )}
      </div>
      <label className={`devc-full-opt ${fullAllowed ? '' : 'devc-dim'}`}>
        <input
          type="checkbox"
          checked={fullRebuild && fullAllowed}
          disabled={!fullAllowed}
          onChange={(e) => setFullRebuild(e.target.checked)}
          title={fullAllowed ? undefined : 'Production-only on this tier: the container image is shared'}
        />
        Full server rebuild (deps / Dockerfile changes — slower)
      </label>
      {uatReady && env === 'uat' && (
        <button
          className="devc-btn devc-promote"
          data-agent-noclick
          disabled={running}
          onClick={() => push('promote', promoteTargets(), 'prod')}
          title={promoteBits.length ? `Deploy ${promoteBits.join(' + ')} to production` : 'Deploy the same merged main to production (client + server)'}
        >
          {busy === 'promote' ? 'Promoting…' : (promoteBits.length ? `⬆ Promote ${promoteBits.join(' + ')} → production` : '⬆ Promote UAT → production')}
        </button>
      )}
      {job?.message && (job.status === 'running' || job.status === 'queued') && <div className="devc-status" role="status">{job.message}</div>}
      {job?.status === 'error' && <FailureCard failure={job} title={job.errorUser ? null : 'Deploy failed'} />}
      {err && <FailureCard failure={err} />}
      {job && <JobLogTail jobId={session.deployJobId} />}
      {job?.forwarded && (
        <div className="devc-dim">
          Running on the UAT instance — full logs live in its own Dev Console.
        </div>
      )}
    </div>
  );
}

// ---- ship strip -------------------------------------------------------------------
// One row above the tabs: every step this change needs to reach the running
// app (shared/devOps/ship.js shipPlan — answers → plan → PR → merge →
// schema → server → client → verify → knowledge) and ONE next-action button.
// The session doc's flat summary (shipAt and the fields that feed it) says
// when to refetch; the ship job's own doc keeps the chips moving while the
// runner works. Ship / Retry are guarded: the copilot runs the same action
// server-side (dev_ship) after you confirm, never by clicking here.
const STEP_TONE = { done: 'green', running: 'blue', needed: 'amber', pending: 'gray', blocked: 'red', failed: 'red', skipped: 'gray' };
const STEP_MARK = { done: '✓', running: '', needed: '•', pending: '·', blocked: '⛔', failed: '✕', skipped: '–' };

function ShipStrip({ session, sessionId, onAction, busy, onOpenTerminal }) {
  const [plan, setPlan] = useState(null);
  const [err, setErr] = useState(null);
  const [acting, setActing] = useState(false);
  const [confirmProd, setConfirmProd] = useState(false);
  const jobId = plan?.jobId || session?.shipJobId || session?.deployJobId || null;
  const job = useDoc(jobId ? `devJobs/${jobId}` : null);
  const stamp = [session?.shipAt, session?.status, session?.prUrl, session?.planExecuted, session?.awaitingAnswers, session?.prMerged, session?.deployJobId, session?.shipJobId, job?.updatedAt, session?.verdict?.state, session?.verdict?.at, session?.loop?.at].join('|');
  useEffect(() => {
    if (!sessionId) { setPlan(null); return undefined; }
    let alive = true;
    api(`/dev/sessions/${sessionId}/ship`)
      .then((p) => { if (alive) { setPlan(p); setErr(null); } })
      .catch((e) => { if (alive) setErr(e.message); });
    return () => { alive = false; };
  }, [sessionId, stamp]);
  useEffect(() => { setConfirmProd(false); }, [sessionId, plan?.next?.env]);

  if (!sessionId) return null;
  if (!plan) {
    return (
      <div className="devc-ship" data-nova-note="Ship strip: loading what this change still needs">
        <span className="devc-dim">{err ? `Ship status unavailable: ${err}` : 'Working out what this change needs…'}</span>
      </div>
    );
  }
  const next = plan.next || {};
  const shown = plan.steps.filter((s) => s.status !== 'skipped');
  const runningStep = plan.steps.find((s) => s.status === 'running');
  const envName = (e) => (e === 'prod' ? 'Production' : 'UAT');

  const run = async (retry) => {
    setActing(true); setErr(null);
    try {
      await api('/dev/ship', { sessionId, env: next.env || plan.env, retry });
      setConfirmProd(false);
    } catch (e) { setErr(e.message); } finally { setActing(false); }
  };
  // An independent verifier (shared/devOps/verify.js): a browser-driving
  // agent on the verify image proves the change on the live surface and
  // pins a verdict to the PR head. Offered once the session has a branch and
  // the agent is idle; the verdict rides the Verify step.
  const verdict = plan.steps.find((s) => s.id === 'verify')?.verdict || null;
  const canVerify = session?.kind !== 'verifier' && !!session?.branch && !['starting', 'running'].includes(session?.status) && verdict?.state !== 'running';
  const verify = async () => {
    setActing(true); setErr(null);
    try { await api(`/dev/sessions/${sessionId}/verify`, {}); } catch (e) { setErr(e.message); } finally { setActing(false); }
  };
  const VERDICT_LABEL = { pass: 'verifier passed', fail: 'verifier failed', running: 'verifier running', stale: 'verdict is for an older head', unverified: 'verifier reported nothing', error: 'verifier died' };
  // Kit sessions (repo kind kit) add two plane-side steps — kitdev (preview
  // on the kit-dev instance) and release (tag + fleet roll); both go through
  // the same /dev/ship call, the plan's next step names the action.
  const kitChain = plan.repo?.kind === 'kit';
  const planeChain = plan.repo?.kind === 'plane';
  const confirmable = next.action === 'ship' || next.action === 'release';
  const click = () => {
    if (confirmable || next.action === 'retry' || next.action === 'kitdev') {
      const prodLike = next.env === 'prod' && (confirmable) && !(kitChain && next.stepId === 'merge');
      if (prodLike && !confirmProd) { setConfirmProd(true); return; }
      run(next.action === 'retry');
      return;
    }
    onAction?.(next.action, next);
  };
  const actionable = ['answer', 'approve', 'ship', 'retry', 'followup', 'kitdev', 'release', 'review'].includes(next.action);
  const tone = next.action === 'done' ? 'devc-ship-done'
    : next.action === 'blocked' ? 'devc-ship-blocked'
      : next.action === 'wait' ? 'devc-ship-wait'
        : next.action === 'retry' || next.action === 'followup' ? 'devc-ship-retry'
          : next.action === 'none' ? 'devc-ship-none' : 'devc-ship-needs';
  const btnClass = next.action === 'retry' || next.action === 'followup' ? 'danger'
    : (next.action === 'ship' && next.env === 'prod' && !(kitChain && next.stepId === 'merge')) || next.action === 'release' ? 'devc-promote' : 'primary';
  const confirmLabel = next.action === 'release' ? 'Yes, release the kit' : planeChain ? 'Yes, ship the control plane' : 'Yes, ship to Production';
  const confirmText = next.action === 'release' ? 'This rolls the kit to the canary, then every instance behind it. Sure?'
    : planeChain ? 'This deploys nventr.io itself. Sure?' : 'This goes live for everyone. Sure?';
  const previewUrl = kitChain ? (plan.steps.find((s) => s.id === 'kitdev')?.url || next.url || null) : null;
  const where = kitChain ? 'the kit' : planeChain ? 'the control plane' : envName(plan.env);
  const note = `Ship strip for this session (${plan.repo?.label || 'this app'}). Environment ${where}. Steps: ${plan.steps.map((s) => `${s.label} ${s.status}`).join(', ')}. Next: ${next.label}${next.reason ? ` (${next.reason})` : ''}.${plan.stalled ? ' A deploy looks stalled.' : ''}`;
  return (
    <div className={`devc-ship ${tone}`} data-nova-note={note}>
      <div className="devc-ship-steps" role="list" aria-label="Ship steps">
        {shown.map((s) => (
          <span
            key={s.id}
            role="listitem"
            className={`devc-ship-step ${STEP_TONE[s.status] || 'gray'}${s.status === 'running' ? ' is-live' : ''}${s.stalled ? ' is-stalled' : ''}`}
            title={[s.label, s.status, s.reason || '', s.stage ? `stage ${s.stage}` : ''].filter(Boolean).join(' · ')}
          >
            {s.status === 'running' ? <span className={`devc-pulse${s.stalled ? ' is-stalled' : ''}`} /> : <span className="devc-ship-mark" aria-hidden="true">{STEP_MARK[s.status] || ''}</span>}
            {s.label}
            {s.id === 'pr' && s.prNumber ? <span className="devc-dim"> #{s.prNumber}</span> : null}
            {s.id === 'verify' && verdict && verdict.state !== 'none' ? <span className="devc-dim" title={VERDICT_LABEL[verdict.state] || verdict.state}> · {verdict.state === 'running' ? '…' : verdict.state}{verdict.headSha ? ` @${String(verdict.headSha).slice(0, 7)}` : ''}</span> : null}
            {s.id === 'review' && s.headSha ? <span className="devc-dim"> @{String(s.headSha).slice(0, 7)}</span> : null}
          </span>
        ))}
        {plan.review?.canStart && !shown.some((s) => s.id === 'review' && ['needed', 'running'].includes(s.status)) && (
          <button type="button" className="devc-filter" onClick={() => onAction?.('review', next)} disabled={acting || busy} title="Start a review graph: independent verifiers drive the change on the live surface and a judge from another model family decides whether it may merge" data-nova-note="Review: start a review graph (verifiers + judge) on this pull request">
            {plan.review.stale ? 'Review again' : 'Review'}
          </button>
        )}
        {canVerify && shown.some((s) => s.id === 'verify') && (
          <button type="button" className="devc-filter" onClick={verify} disabled={acting || busy} title="Start an independent verifier: a browser-driving agent proves this change on the live surface and pins its verdict to the PR head" data-nova-note="Verify: start an independent verifier session for this change">
            {verdict?.state === 'stale' || verdict?.state === 'fail' ? 'Verify again' : 'Verify'}
          </button>
        )}
        {plan.loop && (plan.loop.turns > 0 || plan.loop.verifyTurns > 0) && (
          <span className={`devc-chip ${plan.loop.state === 'fixing' || plan.loop.state === 'verifying' ? 'blue' : plan.loop.state === 'red' ? 'red' : 'gray'}`} title={plan.loop.stop ? `Fix loop stopped: ${plan.loop.stop}` : plan.loop.lastCheck ? `Fixing: ${plan.loop.lastCheck}` : 'Fix turns the agent took on red checks or a failed verifier verdict (dev.loop.maxTurns)'} data-nova-note="Inner loop: fix turns spent against the dev.loop budget">
            turn {Math.max(plan.loop.turns, plan.loop.verifyTurns)}/{plan.loop.maxTurns}{plan.loop.state === 'fixing' ? '…' : plan.loop.state === 'verifying' ? ' · verifying' : ''}
          </span>
        )}
        {plan.hasUat && <span className="devc-chip gray" title="The environment these steps are about">{envName(plan.env)}</span>}
        {(kitChain || planeChain) && <span className="devc-chip gray" title={`This session works ${plan.repo.label}`}>{plan.repo.label}</span>}
        {previewUrl && <a className="devc-filter" href={previewUrl} target="_blank" rel="noopener noreferrer" title="The kit-dev instance running this branch">kit-dev ↗</a>}
      </div>
      <div className="devc-ship-next">
        {runningStep && (
          <span className="devc-ship-live">
            <span className={`devc-pulse${plan.stalled ? ' is-stalled' : ''}`} />
            {plan.stalled ? `No deploy output for a while — ${runningStep.label.toLowerCase()} looks stalled.` : (runningStep.reason || `${runningStep.label}${runningStep.stage && runningStep.stage !== runningStep.id ? ` · ${runningStep.stage}` : ''}…`)}
            {jobId && <button type="button" className="devc-filter" onClick={onOpenTerminal}>Terminal</button>}
          </span>
        )}
        {next.action === 'blocked' && <span className="devc-ship-reason">{next.reason}</span>}
        {(next.action === 'retry' || next.action === 'followup') && next.reason && <span className="devc-ship-reason">{next.reason}</span>}
        {['ship', 'kitdev', 'release'].includes(next.action) && next.summary && !confirmProd && <span className="devc-dim">{next.summary}</span>}
        {confirmProd && <span className="devc-ship-reason">{confirmText}</span>}
        <span className="devc-ship-actions" data-agent-guard>
          {confirmProd && <button type="button" className="devc-btn" onClick={() => setConfirmProd(false)}>Not yet</button>}
          <button
            type="button"
            className={`devc-btn ${actionable ? btnClass : ''}`}
            disabled={!actionable || acting || busy}
            onClick={click}
            title={next.reason || next.label}
          >
            {acting ? 'Starting…' : confirmProd ? confirmLabel : next.label}
          </button>
        </span>
      </div>
      {err && <div className="devc-err devc-ship-err">{err}</div>}
    </div>
  );
}

// Any deploy job running on this instance, wherever it came from (this
// session, another, the copilot, a UAT peer) — one line under the header so
// a deploy in flight is never invisible.
function DeployBanner({ sessions, onOpen }) {
  const jobs = useOrdered('devJobs', 'updatedAt', 'desc', 12);
  const live = jobs.filter((j) => ['queued', 'running'].includes(j.status));
  if (!live.length) return null;
  const titleOf = (j) => sessions.find((s) => s.id === j.sessionId)?.title || (j.remote ? 'control plane / UAT peer' : j.requestedByEmail || 'deploy');
  return (
    <div className="devc-deploy-banner" role="status" data-nova-note={`${live.length} deploy${live.length === 1 ? '' : 's'} running now`}>
      {live.map((j) => (
        <button type="button" key={j.id} className="devc-deploy-banner-item" onClick={() => onOpen?.(j)} title="Open the session and its terminal">
          <span className="devc-pulse" />
          Deploying {(j.targets || []).join(' + ') || 'change'} → {j.env === 'uat' ? 'UAT' : 'Production'}
          {j.message ? <span className="devc-dim"> · {String(j.message).slice(0, 140)}</span> : j.stage ? <span className="devc-dim"> · {j.stage}</span> : null}
          <span className="devc-dim"> · {titleOf(j)}</span>
        </button>
      ))}
    </div>
  );
}

// Preview channel: build the PR branch onto a temporary Firebase Hosting URL —
// see the change live before merging anything.
function PreviewCard({ session, sessionId, onReady }) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const job = useDoc(session?.previewJobId ? `devJobs/${session.previewJobId}` : null);
  const building = job && ['queued', 'running'].includes(job.status);

  const go = async () => {
    setBusy(true); setErr(null);
    try {
      await api('/dev/preview', { sessionId });
    } catch (e) {
      setErr(e);
    } finally {
      setBusy(false);
    }
  };

  if (!session?.prUrl || session.prMerged) return null;
  return (
    <div className="devc-card">
      <div className="devc-card-title">
        Preview before you ship
        {job && <span className={`devc-chip ${job.status === 'done' ? 'green' : job.status === 'error' ? 'red' : 'blue'}`}>Preview: {job.stage || job.status}</span>}
      </div>
      <p className="devc-dim" style={{ margin: '2px 0 10px' }}>
        Builds this PR&apos;s branch to a temporary URL (expires in 7 days). Production stays untouched —
        merge only when it looks right.
      </p>
      {session.previewUrl && (
        <div style={{ marginBottom: 10 }}>
          <a className="devc-link" href={session.previewUrl} target="_blank" rel="noopener noreferrer">{session.previewUrl}</a>
          {onReady && <button className="devc-btn" style={{ marginLeft: 10 }} onClick={onReady}>Open Preview tab</button>}
        </div>
      )}
      <button className="devc-btn primary" disabled={busy || building} onClick={go}>
        {building ? 'Building preview…' : session.previewUrl ? 'Rebuild preview' : 'Build preview'}
      </button>
      {job?.message && building && <div className="devc-status" role="status">{job.message}</div>}
      {job?.status === 'error' && <FailureCard failure={job} title={job.errorUser ? null : 'Preview failed'} />}
      {err && <FailureCard failure={err} />}
      {building && <JobLogTail jobId={session.previewJobId} />}
    </div>
  );
}

function JobLogTail({ jobId }) {
  const logs = useOrdered(jobId ? `devJobs/${jobId}/logs` : null, 'seq', 'asc', 1000);
  const end = useRef(null);
  useEffect(() => { end.current?.scrollIntoView({ block: 'nearest' }); }, [logs.length]);
  if (!logs.length) return null;
  return (
    <div className="devc-joblog">
      {logs.slice(-40).map((l) => <pre key={l.id}>{l.text}</pre>)}
      <div ref={end} />
    </div>
  );
}

// ---- plan card -------------------------------------------------------------------
// A plan-mode run's final plan arrives as a dedicated `plan` event (full
// markdown — backend extracts it from the SDK's createPlan tool call). It
// renders as a structured card in the chat timeline, visually distinct from
// regular messages, so the user reviews exactly what will run BEFORE
// Approve & Execute. Long plans start collapsed; a regenerated plan
// supersedes earlier ones, which collapse to a stub instead of leaving the
// history ambiguous about which plan will execute.
const PLAN_COLLAPSE_CHARS = 2500;

// Does a result event's text just repeat the assistant text streamed since
// the previous user turn? (Both runtimes' CLIs/SDKs hand back the final
// message as the "result" too.)
function resultRepeatsAssistant(result, events) {
  const norm = (t) => String(t || '').replace(/\s+/g, ' ').trim();
  const r = norm(result.text);
  if (!r) return false;
  const parts = [];
  for (let i = events.length - 1; i >= 0; i -= 1) {
    const e = events[i];
    if (e.seq >= result.seq) continue;
    if (e.type === 'user') break;
    if (e.type === 'assistant' && e.text) parts.unshift(norm(e.text));
  }
  if (!parts.length) return false;
  if (parts[parts.length - 1] === r) return true;
  const joined = norm(parts.join(' '));
  return joined === r || (r.length > 40 && joined.includes(r));
}

function PlanCard({ e, superseded, pending, execLabel, execInvalid }) {
  const [expanded, setExpanded] = useState(false);
  // A plan under review is never folded — members read "Phase 1" above the
  // fade and thought that was the whole plan. Only executed/old plans fold.
  const long = !pending && (e.text || '').length > PLAN_COLLAPSE_CHARS;
  if (superseded) {
    return (
      <div className="devc-msg plan superseded">
        <div className="devc-msg-who">
          📋 Plan <span className="devc-chip gray">superseded</span>
        </div>
        {expanded
          ? <ReactMarkdown remarkPlugins={[remarkGfm]} components={mdComponents}>{e.text || '(empty plan)'}</ReactMarkdown>
          : null}
        <button className="devc-filter" onClick={() => setExpanded(!expanded)}>
          {expanded ? 'Hide old plan' : 'Show old plan'}
        </button>
      </div>
    );
  }
  return (
    <div className="devc-msg plan">
      <div className="devc-msg-who">
        📋 Execution plan{pending ? ' — review before approving' : ''}
      </div>
      {e.error && <div className="devc-err">Plan generation failed: {e.error}</div>}
      {!e.error && !e.text && pending && (
        <div className="devc-err">
          The planning run finished but the plan came back empty — use &ldquo;Revise plan…&rdquo; to ask
          the agent to regenerate it. Approval stays blocked until a plan renders here.
        </div>
      )}
      {e.text && (
        <div className={`devc-plan-body${long && !expanded ? ' collapsed' : ''}`}>
          <ReactMarkdown remarkPlugins={[remarkGfm]} components={mdComponents}>{e.text}</ReactMarkdown>
        </div>
      )}
      {e.text && long && (
        <button className="devc-filter" onClick={() => setExpanded(!expanded)}>
          {expanded ? 'Collapse plan' : `Show full plan (${Math.round(e.text.length / 1000)}k chars)`}
        </button>
      )}
      {pending && execLabel && (
        <div className="devc-plan-model">
          Executes with <strong>{execLabel}</strong>
          {execInvalid
            ? <span className="devc-chip red">not in the current model catalog</span>
            : <span className="devc-dim">— change it in the approval bar below</span>}
        </div>
      )}
    </div>
  );
}

// Resolve a model string against the live catalog the way the backend does:
// canonical id, any alias, or display name (case/whitespace-insensitive).
// Returns the catalog entry or null when the catalog positively doesn't know
// the model.
const normModelKey = (s) => String(s || '').trim().toLowerCase().replace(/\s+/g, ' ');
function findCatalogModel(models, value) {
  const want = normModelKey(value);
  if (!want) return null;
  return (models || []).find((m) => normModelKey(m.id) === want
    || (m.aliases || []).some((a) => normModelKey(a) === want)
    || normModelKey(m.name) === want) || null;
}

// ---- activity timeline -----------------------------------------------------------
const EVENT_META = {
  user: ['💬', 'You'],
  assistant: ['🤖', 'Agent'],
  thinking: ['🧠', 'Thinking'],
  plan: ['📋', 'Plan'],
  questions: ['❓', 'Questions'],
  tool: ['⚙️', 'Tool'],
  status: ['ℹ️', 'Status'],
  usage: ['🎟️', 'Tokens'],
  result: ['🏁', 'Result'],
};

function ActivityRow({ e }) {
  const [icon, label] = EVENT_META[e.type] || ['•', e.type];
  let body = null;
  if (e.type === 'tool') {
    body = (
      <>
        <span className="devc-act-name">{e.name}</span>
        <span className={`devc-chip ${e.status === 'running' ? 'blue' : e.status === 'error' ? 'red' : 'gray'}`}>{e.status}</span>
        {e.args && <code className="devc-act-args">{e.args}</code>}
        {e.result && <pre className="devc-act-result">{e.result}</pre>}
      </>
    );
  } else if (e.type === 'usage') {
    body = <span className="devc-dim">{fmtTok(e.inputTokens)} in · {fmtTok(e.outputTokens)} out this turn</span>;
  } else if (e.type === 'questions') {
    body = <span className="devc-act-text">{(e.questions || []).map((q) => q.question).join(' · ').slice(0, 400)}</span>;
  } else if (e.type === 'result') {
    body = (
      <>
        <Chip status={e.status} />
        {e.error && <div className="devc-err">{e.error}</div>}
        {e.prUrl && <a className="devc-link" href={e.prUrl} target="_blank" rel="noopener noreferrer">{e.prUrl}</a>}
      </>
    );
  } else {
    body = <span className="devc-act-text">{(e.text || e.message || '').slice(0, 400)}</span>;
  }
  return (
    <div className={`devc-act ${e.type}`}>
      <div className="devc-act-icon">{icon}</div>
      <div className="devc-act-body">
        <div className="devc-act-head">
          <strong>{label}</strong>
          <span className="devc-dim">{e.ts ? new Date(e.ts).toLocaleTimeString() : ''}</span>
        </div>
        {body}
      </div>
    </div>
  );
}

// ---- inline PR diff --------------------------------------------------------------
// Per-file patches from GET /dev/sessions/:id/diff — review the PR without a
// GitHub round trip. First files start expanded.
function parsePatch(patch) {
  const rows = [];
  let oldN = 0;
  let newN = 0;
  for (const line of String(patch || '').replace(/\n$/, '').split('\n')) {
    const hunk = /^@@ -(\d+)(?:,\d+)? \+(\d+)(?:,\d+)? @@/.exec(line);
    if (hunk) {
      oldN = Number(hunk[1]);
      newN = Number(hunk[2]);
      rows.push({ kind: 'hunk', text: line });
    } else if (line.startsWith('diff ') || line.startsWith('index ') || line.startsWith('---') || line.startsWith('+++') || line.startsWith('\\')) {
      rows.push({ kind: 'meta', text: line });
    } else if (line.startsWith('+')) {
      rows.push({ kind: 'add', old: '', neu: String(newN), text: line.slice(1) });
      newN += 1;
    } else if (line.startsWith('-')) {
      rows.push({ kind: 'del', old: String(oldN), neu: '', text: line.slice(1) });
      oldN += 1;
    } else {
      const text = line.startsWith(' ') ? line.slice(1) : line;
      rows.push({ kind: 'ctx', old: String(oldN), neu: String(newN), text });
      oldN += 1;
      newN += 1;
    }
  }
  return rows;
}

function DiffViewer({ sessionId, prUrl, summaryFirst = false }) {
  const [diff, setDiff] = useState(null);
  const [err, setErr] = useState(null);
  const [open, setOpen] = useState({});
  const [attempt, setAttempt] = useState(0);
  // "Summary first" (book band / phone): the plain-language PR summary the
  // voice shares (GET /dev/sessions/:id/pr-summary, cached on the session)
  // above the files, every file collapsed until tapped.
  const [summary, setSummary] = useState(null);
  const [summaryErr, setSummaryErr] = useState(null);
  const [wantSummary, setWantSummary] = useState(summaryFirst);
  const [patches, setPatches] = useState(false);
  useEffect(() => {
    if (!prUrl) return undefined;
    let alive = true;
    setErr(null);
    setDiff(null);
    // Names first. Patches are GetBlob per file and share the account
    // limit with the merge, so they load only when asked.
    api(`/dev/sessions/${sessionId}/diff${patches ? '' : '?names=1'}`)
      .then((d) => {
        if (!alive) return;
        setDiff(d);
        const first = {};
        if (patches && !summaryFirst) (d.files || []).slice(0, 3).forEach((f) => { first[f.filename] = true; });
        setOpen(first);
      })
      .catch((e) => { if (alive) setErr(e.message); });
    return () => { alive = false; };
  }, [sessionId, prUrl, attempt, summaryFirst, patches]);
  useEffect(() => {
    if (!prUrl || !wantSummary) return undefined;
    let alive = true;
    setSummary(null); setSummaryErr(null);
    api(`/dev/sessions/${sessionId}/pr-summary`)
      .then((d) => { if (alive) setSummary(d); })
      .catch((e) => { if (alive) setSummaryErr(e.message); });
    return () => { alive = false; };
  }, [sessionId, prUrl, wantSummary]);

  if (!prUrl) return null;
  if (err) {
    return (
      <div className="devc-err" role="alert" style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <span>Diff unavailable: {err}</span>
        <button type="button" className="devc-filter" onClick={() => setAttempt((n) => n + 1)}>Retry</button>
      </div>
    );
  }
  if (!diff) return <div className="devc-dim" style={{ padding: '8px 2px' }}>Loading diff…</div>;
  return (
    <div className="devc-card">
      <div className="devc-card-title">
        Changes in this PR
        <span className="devc-chip gray">{diff.files.length} file{diff.files.length === 1 ? '' : 's'}</span>
        {!diff.namesOnly && <span className="devc-chip green">+{diff.files.reduce((n, f) => n + (f.additions || 0), 0)}</span>}
        {!diff.namesOnly && <span className="devc-chip red">−{diff.files.reduce((n, f) => n + (f.deletions || 0), 0)}</span>}
        {diff.namesOnly && <button type="button" className="devc-filter" onClick={() => setPatches(true)}>Load patches</button>}
        {!wantSummary && <button type="button" className="devc-filter" style={{ marginLeft: 'auto' }} onClick={() => setWantSummary(true)}>Summary</button>}
      </div>
      {wantSummary && (
        <div className="devc-pr-summary">
          {summary ? <p>{summary.text}</p> : summaryErr ? <span className="devc-dim">Summary unavailable: {summaryErr}</span> : <span className="devc-dim">Summarizing the change…</span>}
        </div>
      )}
      {diff.files.map((f) => (
        <div key={f.filename} className="devc-diff-file">
          <button className="devc-diff-head" onClick={() => setOpen({ ...open, [f.filename]: !open[f.filename] })}>
            <span className="devc-diff-name">{open[f.filename] ? '▾' : '▸'} {f.filename}</span>
            <span className="devc-dim">{f.status}{!diff.namesOnly && f.additions != null ? ` · +${f.additions} −${f.deletions}` : ''}</span>
          </button>
          {open[f.filename] && (
            <pre className="devc-diff-body">
              {(f.patch ? parsePatch(f.patch) : [{ kind: 'meta', text: diff.namesOnly ? 'Load patches to read this file.' : '(no textual diff — binary or too large)' }]).map((row, i) => (
                <span
                  // eslint-disable-next-line react/no-array-index-key
                  key={i}
                  className={row.kind}
                >
                  {row.kind === 'hunk' || row.kind === 'meta' ? row.text : (
                    <>
                      <i>{row.old}</i>
                      <i>{row.neu}</i>
                      <b>{row.kind === 'add' ? '+' : row.kind === 'del' ? '−' : ' '}</b>
                      {row.text}
                    </>
                  )}
                </span>
              ))}
            </pre>
          )}
        </div>
      ))}
    </div>
  );
}

// ---- attachments ----------------------------------------------------------------
// Any file type, any count within live-config limits (dev.attachments via
// /dev/status): screenshots and documents stay first-class, whole folders
// become a data package (frontend/src/dev/attachments.js). The hint below
// is only what the picker suggests; nothing is rejected by extension.
const DOC_EXT_LIST = '.txt,.md,.markdown,.csv,.tsv,.json,.log,.yaml,.yml,.xml,.html,.pdf,.docx';

// ---- structured prompt templates ------------------------------------------------
// Each chip collects named fields and compiles them into a specific prompt —
// a well-formed change request instead of a bare starter sentence.
const TEMPLATES = [
  {
    id: 'bug',
    label: 'Bug fix',
    icon: '🐛',
    desc: 'Something broken — what happened vs. what you expected',
    fields: [
      { key: 'what_happened', label: 'What happened', ph: 'clicking Save shows a blank screen' },
      { key: 'expected', label: 'Expected behavior', ph: 'the claim saves and returns to the list' },
      { key: 'where_seen', label: 'Where it appears', ph: '/items, signed-in app' },
    ],
    template: 'Fix this bug. What happened: {what_happened}. Expected behavior: {expected}. Where it appears: {where_seen}. If a screenshot is attached, use it as the source of truth for the visual state.',
  },
  {
    id: 'ui',
    label: 'UI tweak',
    icon: '🎨',
    desc: 'Change how an element looks or behaves',
    fields: [
      { key: 'element', label: 'Element', ph: 'the dashboard KPI cards' },
      { key: 'current', label: 'Current look/behavior', ph: 'four cards in one row, cramped on laptop' },
      { key: 'desired', label: 'Desired', ph: 'two rows of two below 1200px' },
    ],
    template: 'UI change. Element: {element}. Current: {current}. Desired: {desired}. If a screenshot is attached, use it as the source of truth for the visual state.',
  },
  {
    id: 'copy',
    label: 'Copy change',
    icon: '✏️',
    desc: 'Reword text anywhere in the product',
    fields: [
      { key: 'location', label: 'Where', ph: 'home page welcome banner' },
      { key: 'current_copy', label: 'Current text', ph: 'Welcome back' },
      { key: 'new_copy', label: 'New text', ph: 'Good to see you' },
    ],
    template: 'Copy change. Location: {location}. Current text: "{current_copy}". New text: "{new_copy}". Change only the wording, not layout or logic.',
  },
  {
    id: 'feature',
    label: 'New feature',
    icon: '✨',
    desc: 'A user story with acceptance criteria',
    fields: [
      { key: 'user_story', label: 'User story', ph: 'as an admin I want to export an item summary' },
      { key: 'acceptance_criteria', label: 'Acceptance criteria', ph: 'button on the item page, PDF with details + asset list' },
      { key: 'constraints', label: 'Constraints', ph: 'no new backend dependencies' },
    ],
    template: 'Add a feature. User story: {user_story}. Acceptance criteria: {acceptance_criteria}. Constraints: {constraints}.',
  },
  {
    id: 'explain',
    label: 'Explain',
    icon: '❓',
    desc: 'Ask how something works — no code changes',
    fields: [
      { key: 'question', label: 'Question', ph: 'how does the evidence upload path work?' },
    ],
    template: 'Do not change any files. Explain, with file references: {question}',
  },
];

const compileTemplate = (tpl, values) =>
  tpl.template.replace(/\{(\w+)\}/g, (_, k) => (values[k] || '').trim() || `(${k.replace(/_/g, ' ')}: not specified)`);

// Prompt sent when a plan is approved — executes in agent mode (Cursor's
// native Plan mode produced the plan; this follow-up flips to implementation).
// ≤699px = the phone posture (DevConsole.css); the book band (700–900) and
// desktop keep the two-pane console.
const PHONE_MQ = '(max-width: 699px)';
const NARROW_MQ = '(max-width: 900px)';
function useMedia(query) {
  const [on, setOn] = useState(() => typeof window !== 'undefined' && !!window.matchMedia?.(query).matches);
  useEffect(() => {
    const mq = window.matchMedia?.(query);
    if (!mq) return undefined;
    const fn = (e) => setOn(e.matches);
    mq.addEventListener('change', fn);
    return () => mq.removeEventListener('change', fn);
  }, [query]);
  return on;
}
const usePhone = () => useMedia(PHONE_MQ);
const useNarrow = () => useMedia(NARROW_MQ);

const EXECUTE_PROMPT = 'Approved. Execute the plan you produced in this session exactly as written. '
  + 'Push a branch and open a pull request when complete.';

// One failure card for sessions, deploy jobs and request errors — renders
// the error contract (backend shared/devOps/devErrors.js): the plain
// headline (`errorUser` / `error`), the action the code calls for as a
// button, the raw text only behind a disclosure, and the request id when
// support will need it. `failure` is a session doc, a job doc, or the
// Error thrown by api(). Older docs (no errorUser) fall back to a hint
// derived from the raw text so nothing renders blank.
const ACTION_LABEL = { retry: '↻ Retry', wait: '↻ Retry in a minute', 'fix-config': 'Open status', followup: 'Send a follow-up', contact: 'Copy details for an operator' };
function failureShape(f) {
  if (!f) return null;
  if (typeof f === 'string') return { user: f, raw: '', code: null, action: 'retry', requestId: null };
  const raw = f.errorMessage || f.message || f.detail || (f instanceof Error ? f.message : '') || '';
  const user = f.errorUser || (f instanceof Error ? f.message : '') || legacyHint(raw, f.errorCode) || raw;
  return {
    user, raw: raw && raw !== user ? raw : '',
    code: f.errorCode || f.code || null,
    action: f.errorAction || f.action || (f.retryable === false ? 'contact' : 'retry'),
    requestId: f.requestId || null,
  };
}
function legacyHint(raw, code) {
  const m = `${code || ''} ${raw || ''}`.toLowerCase();
  if (!m.trim()) return null;
  if (/(^|\D)401(\D|$)|unauthenticated|invalid api key|key rejected/.test(m)) return 'API key rejected — check the key indicator on the status page and rotate the secret.';
  if (/(^|\D)403(\D|$)|forbidden|not authorized/.test(m)) return 'The configured credential does not have access to this repository.';
  if (/(^|\D)404(\D|$)|agent.*not found|deleted or expired/.test(m)) return 'This agent was deleted or expired on the provider side — Retry starts a fresh session.';
  if (/usage_limit_exceeded|usage limit|on-demand usage|spend limit|contact (your )?admin or wait until/.test(m)) {
    return 'Cursor refused this run for a usage or spend limit. Set config/app `dev.agentRuntime` to `runner`, or ask a Cursor team admin to raise the spend limit, or wait until the reset date in the details.';
  }
  if (/(^|\D)429(\D|$)|rate limit|rate exceeded/.test(m)) return 'Rate limited. Wait a minute, then retry.';
  if (/(^|\D)5\d\d(\D|$)|internal error|service unavailable|overloaded/.test(m)) return 'Provider-side error. Retry usually resolves this.';
  return null;
}
function FailureCard({ failure, title = null, onRetry = null, onFollowup = null, onStatus = null, style = null }) {
  const [open, setOpen] = useState(false);
  const f = failureShape(failure);
  if (!f || !f.user) return null;
  const handler = { retry: onRetry, wait: onRetry, 'fix-config': onStatus, followup: onFollowup, contact: null }[f.action];
  const copyAll = () => navigator.clipboard?.writeText([title, f.user, f.code && `code: ${f.code}`, f.requestId && `request id: ${f.requestId}`, f.raw].filter(Boolean).join('\n')).catch(() => {});
  return (
    <div className="devc-err devc-failure" role="alert" style={style || undefined} data-code={f.code || undefined}>
      <div className="devc-failure-head">{title ? `${title}: ` : ''}{f.user}</div>
      <div className="devc-failure-actions">
        {handler && <button className="devc-btn" onClick={handler}>{ACTION_LABEL[f.action]}</button>}
        {f.action === 'contact' && <button className="devc-btn" onClick={copyAll}>{ACTION_LABEL.contact}</button>}
        {f.raw && <button className="devc-filter" onClick={() => setOpen((v) => !v)}>{open ? 'hide details' : 'details'}</button>}
        {f.code && <span className="devc-dim devc-failure-code">{f.code}</span>}
      </div>
      {open && f.raw && <pre className="devc-failure-raw">{f.raw}</pre>}
      {f.requestId && (
        <div className="devc-reqid">
          request id <code>{f.requestId}</code>
          <button className="devc-filter" title="Copy — support asks for this id" onClick={() => navigator.clipboard?.writeText(f.requestId).catch(() => {})}>copy</button>
        </div>
      )}
    </div>
  );
}

// ---- console settings (localStorage) ---------------------------------------------
const SETTINGS_KEY = 'devc-settings';
const loadSettings = () => {
  try { return { notify: false, autoArchive: false, ...JSON.parse(localStorage.getItem(SETTINGS_KEY) || '{}') }; }
  catch { return { notify: false, autoArchive: false }; }
};

// ---- artifacts tab -----------------------------------------------------------------
const isImagePath = (p) => /\.(png|jpe?g|gif|webp)$/i.test(p || '');
const fmtBytes = (n) => {
  if (n == null) return '—';
  if (n >= 1e6) return `${(n / 1e6).toFixed(1)} MB`;
  if (n >= 1e3) return `${(n / 1e3).toFixed(1)} kB`;
  return `${n} B`;
};

function ArtifactsPanel({ sessionId, running }) {
  const [items, setItems] = useState(null);
  const [err, setErr] = useState(null);
  const [previews, setPreviews] = useState({}); // path -> presigned url

  useEffect(() => {
    let alive = true;
    setItems(null); setErr(null); setPreviews({});
    api(`/dev/sessions/${sessionId}/artifacts`)
      .then((d) => { if (alive) setItems(d.items || []); })
      .catch((e) => { if (alive) setErr(e.message); });
    return () => { alive = false; };
  }, [sessionId]);

  // Links expire in 15 minutes — always fetch fresh at click time.
  const openArtifact = async (path) => {
    try {
      const { url } = await api(`/dev/sessions/${sessionId}/artifacts/link?path=${encodeURIComponent(path)}`);
      window.open(url, '_blank', 'noopener');
    } catch (e) { setErr(e.message); }
  };
  const togglePreview = async (path) => {
    if (previews[path]) { setPreviews((p) => ({ ...p, [path]: null })); return; }
    try {
      const { url } = await api(`/dev/sessions/${sessionId}/artifacts/link?path=${encodeURIComponent(path)}`);
      setPreviews((p) => ({ ...p, [path]: url }));
    } catch (e) { setErr(e.message); }
  };

  if (err) return <div className="devc-err">Artifacts unavailable: {err}</div>;
  if (items === null) return <div className="devc-dim" style={{ padding: 8 }}>Loading artifacts…</div>;
  if (!items.length) {
    return (
      <div className="devc-empty">
        <h3>No artifacts yet</h3>
        <p>
          Images, PDFs and recordings the agent produces, plus anything it saves under an
          <code>artifacts/</code> folder, appear here after the turn finishes. Ask for one in a follow-up:
          &ldquo;attach a screenshot of the change&rdquo; or &ldquo;save the sample images under artifacts/&rdquo;.
          {running ? ' The agent is still working — check back when it finishes.' : ''}
        </p>
      </div>
    );
  }
  return (
    <div className="devc-card">
      <div className="devc-card-title">Artifacts <span className="devc-chip gray">{items.length}</span></div>
      {items.map((a) => (
        <div key={a.path} className="devc-artifact">
          <div className="devc-artifact-row">
            <span className="devc-artifact-ic">{isImagePath(a.path) ? '🖼' : '📄'}</span>
            <span className="devc-artifact-name">{a.path.replace(/^artifacts\//, '')}</span>
            <span className="devc-dim">{fmtBytes(a.sizeBytes)}{a.updatedAt ? ` · ${fmtAgo(new Date(a.updatedAt).getTime())}` : ''}</span>
            {isImagePath(a.path) && (
              <button className="devc-filter" onClick={() => togglePreview(a.path)}>
                {previews[a.path] ? 'Hide' : 'Preview'}
              </button>
            )}
            <button className="devc-filter" onClick={() => openArtifact(a.path)}>Download ↗</button>
          </div>
          {previews[a.path] && <img className="devc-artifact-img" src={previews[a.path]} alt={a.path} />}
        </div>
      ))}
      <div className="devc-dim" style={{ marginTop: 8 }}>Download links are temporary (15 minutes) — a fresh one is minted per click.</div>
    </div>
  );
}

// ---- session feedback (thumbs) ----------------------------------------------------
// The quality signal behind smart routing: up = the tier's work was accepted,
// down = it failed (the next follow-up steps up a tier). Clicking the active
// thumb clears it.
function SessionFeedback({ sessionId, feedback }) {
  const [busy, setBusy] = useState(false);
  const current = feedback?.rating || null;
  const send = async (rating) => {
    if (busy || !sessionId) return;
    setBusy(true);
    try { await api(`/dev/sessions/${sessionId}/feedback`, { rating: current === rating ? null : rating }); } catch { /* the session doc is the truth */ } finally { setBusy(false); }
  };
  return (
    <div className="devc-feedback" role="group" aria-label="Rate this result">
      <span className="devc-dim">Was this result right?</span>
      <button type="button" className={`devc-thumb${current === 'up' ? ' is-on' : ''}`} aria-pressed={current === 'up'} aria-label="Thumbs up" disabled={busy} onClick={() => send('up')}>👍</button>
      <button type="button" className={`devc-thumb${current === 'down' ? ' is-on' : ''}`} aria-pressed={current === 'down'} aria-label="Thumbs down" disabled={busy} onClick={() => send('down')}>👎</button>
    </div>
  );
}

// ---- project references ---------------------------------------------------------------
// Files pinned once for a repo (📌 on an attachment before sending); the runner
// hands them to every later session, so a prototype or spec is attached once,
// not per story. Unpin here.
function ReferencesStrip({ repoId, stamp }) {
  const [refs, setRefs] = useState([]);
  const [busy, setBusy] = useState('');
  useEffect(() => {
    let live = true;
    api(`/dev/references?repo=${encodeURIComponent(repoId || 'default')}`)
      .then((r) => { if (live) setRefs(Array.isArray(r.references) ? r.references : []); })
      .catch(() => { if (live) setRefs([]); });
    return () => { live = false; };
  }, [repoId, stamp]);
  if (!refs.length) return null;
  const unpin = async (key) => {
    setBusy(key);
    try {
      const r = await api('/dev/references/unpin', { repoId: repoId || 'default', key });
      setRefs(Array.isArray(r.references) ? r.references : []);
    } catch { /* the next fetch is the truth */ } finally { setBusy(''); }
  };
  return (
    <div className="devc-refs" role="group" aria-label="Project references">
      <span className="devc-dim" title="Pinned files every session on this repo receives">📌 Project references</span>
      {refs.map((r) => (
        <span key={r.key} className="devc-filechip is-pinned" title={`${r.name}${r.by ? ` — pinned by ${r.by}` : ''}`}>
          {r.kind === 'image' ? <span aria-hidden="true">🖼 </span> : <span aria-hidden="true">📄 </span>}
          {r.name}
          <button type="button" onClick={() => unpin(r.key)} disabled={busy === r.key} aria-label={`Stop keeping ${r.name} for this project`}>×</button>
        </span>
      ))}
    </div>
  );
}

// ---- per-run usage tab --------------------------------------------------------------
function UsagePanel({ sessionId }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  useEffect(() => {
    let alive = true;
    setData(null); setErr(null);
    api(`/dev/sessions/${sessionId}/usage`)
      .then((d) => { if (alive) setData(d); })
      .catch((e) => { if (alive) setErr(e.message); });
    return () => { alive = false; };
  }, [sessionId]);

  const costCell = (c) => (c == null || c.chargedCents == null ? '—' : `$${(c.chargedCents / 100).toFixed(3)}`);
  const costReportPath = '/dev/costs';

  if (err) return <div className="devc-err">Usage unavailable: {err}</div>;
  if (!data) return <div className="devc-dim" style={{ padding: 8 }}>Loading usage…</div>;
  return (
    <div className="devc-card">
      <div className="devc-card-title">Token usage by run <span className="devc-chip gray">{data.runs.length} run{data.runs.length === 1 ? '' : 's'}</span></div>
      <table className="devc-usage-table">
        <thead>
          <tr><th>Run</th><th>Input</th><th>Output</th><th>Cache read</th><th>Total</th><th>Cost</th></tr>
        </thead>
        <tbody>
          {data.runs.map((r, i) => (
            <tr key={r.runId}>
              <td title={r.runId}>#{i + 1}</td>
              <td>{fmtTok(r.usage?.inputTokens)}</td>
              <td>{fmtTok(r.usage?.outputTokens)}</td>
              <td>{fmtTok(r.usage?.cacheReadTokens)}</td>
              <td>{fmtTok(r.usage?.totalTokens)}</td>
              <td>{costCell(r.cost)}</td>
            </tr>
          ))}
          <tr className="devc-usage-total">
            <td>Total</td>
            <td>{fmtTok(data.usage?.inputTokens)}</td>
            <td>{fmtTok(data.usage?.outputTokens)}</td>
            <td>{fmtTok(data.usage?.cacheReadTokens)}</td>
            <td>{fmtTok(data.usage?.totalTokens)}</td>
            <td>{costCell(data.cost)}</td>
          </tr>
        </tbody>
      </table>
      <div className="devc-dim" style={{ marginTop: 8 }}>
        {data.source
          ? <>Runner session — {data.source === 'reported' ? 'tokens and cost as reported by the agent CLI' : data.source === 'estimated' ? 'tokens estimated from the bytes exchanged, priced by dev.router.priceUsdPerMtok' : 'a mix of CLI-reported and estimated turns'}.</>
          : <>Cost is Cursor-billed dollars (eventually consistent — recent runs can show &ldquo;—&rdquo; briefly).</>}{' '}
        <a className="devc-link" href={costReportPath}>Team-wide spend: Cost Report →</a>
      </div>
    </div>
  );
}

// ---- workspace views: Config / Access / Ops ---------------------------------------
// Session-independent operator surfaces (sidebar switcher). Read access rides
// the internal claim like the rest of the console; mutations need the admin
// app role (the backend enforces it — the UI just explains the read-only state).

const deepMergeCfg = (base, over) => {
  if (!over || typeof over !== 'object') return base;
  const out = { ...base };
  for (const [k, v] of Object.entries(over)) {
    out[k] = v && typeof v === 'object' && !Array.isArray(v) && base?.[k] && typeof base[k] === 'object'
      ? deepMergeCfg(base[k], v)
      : v;
  }
  return out;
};

// Keep only values that differ from the defaults (so the saved doc stays a
// minimal override set, like it was when hand-edited in the console).
const pruneCfg = (merged, defaults) => {
  const out = {};
  for (const [k, v] of Object.entries(merged || {})) {
    const d = defaults?.[k];
    if (v && typeof v === 'object' && !Array.isArray(v)) {
      const sub = pruneCfg(v, d || {});
      if (Object.keys(sub).length) out[k] = sub;
    } else if (JSON.stringify(v) !== JSON.stringify(d)) {
      out[k] = v;
    }
  }
  return out;
};

// ---- Routing view ---------------------------------------------------------------
// Smart model routing (backend shared/devRouter.js + shared/devModes.js):
// the engine, the tier → model map, the frontier cap, the conversation
// profiles (each a routing policy a seat may pick / edit) and this month's
// ledger. Router fields save as a whole for admins (PUT /dev/router); a
// profile saves on its own (PUT /dev/router/modes/:id) for whoever its
// editRoles names — the server is the gate, the UI only hides the buttons.
const TIER_IDS = ['bulk', 'strong', 'frontier'];
const ENGINES = [['rules', 'Rules — deterministic heuristics, free'], ['jev', 'Jev — TypeSafe decision model, falls back to rules'], ['shadow', 'Shadow — rules decide, Jev is scored beside them'], ['pipeline', 'Pipeline — an nQube router pipeline decides, rules fall back'], ['shadow-pipeline', 'Shadow pipeline — rules decide, the pipeline is scored beside them']];
const usd4 = (v) => (v == null ? '—' : `$${Number(v).toFixed(v < 1 ? 4 : 2)}`);

function RoutingPanel() {
  const [data, setData] = useState(null);
  const [router, setRouter] = useState(null);
  const [editing, setEditing] = useState(null); // mode id being edited
  const [draft, setDraft] = useState(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);

  const load = async () => {
    try {
      const d = await api('/dev/router');
      setData(d);
      const tiers = d.router.tiers || d.defaults.tiers || {};
      setRouter({ ...d.defaults, ...Object.fromEntries(Object.entries(d.router).filter(([, v]) => v != null)), tiers: Object.fromEntries(TIER_IDS.map((t) => [t, typeof tiers[t] === 'string' ? { cloud: tiers[t], runner: '' } : { cloud: tiers[t]?.cloud || '', runner: tiers[t]?.runner || '' }])) });
      setMsg(null);
    } catch (e) { setMsg({ ok: false, text: e.message }); }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const saveRouter = async () => {
    setBusy(true); setMsg(null);
    try {
      const patch = {
        engine: router.engine, frontierSharePct: Number(router.frontierSharePct), jevModel: router.jevModel, jevMinConfidence: Number(router.jevMinConfidence), jevTimeoutMs: Number(router.jevTimeoutMs),
        stickyFollowups: router.stickyFollowups !== false, escalateOnFailure: router.escalateOnFailure !== false, defaultMode: router.defaultMode || 'balanced',
        tiers: Object.fromEntries(TIER_IDS.map((t) => [t, { cloud: router.tiers[t].cloud.trim(), runner: router.tiers[t].runner.trim() }])),
        ...(router.pipelineId ? { pipelineId: router.pipelineId } : {}),
      };
      await api('/dev/router', { router: patch }, 'PUT');
      setMsg({ ok: true, text: 'Router saved — backends pick it up within ~1 min.' });
      await load();
    } catch (e) { setMsg({ ok: false, text: e.message }); } finally { setBusy(false); }
  };
  const saveMode = async () => {
    setBusy(true); setMsg(null);
    try {
      const { id, canPick, canEdit, ...fields } = draft;
      await api(`/dev/router/modes/${id}`, { mode: { ...fields, frontierSharePct: fields.frontierSharePct === '' || fields.frontierSharePct == null ? null : Number(fields.frontierSharePct), pipelineId: fields.pipelineId || null } }, 'PUT');
      setMsg({ ok: true, text: `Profile “${draft.label}” saved.` });
      setEditing(null); setDraft(null);
      await load();
    } catch (e) { setMsg({ ok: false, text: e.message }); } finally { setBusy(false); }
  };
  const resetModes = async () => {
    if (!window.confirm('Drop every saved profile and return to the seeded set?')) return;
    setBusy(true);
    try { await api('/dev/router/modes', {}, 'DELETE'); setMsg({ ok: true, text: 'Profiles reset to the seeded set.' }); await load(); } catch (e) { setMsg({ ok: false, text: e.message }); } finally { setBusy(false); }
  };

  if (!data || !router) return <div className="devc-panel">{msg ? <div className="devc-err">{msg.text}</div> : <span className="devc-dim">Loading routing…</span>}</div>;
  const L = data.ledger;
  const canEditRouter = data.canEditRouter;
  const field = (k, label, type = 'text', extra = {}) => (
    <label className="devc-field"><span>{label}</span><input type={type} value={router[k] ?? ''} disabled={!canEditRouter} onChange={(e) => setRouter({ ...router, [k]: e.target.value })} {...extra} /></label>
  );

  return (
    <div className="devc-panel">
      <datalist id="devc-router-pipelines">{(data?.routers || []).map((r) => <option key={r.id} value={r.id}>{r.name}{r.status !== 'ready' ? ' (draft)' : ''}</option>)}</datalist>
      <div className="devc-card">
        <div className="devc-card-title">Smart routing <span className="devc-dim" style={{ fontWeight: 400 }}>· each request is routed to a tier; the tier maps to a model per runtime</span></div>
        <div className="devc-fields">
          <label className="devc-field"><span>Engine</span>
            <select value={router.engine || 'rules'} disabled={!canEditRouter} onChange={(e) => setRouter({ ...router, engine: e.target.value })}>
              {ENGINES.map(([id, lbl]) => <option key={id} value={id}>{lbl}</option>)}
            </select>
          </label>
          {field('frontierSharePct', 'Frontier cap (% of sessions / month)', 'number', { min: 0, max: 100 })}
          <label className="devc-field"><span>Default profile</span>
            <select value={router.defaultMode || 'balanced'} disabled={!canEditRouter} onChange={(e) => setRouter({ ...router, defaultMode: e.target.value })}>
              {data.modes.map((m) => <option key={m.id} value={m.id}>{m.label}</option>)}
            </select>
          </label>
        </div>
        <div className="devc-fields">
          {field('jevModel', 'Jev model')}
          {field('jevMinConfidence', 'Jev min confidence', 'number', { step: 0.05, min: 0, max: 1 })}
          {field('jevTimeoutMs', 'Jev timeout (ms)', 'number', { min: 200 })}
          {(router.engine === 'pipeline' || router.engine === 'shadow-pipeline') && (
            <label className="devc-field"><span>Router pipeline</span>
              <input list="devc-router-pipelines" value={router.pipelineId ?? ''} disabled={!canEditRouter} onChange={(e) => setRouter({ ...router, pipelineId: e.target.value })} placeholder="router-rules" />
            </label>
          )}
          <label className="devc-field"><span>Follow-ups</span>
            <select value={router.stickyFollowups === false ? 'fresh' : 'sticky'} disabled={!canEditRouter} onChange={(e) => setRouter({ ...router, stickyFollowups: e.target.value === 'sticky' })}>
              <option value="sticky">stay on the session's tier</option><option value="fresh">re-route every turn</option>
            </select>
          </label>
          <label className="devc-field"><span>On failure</span>
            <select value={router.escalateOnFailure === false ? 'no' : 'yes'} disabled={!canEditRouter} onChange={(e) => setRouter({ ...router, escalateOnFailure: e.target.value === 'yes' })}>
              <option value="yes">escalate one tier</option><option value="no">keep the tier</option>
            </select>
          </label>
        </div>
        <table className="devc-access-table" style={{ marginTop: 6 }}>
          <thead><tr><th>Tier</th><th>Cloud / pool model</th><th>Runner model (blank = cloud id)</th>{L && <><th>Sessions</th><th>Spend</th><th>$/session</th><th>Accepted / failed</th></>}</tr></thead>
          <tbody>
            {TIER_IDS.map((t) => (
              <tr key={t}>
                <td><strong>{t}</strong></td>
                <td><input className="devc-inline" value={router.tiers[t].cloud} disabled={!canEditRouter} onChange={(e) => setRouter({ ...router, tiers: { ...router.tiers, [t]: { ...router.tiers[t], cloud: e.target.value } } })} style={{ width: '100%' }} /></td>
                <td><input className="devc-inline" value={router.tiers[t].runner} disabled={!canEditRouter} placeholder={router.tiers[t].cloud} onChange={(e) => setRouter({ ...router, tiers: { ...router.tiers, [t]: { ...router.tiers[t], runner: e.target.value } } })} style={{ width: '100%' }} /></td>
                {L && <><td>{L.tiers[t].sessions} <span className="devc-dim">({L.tiers[t].sessionSharePct}%)</span></td><td>{usd4(L.tiers[t].usd)} <span className="devc-dim">({L.tiers[t].usdSharePct}%)</span></td><td>{usd4(L.tiers[t].usdPerSession)}</td><td>{L.tiers[t].accepted} / {L.tiers[t].failed}</td></>}
              </tr>
            ))}
          </tbody>
        </table>
        {L && (
          <p className="devc-dim" style={{ marginTop: 8 }}>
            This month: {L.sessions} routed sessions · {usd4(L.usd)} · engines {Object.entries(L.engines).map(([k, v]) => `${k} ${v}`).join(', ') || '—'} · escalations {Object.values(L.escalations).reduce((a, b) => a + b, 0)} · capped {L.capped}{L.pipelineAgree + L.pipelineDisagree > 0 ? ` · policy agreed ${L.pipelineAgree}/${L.pipelineAgree + L.pipelineDisagree}` : ''}{L.pipelineErrors ? ` · policy errors ${L.pipelineErrors}` : ''}
            {L.jev.agreementPct != null && <> · Jev agrees {L.jev.agreementPct}% ({L.jev.agree}/{L.jev.agree + L.jev.disagree}, {L.jev.errors} errors, {usd4(L.jev.decisionUsd)} on decisions)</>}
          </p>
        )}
        {canEditRouter && <div style={{ display: 'flex', gap: 8, marginTop: 10 }}><button type="button" className="devc-btn primary" disabled={busy} onClick={saveRouter}>{busy ? 'Saving…' : 'Save router'}</button><button type="button" className="devc-btn" disabled={busy} onClick={load}>Discard</button></div>}
        {!canEditRouter && <p className="devc-dim">Admins edit the router; you can edit the profiles whose “edit” seat you hold.</p>}
      </div>

      <div className="devc-card">
        <div className="devc-card-title">Conversation profiles <span className="devc-dim" style={{ fontWeight: 400 }}>· {data.seeded ? 'seeded set' : 'customized'} · the “Profile” picker in the composer</span>
          {!data.seeded && canEditRouter && <button type="button" className="devc-filter" style={{ marginLeft: 'auto' }} disabled={busy} onClick={resetModes}>Reset to seeded</button>}
        </div>
        <table className="devc-access-table">
          <thead><tr><th>Profile</th><th>Conversation</th><th>Tiers</th><th>Plan bias</th><th>Engine</th><th>Cap</th><th>Who picks / edits</th>{L && <th>Sessions</th>}<th /></tr></thead>
          <tbody>
            {data.modes.map((m) => (
              <tr key={m.id} className={editing && editing !== m.id ? 'devc-row-busy' : ''}>
                <td><strong>{m.label}</strong><div className="devc-dim">{m.description}</div>{m.cue && <div className="devc-dim" style={{ fontStyle: 'italic' }}>“{m.cue}”</div>}</td>
                <td>{m.conversation}</td>
                <td>{m.tierMin === m.tierMax ? m.tierMin : `${m.tierMin} – ${m.tierMax}`}</td>
                <td>{m.planFirstBias || '—'}</td>
                <td>{m.engine || <span className="devc-dim">router</span>}</td>
                <td>{m.frontierSharePct == null ? <span className="devc-dim">router</span> : `${m.frontierSharePct}%`}</td>
                <td>{m.allowRoles} / {m.editRoles}</td>
                {L && <td>{L.modes[m.id.replace(/[^A-Za-z0-9_]/g, '_')] || 0}</td>}
                <td>{m.canEdit && <button type="button" className="devc-filter" disabled={busy} onClick={() => { setEditing(m.id); setDraft({ ...m }); }}>Edit</button>}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {draft && (
          <div className="devc-fields" style={{ marginTop: 10, flexDirection: 'column', alignItems: 'stretch' }}>
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
              <label className="devc-field"><span>Label</span><input value={draft.label} onChange={(e) => setDraft({ ...draft, label: e.target.value })} /></label>
              <label className="devc-field"><span>Conversation</span>
                <select value={draft.conversation} onChange={(e) => setDraft({ ...draft, conversation: e.target.value })}><option value="auto">auto (router decides)</option><option value="plan">always plan first</option><option value="agent">always straight to changes</option></select>
              </label>
              <label className="devc-field"><span>Min tier</span><select value={draft.tierMin} onChange={(e) => setDraft({ ...draft, tierMin: e.target.value })}>{TIER_IDS.map((t) => <option key={t}>{t}</option>)}</select></label>
              <label className="devc-field"><span>Max tier</span><select value={draft.tierMax} onChange={(e) => setDraft({ ...draft, tierMax: e.target.value })}>{TIER_IDS.map((t) => <option key={t}>{t}</option>)}</select></label>
              <label className="devc-field"><span>Plan bias (when auto)</span><select value={draft.planFirstBias || ''} onChange={(e) => setDraft({ ...draft, planFirstBias: e.target.value || null })}><option value="">none</option><option value="plan">plan first</option><option value="agent">straight to changes</option></select></label>
              <label className="devc-field"><span>Engine override</span><select value={draft.engine || ''} onChange={(e) => setDraft({ ...draft, engine: e.target.value || null })}><option value="">router's engine</option>{ENGINES.map(([id]) => <option key={id} value={id}>{id}</option>)}</select></label>
              <label className="devc-field"><span>Frontier cap % (blank = router's)</span><input type="number" min={0} max={100} value={draft.frontierSharePct ?? ''} onChange={(e) => setDraft({ ...draft, frontierSharePct: e.target.value })} /></label>
              {(draft.engine === 'pipeline' || draft.engine === 'shadow-pipeline') && <label className="devc-field"><span>Router pipeline</span><input list="devc-router-pipelines" value={draft.pipelineId || ''} onChange={(e) => setDraft({ ...draft, pipelineId: e.target.value })} placeholder="router-rules" /></label>}
              {canEditRouter && <label className="devc-field"><span>Who may pick</span><select value={draft.allowRoles} onChange={(e) => setDraft({ ...draft, allowRoles: e.target.value })}>{['viewer', 'member', 'admin', 'owner'].map((r) => <option key={r}>{r}</option>)}</select></label>}
              {canEditRouter && <label className="devc-field"><span>Who may edit</span><select value={draft.editRoles} onChange={(e) => setDraft({ ...draft, editRoles: e.target.value })}>{['member', 'admin', 'owner'].map((r) => <option key={r}>{r}</option>)}</select></label>}
            </div>
            <label className="devc-field"><span>Description</span><input value={draft.description} onChange={(e) => setDraft({ ...draft, description: e.target.value })} /></label>
            <label className="devc-field"><span>Cue — appended to every turn's instructions in this profile</span><textarea rows={3} value={draft.cue} onChange={(e) => setDraft({ ...draft, cue: e.target.value })} style={{ border: '1px solid var(--border)', background: 'var(--bg-inset)', borderRadius: 8, padding: '7px 9px', fontFamily: 'inherit', fontSize: 12.5, color: 'var(--text)' }} /></label>
            <div style={{ display: 'flex', gap: 8 }}><button type="button" className="devc-btn primary" disabled={busy} onClick={saveMode}>{busy ? 'Saving…' : 'Save profile'}</button><button type="button" className="devc-btn" disabled={busy} onClick={() => { setEditing(null); setDraft(null); }}>Cancel</button></div>
          </div>
        )}
      </div>
      {msg && <div className={msg.ok ? 'devc-ok' : 'devc-err'} role={msg.ok ? 'status' : 'alert'}>{msg.text}</div>}
    </div>
  );
}

function ConfigPanel({ isAdmin }) {
  const [data, setData] = useState(null); // { doc, defaults }
  const [merged, setMerged] = useState(null);
  const [rawMode, setRawMode] = useState(false);
  const [rawText, setRawText] = useState('');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null); // { ok, text }

  const load = async () => {
    try {
      const d = await api('/dev/config');
      setData(d);
      setMerged(deepMergeCfg(d.defaults, d.doc));
      setMsg(null);
    } catch (e) {
      setMsg({ ok: false, text: e.message });
    }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const setField = (section, key, value) =>
    setMerged((m) => ({ ...m, [section]: { ...m[section], [key]: value } }));

  const save = async () => {
    setBusy(true); setMsg(null);
    try {
      let config;
      if (rawMode) {
        config = JSON.parse(rawText); // throws with a readable message
      } else {
        config = pruneCfg(merged, data.defaults);
        // Unknown keys the defaults don't know about (e.g. vaResources) ride
        // along verbatim from the saved doc.
        for (const [k, v] of Object.entries(data.doc || {})) {
          if (!(k in data.defaults)) config[k] = v;
        }
      }
      const res = await api('/dev/config', { config });
      setMsg({ ok: true, text: res.changes.length ? `Saved — ${res.changes.length} value(s) changed. Backends pick it up within ~1 min.` : 'No changes.' });
      await load();
      setRawMode(false);
    } catch (e) {
      setMsg({ ok: false, text: e.message });
    } finally {
      setBusy(false);
    }
  };

  if (!merged) return <div className="devc-empty"><h3>Live config</h3><p>{msg?.text || 'Loading config/app…'}</p></div>;

  return (
    <div className="devc-panel">
      <div className="devc-card">
        <div className="devc-card-title">
          Live config — <code>config/app</code>
          <span className="devc-dim" style={{ fontWeight: 400 }}>
            &nbsp;applies within ~1 minute, no deploy · every save is audited
          </span>
        </div>
        {!isAdmin && <div className="devc-warn">Read-only — changing values needs the admin role.</div>}
        {rawMode ? (
          <>
            <textarea
              className="devc-cfg-raw"
              value={rawText}
              onChange={(e) => setRawText(e.target.value)}
              spellCheck={false}
            />
            <div className="devc-dim">Raw mode writes the doc exactly as typed (only overrides need to be present — defaults fill the rest).</div>
          </>
        ) : (
          Object.entries(merged).map(([section, fields]) => (
            (fields && typeof fields === 'object' && !Array.isArray(fields)) ? (
              <div className="devc-cfg-section" key={section}>
                <h4>{section}</h4>
                <div className="devc-cfg-grid">
                  {Object.entries(fields).map(([key, value]) => (
                    <label key={key} className="devc-cfg-field">
                      <span>{key}</span>
                      {typeof value === 'boolean' ? (
                        <input type="checkbox" disabled={!isAdmin} checked={value} onChange={(e) => setField(section, key, e.target.checked)} />
                      ) : typeof value === 'number' ? (
                        <input type="number" disabled={!isAdmin} value={value} onChange={(e) => setField(section, key, Number(e.target.value))} />
                      ) : typeof value === 'string' ? (
                        <input type="text" disabled={!isAdmin} value={value} onChange={(e) => setField(section, key, e.target.value)} />
                      ) : (
                        <input type="text" disabled title="Edit nested/complex values in raw mode" value={JSON.stringify(value)} readOnly />
                      )}
                    </label>
                  ))}
                </div>
              </div>
            ) : null
          ))
        )}
        <div className="devc-cfg-actions">
          {isAdmin && <button className="devc-btn primary" disabled={busy} onClick={save}>{busy ? 'Saving…' : 'Save config'}</button>}
          <button
            className="devc-btn"
            onClick={() => {
              if (!rawMode) setRawText(JSON.stringify(data.doc || {}, null, 2));
              setRawMode(!rawMode);
              setMsg(null);
            }}
          >
            {rawMode ? 'Form mode' : 'Raw JSON'}
          </button>
          <button className="devc-btn" onClick={load}>↻ Reload</button>
          {msg && <span className={msg.ok ? 'devc-ok' : 'devc-err'}>{msg.text}</span>}
        </div>
      </div>
    </div>
  );
}

const APP_ROLES = ['admin', 'product', 'tester', 'waitlist'];

function AccessPanel({ isAdmin }) {
  const [rows, setRows] = useState(null);
  const [newEmail, setNewEmail] = useState('');
  const [busyEmail, setBusyEmail] = useState(null);
  const [msg, setMsg] = useState(null);
  const [note, setNote] = useState(null);

  const load = async () => {
    try {
      setRows((await api('/dev/access')).rows);
      setMsg(null);
    } catch (e) {
      setMsg(e.message);
    }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const act = async (email, fn) => {
    setBusyEmail(email); setMsg(null); setNote(null);
    try {
      await fn();
      await load();
    } catch (e) {
      setMsg(e.message);
    } finally {
      setBusyEmail(null);
    }
  };
  const addEmail = () => act(newEmail, async () => {
    const r = await api('/dev/access/allowlist', { email: newEmail.trim(), action: 'add' });
    setNewEmail('');
    setNote(r.seated ? `${newEmail.trim()} already has an account — seated as ${r.role}` : (r.note || null));
  });
  const removeEmail = (email) => act(email, () => api('/dev/access/allowlist', { email, action: 'remove' }));
  const setRole = (email, role) => act(email, () => api('/dev/access/role', { email, role: role || null }));

  return (
    <div className="devc-panel">
      <div className="devc-card">
        <div className="devc-card-title">
          Access — invite people &amp; roles
          <span className="devc-dim" style={{ fontWeight: 400 }}>&nbsp;role changes land on the user&apos;s next sign-in · every change is audited</span>
        </div>
        {!isAdmin && <div className="devc-warn">Read-only — managing access needs the admin role.</div>}
        <p className="devc-muted" style={{ margin: '4px 0 10px', fontSize: 12 }}>
          Roles: <strong>admin</strong> runs the console and changes settings · <strong>product</strong> sees
          everything, changes nothing · <strong>tester</strong> uses the app (the default) · <strong>waitlist</strong> is
          signed up but can&apos;t see pre-launch pages yet.
        </p>
        {isAdmin && (
          <div className="devc-access-add">
            <input
              type="email"
              placeholder="invite by email — seats them and emails a set-password link"
              value={newEmail}
              onChange={(e) => setNewEmail(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter' && newEmail.includes('@')) addEmail(); }}
            />
            <button className="devc-btn primary" disabled={!newEmail.includes('@') || busyEmail} onClick={addEmail}>+ Invite</button>
          </div>
        )}
        {msg && <div className="devc-err">{msg}</div>}
        {note && <div className="devc-dim" role="status" style={{ margin: '0 0 8px' }}>{note}</div>}
        {rows === null ? (
          <div className="devc-dim" style={{ padding: 10 }}>Loading…</div>
        ) : (
          <table className="devc-access-table">
            <thead>
              <tr><th>Email</th><th>Status</th><th>Role</th><th>Last sign-in</th><th /></tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.email} className={busyEmail === r.email ? 'devc-row-busy' : ''}>
                  <td>
                    {r.email}
                    {r.internal && <span className="devc-chip blue" title="internal claim (Dev Console access) — managed by the owner's scripts only">internal</span>}
                  </td>
                  <td>
                    {r.hasAccount
                      ? <span className="devc-chip green" title={r.notAllowlisted ? 'Has an account (not invited from this panel)' : 'Has an account'}>active</span>
                      : <span className="devc-chip gray" title="Invite sent — seated when they set a password and sign in">invited</span>}
                  </td>
                  <td>
                    {r.hasAccount ? (
                      <select
                        disabled={!isAdmin || busyEmail === r.email}
                        value={r.role || ''}
                        onChange={(e) => setRole(r.email, e.target.value)}
                      >
                        <option value="">(none — tester behavior)</option>
                        {APP_ROLES.map((x) => <option key={x} value={x}>{x}</option>)}
                      </select>
                    ) : <span className="devc-dim">—</span>}
                  </td>
                  <td className="devc-dim">{r.lastSignIn ? fmtAgo(new Date(r.lastSignIn).getTime()) : '—'}</td>
                  <td>
                    {isAdmin && !r.notAllowlisted && (
                      <button className="devc-btn danger" title="Remove from this list (does not remove their account or seat)" disabled={busyEmail === r.email} onClick={() => removeEmail(r.email)} data-agent-noclick>✕</button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        <div className="devc-dim" style={{ marginTop: 8 }}>
          Invite seats the person as a <em>member</em> and emails a set-password link; seat roles (admin, owner) are managed on the Team page.
          Removing a row here does not disable an account or seat.
          The <code>internal</code> claim (Dev Console access itself) is deliberately not manageable here.
        </div>
      </div>
    </div>
  );
}

// ---- Secrets panel: write-only key drop ------------------------------------------
// Keys the layer declared (layer.json `secrets`) plus any other file secret
// present, each set / not set. Values go in through the paste box or a
// per-key field and are never shown again; the backend writes them to the
// instance's secret store (shared/devOps/secrets.js) and, on a linked pair,
// mirrors them to the UAT twin. Paste format: KEY=value per line (a .env
// block) — parsed here in the browser, nothing is uploaded as a file.
function parseSecretLines(text) {
  const out = [];
  const bad = [];
  for (const raw of String(text || '').split(/\r?\n/)) {
    const line = raw.trim();
    if (!line || line.startsWith('#')) continue;
    const m = line.match(/^(?:export\s+)?([A-Za-z0-9_.-]+)\s*[=:]\s*(.*)$/);
    if (!m) { bad.push(line.slice(0, 40)); continue; }
    // TIKTOK_CLIENT_KEY → tiktok-client-key (file-secret names are kebab).
    const key = m[1].toLowerCase().replace(/[_.]/g, '-');
    let value = m[2].trim();
    if ((value.startsWith('"') && value.endsWith('"')) || (value.startsWith("'") && value.endsWith("'"))) value = value.slice(1, -1);
    if (!value) continue;
    out.push({ key, value });
  }
  return { entries: out, bad };
}

function SecretsPanel({ isAdmin }) {
  const [data, setData] = useState(null);
  const [paste, setPaste] = useState('');
  const [single, setSingle] = useState({}); // key → value being typed
  const [copyToUat, setCopyToUat] = useState(true);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);
  const [err, setErr] = useState(null);

  const load = async () => {
    try {
      setData(await api('/dev/secrets'));
      setErr(null);
    } catch (e) {
      setErr(e.message);
    }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const submit = async (entries) => {
    if (!entries.length) return;
    setBusy(true); setMsg(null); setErr(null);
    try {
      const r = await api('/dev/secrets', { secrets: entries, copyToUat: copyToUat && !!data?.uatLinked });
      const uatNote = r.uat ? (r.uat.ok ? ' · mirrored to UAT' : ` · UAT: ${r.uat.error}`) : '';
      setMsg(`Saved ${r.keys.length} key${r.keys.length === 1 ? '' : 's'}: ${r.keys.join(', ')}${uatNote}`);
      setPaste('');
      setSingle({});
      await load();
    } catch (e) {
      setErr(e.message);
    } finally {
      setBusy(false);
    }
  };
  const submitPaste = () => {
    const { entries, bad } = parseSecretLines(paste);
    if (bad.length) { setErr(`Could not parse: ${bad.join(' · ')} — use KEY=value per line`); return; }
    if (!entries.length) { setErr('Nothing to save — paste KEY=value lines'); return; }
    submit(entries);
  };
  const remove = async (key) => {
    setBusy(true); setMsg(null); setErr(null);
    try {
      await api('/dev/secrets/delete', { key, copyToUat: copyToUat && !!data?.uatLinked });
      setMsg(`Cleared ${key}`);
      await load();
    } catch (e) {
      setErr(e.message);
    } finally {
      setBusy(false);
    }
  };
  // A key the control plane's Vault placed: keep the value, drop the mark so
  // it can be edited here. The Vault re-marks it on its next push.
  const detach = async (key) => {
    if (!window.confirm(`Take ${key} over locally? The nventr Vault will stop managing it here until it is placed again from the Vault.`)) return;
    setBusy(true); setMsg(null); setErr(null);
    try {
      await api('/dev/secrets/detach', { key });
      setMsg(`${key} is now managed here`);
      await load();
    } catch (e) {
      setErr(e.message);
    } finally {
      setBusy(false);
    }
  };

  const rows = data?.rows || [];
  const groups = [...new Set(rows.map((r) => r.group || ''))];
  const missingRequired = rows.filter((r) => r.required && !r.set).length;
  const managedCount = rows.filter((r) => r.managed === 'vault').length;
  const pasteCount = parseSecretLines(paste).entries.length;

  return (
    <div className="devc-panel">
      <div className="devc-card">
        <div className="devc-card-title">
          Secrets — API keys this instance uses
          <span className="devc-dim" style={{ fontWeight: 400 }}>
            &nbsp;write-only · values are never shown again · every change is audited by key name
            {data?.store === 'ssm' ? ' · stored in SSM' : ''}
          </span>
        </div>
        {!isAdmin && <div className="devc-warn">Read-only — setting secrets needs the admin role.</div>}
        <p className="devc-muted" style={{ margin: '4px 0 10px', fontSize: 12 }}>
          Paste a block of <code>KEY=value</code> lines (one per key, <code>#</code> comments ignored) or fill a single
          key below. The value goes straight to this instance&apos;s secret store — not into git, a file upload, or an
          agent session. <code>TIKTOK_CLIENT_KEY</code> and <code>tiktok-client-key</code> name the same secret.
        </p>
        {isAdmin && (
          <>
            <textarea
              className="devc-secrets-paste"
              rows={5}
              spellCheck={false}
              autoComplete="off"
              placeholder={'tiktok-client-key=…\ntiktok-client-secret=…\nmeta-app-id=…'}
              value={paste}
              onChange={(e) => setPaste(e.target.value)}
              data-nova-ignore
            />
            <div className="devc-secrets-actions">
              <button className="devc-btn primary" disabled={busy || !pasteCount} onClick={submitPaste}>
                Save {pasteCount ? `${pasteCount} key${pasteCount === 1 ? '' : 's'}` : ''}
              </button>
              {data?.uatLinked && (
                <label className="devc-dim" style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                  <input type="checkbox" checked={copyToUat} onChange={(e) => setCopyToUat(e.target.checked)} />
                  also set on the UAT twin
                </label>
              )}
            </div>
          </>
        )}
        {err && <div className="devc-err">{err}</div>}
        {msg && <div className="devc-ok">{msg}</div>}
        {data === null && !err ? (
          <div className="devc-dim" style={{ padding: 10 }}>Loading…</div>
        ) : rows.length === 0 ? (
          <div className="devc-dim" style={{ padding: 10 }}>
            No secrets declared — a layer lists what it needs in <code>layer.json</code> <code>secrets</code>; any key pasted above is stored anyway.
          </div>
        ) : (
          <>
            {missingRequired > 0 && (
              <div className="devc-warn" style={{ marginTop: 6 }}>
                {missingRequired} required key{missingRequired === 1 ? ' is' : 's are'} not set — the features that need them show as not configured.
              </div>
            )}
            {managedCount > 0 && (
              <p className="devc-muted" style={{ margin: '6px 0 0', fontSize: 12 }}>
                {managedCount} key{managedCount === 1 ? ' is' : 's are'} <strong>managed in the nventr Vault</strong> (your organization&apos;s console → Administration → Vault): rotate or remove them there and they are re-placed here, including after every re-stamp. &ldquo;Take over&rdquo; makes one editable here instead.
              </p>
            )}
            <table className="devc-access-table" style={{ marginTop: 8 }}>
              <thead>
                <tr><th>Key</th><th>Status</th>{isAdmin && <th>Set value</th>}<th /></tr>
              </thead>
              <tbody>
                {groups.map((g) => (
                  <Fragment key={g || '_'}>
                    {groups.length > 1 && (
                      <tr><td colSpan={isAdmin ? 4 : 3} className="devc-dim" style={{ paddingTop: 10, fontWeight: 600 }}>{g || 'Other'}</td></tr>
                    )}
                    {rows.filter((r) => (r.group || '') === g).map((r) => (
                      <tr key={r.key}>
                        <td>
                          <code>{r.key}</code>
                          {r.label !== r.key && <div className="devc-dim" style={{ fontSize: 11.5 }}>{r.label}</div>}
                          {r.help && <div className="devc-dim" style={{ fontSize: 11.5 }}>{r.help}</div>}
                        </td>
                        <td>
                          {r.set
                            ? <span className="devc-chip green">set</span>
                            : r.required ? <span className="devc-chip amber">required · not set</span> : <span className="devc-chip gray">not set</span>}
                          {r.managed === 'vault' && (
                            <span className="devc-chip gray" style={{ marginLeft: 4 }} title={`Placed by the nventr Vault${r.vaultEntry && r.vaultEntry !== r.key ? ` (entry ${r.vaultEntry})` : ''}`}>managed in nventr Vault</span>
                          )}
                        </td>
                        {isAdmin && (
                          <td>
                            {r.managed === 'vault' ? (
                              <button className="devc-btn" disabled={busy} onClick={() => detach(r.key)} title="Keep the value, stop the Vault from managing it here">Take over</button>
                            ) : (
                              <div className="devc-access-add" style={{ margin: 0 }}>
                                <input
                                  type="password"
                                  autoComplete="new-password"
                                  placeholder={r.set ? 'replace…' : 'value…'}
                                  value={single[r.key] || ''}
                                  onChange={(e) => setSingle({ ...single, [r.key]: e.target.value })}
                                  onKeyDown={(e) => { if (e.key === 'Enter' && (single[r.key] || '').trim()) submit([{ key: r.key, value: single[r.key] }]); }}
                                  data-nova-ignore
                                />
                                <button className="devc-btn" disabled={busy || !(single[r.key] || '').trim()} onClick={() => submit([{ key: r.key, value: single[r.key] }])}>Save</button>
                              </div>
                            )}
                          </td>
                        )}
                        <td>
                          {isAdmin && r.set && r.managed !== 'vault' && (
                            <button className="devc-btn danger" title={`Clear ${r.key}`} disabled={busy} onClick={() => remove(r.key)} data-agent-noclick>✕</button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </Fragment>
                ))}
              </tbody>
            </table>
          </>
        )}
        <div className="devc-dim" style={{ marginTop: 8 }}>
          Keys the stack mints itself (licence, data-plane credentials, control-plane tethers) are managed by the
          provisioner and are not listed here.
        </div>
      </div>
    </div>
  );
}

const LOG_RANGES = [['15m', 15 * 60 * 1000], ['1h', 60 * 60 * 1000], ['6h', 6 * 60 * 60 * 1000], ['24h', 24 * 60 * 60 * 1000]];

// ---- Agents panel: eval runs + the governance trail -----------------------------
// Read-only ops surface for the agent stack: recent eval runs (written by
// scripts/agent-evals.mjs into `evals`) and the latest policy denials from
// the append-only auditEvents trail (shared/policy.js).
function AgentsPanel() {
  const evals = useOrdered('evals', 'createdAt', 'desc', 20);
  const audits = useOrdered('auditEvents', 'createdAt', 'desc', 200);
  const denials = audits.filter((a) => a.decision === 'denied').slice(0, 30);
  const [openRun, setOpenRun] = useState(null);
  const [usage, setUsage] = useState(null);
  const [openTrace, setOpenTrace] = useState(null);

  useEffect(() => {
    api('/dev/ops/agent-usage').then(setUsage).catch(() => setUsage(null));
  }, []);

  return (
    <div className="devc-panel">
      <div className="devc-card">
        <div className="devc-card-title">AI usage — {usage?.period || 'this month'}</div>
        {!usage && <p className="devc-muted">Loading…</p>}
        {usage && Object.keys(usage.counters || {}).length === 0 && (
          <p className="devc-muted">No AI usage metered yet this month.</p>
        )}
        {usage &&
          Object.entries(usage.counters || {})
            .sort(([a], [b]) => a.localeCompare(b))
            .map(([k, v]) => (
              <div key={k} className="devc-muted" style={{ fontSize: 12, padding: '2px 0' }}>
                <code>{k}</code>: {fmtTok(v)}
              </div>
            ))}
      </div>

      <div className="devc-card">
        <div className="devc-card-title">Recent turn traces</div>
        {(!usage || !(usage.traces || []).length) && <p className="devc-muted">No traced turns yet.</p>}
        {(usage?.traces || []).map((t) => (
          <div key={t.id} style={{ marginBottom: 4 }}>
            <button
              className="devc-filter"
              style={{ width: '100%', textAlign: 'left' }}
              onClick={() => setOpenTrace(openTrace === t.id ? null : t.id)}
            >
              {fmtAgo(t.at)} · {t.status} · {fmtTok(t.tokens)} tok · {fmtDur(t.ms)} ({fmtDur(t.modelMs)} model) — {t.content}
            </button>
            {openTrace === t.id && (
              <div style={{ padding: '4px 10px', fontSize: 12 }}>
                {(t.spans || []).map((s, i) => (
                  <div key={i} className="devc-muted" style={{ padding: '1px 0' }}>
                    {s.kind === 'model'
                      ? `🧠 ${s.stage} · ${s.provider}/${s.model} · ${fmtDur(s.ms)} · ${fmtTok(s.tokens)} tok`
                      : `${s.denied ? '🚫' : s.ok ? '🔧' : '⚠️'} ${s.tool} · ${fmtDur(s.ms)}`}
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>

      <div className="devc-card">
        <div className="devc-card-title">Agent evals</div>
        {evals.length === 0 && (
          <p className="devc-muted">
            No eval runs yet. Evals score your agents against a golden question set and an adversarial red-team set;
            results land here.
            <details style={{ marginTop: 6 }}>
              <summary className="devc-dim">Advanced: run from the command line</summary>
              <code>node scripts/agent-evals.mjs</code>
            </details>
          </p>
        )}
        {evals.map((e) => (
          <div key={e.id} style={{ marginBottom: 6 }}>
            <button
              className="devc-filter"
              style={{ width: '100%', textAlign: 'left' }}
              onClick={() => setOpenRun(openRun === e.id ? null : e.id)}
            >
              {e.summary?.ok ? '✅' : '❌'} {fmtAgo(e.runAt)} · golden {e.summary?.goldenPass}/{e.summary?.goldenTotal}
              {e.summary?.goldenSkipped ? ` (${e.summary.goldenSkipped} skipped)` : ''} · red team{' '}
              {e.summary?.advPass}/{e.summary?.advTotal} · provider {e.provider}
            </button>
            {openRun === e.id && (
              <div style={{ padding: '6px 10px', fontSize: 12 }}>
                {[...(e.golden || []), ...(e.adversarial || [])].map((r) => (
                  <div key={r.id} className="devc-muted" style={{ padding: '2px 0' }}>
                    {r.pass === null ? '⏭' : r.pass ? '✓' : '✗'} {r.id}
                    {typeof r.score === 'number' ? ` (${r.score})` : ''} — {r.reason}
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>

      <div className="devc-card">
        <div className="devc-card-title">Recent policy denials ({denials.length})</div>
        {denials.length === 0 && <p className="devc-muted">No denials in the recent trail.</p>}
        {denials.map((a) => (
          <div key={a.id} className="devc-muted" style={{ fontSize: 12, padding: '3px 0' }}>
            🚫 {fmtAgo(a.createdAt)} · {a.surface} · <code>{a.tool}</code>
            {a.actor?.agentId ? ` · agent ${a.actor.agentId}` : a.actor?.userId ? ` · user ${a.actor.userId.slice(0, 8)}` : ''}
            {a.reason ? ` — ${a.reason}` : ''}
          </div>
        ))}
      </div>
    </div>
  );
}

// ---- Ecosystem (live 3D view) ------------------------------------------------
// One poll of /dev/ops/ecosystem every 30s while the tab is visible, shared by
// the full view and the hero widget. Attention is carried by the scene's
// color + motion and the one-line caption — no badges, sounds, or modals.
function useEcosystemFeed(enabled = true) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  useEffect(() => {
    if (!enabled) return undefined;
    let stop = false;
    let t = null;
    const tick = async () => {
      if (document.visibilityState !== 'visible') return;
      try { const out = await api('/dev/ops/ecosystem'); if (!stop) { setData(out); setErr(null); } } catch (e) { if (!stop) setErr(e.message); }
    };
    tick();
    t = setInterval(tick, 30000);
    const onVis = () => { if (document.visibilityState === 'visible') tick(); };
    document.addEventListener('visibilitychange', onVis);
    return () => { stop = true; clearInterval(t); document.removeEventListener('visibilitychange', onVis); };
  }, [enabled]);
  return { data, err };
}

const LEVEL_CHIP = { ok: 'green', warn: 'amber', down: 'red', off: 'gray' };

function EcosystemPanel() {
  const { data, err } = useEcosystemFeed(true);
  const s = useMemo(() => deriveEcosystem(data), [data]);
  const plane = data?.plane && !data.plane.unreachable ? data.plane : null;
  const cmd = useCommandToggle('devconsole');
  const cs = useMemo(() => deriveCommand(data), [data]);
  // Telemetry: the control plane's edge series for this instance + twin, the
  // local runtime block (queues / stale claims / users), or the simulator
  // over the same two-node roster. Only on while Command mode is up.
  const slug = data?.slug || s.viewer.slug || '';
  const hasTwin = !!plane?.twin?.exists;
  const roster = useMemo(() => (slug ? [{ slug, twinSlug: hasTwin ? `${slug}-uat` : null }, ...(hasTwin ? [{ slug: `${slug}-uat`, envRole: 'uat' }] : [])] : []), [slug, hasTwin]);
  const fetchReal = useCallback((w) => api(`/dev/ops/telemetry?window=${w}`), []);
  const { telemetry, controls } = useTelemetry({ surface: 'devconsole', roster, fetchReal, focusSlug: slug, enabled: cmd.on && !!data });
  // Stage cards (hover / pinned) re-derive from the current feed + telemetry.
  const inspectorFor = useCallback((sel) => commandInspector(cs, telemetry, sel), [cs, telemetry]);
  const panels = useMemo(() => (data ? [...appPanels(cs, { isAdmin: true }), trafficPanel(telemetry, { side: 'left', id: 'ap-traffic' }), anomalyPanel(telemetry, { side: 'right', id: 'ap-anomalies' })] : []), [data, cs, telemetry]);
  const classic = (
    <>
      {err && <div className="devc-dim" style={{ marginTop: 6 }}>feed error: {err}</div>}
      {s.viewer.role === 'uat' && (
        <div className="devc-eco-here">
          You are on the <strong>UAT twin</strong>{s.viewer.name ? <> (<code>{s.viewer.slug}</code>)</> : null} — the picture below is the whole pair; production is the centre, this host is the twin.
        </div>
      )}
      <div className="devc-eco-legend devc-dim">
        <span><i className="eco-dot is-ok" /> gold = healthy</span>
        <span><i className="eco-dot is-warn" /> amber = needs a look</span>
        <span><i className="eco-dot is-down" /> red = down</span>
        <span>The ring pulses each time the uptime monitor checks · sparks mean a security scan is running · a light travelling along a line is a deploy in progress.</span>
      </div>
      <div className="devc-eco-grid">
        <div className="devc-card">
          <div className="devc-card-title">Services <span className="devc-dim" style={{ fontWeight: 400 }}>{data?.runtime === 'docker' ? 'docker host' : 'ECS'}</span></div>
          {s.services.length ? (
            <table className="devc-eco-table">
              <tbody>
                {[...s.services.filter((x) => x.kind !== 'runner' && x.kind !== 'init'), ...s.services.filter((x) => x.kind === 'init'), ...s.services.filter((x) => x.kind === 'runner')].map((x) => (
                  <tr key={x.name} title={x.about || ''}>
                    <td><span className={`devc-chip ${LEVEL_CHIP[x.level]}`}>{x.level === 'off' ? (x.kind === 'init' ? 'done' : 'idle') : x.level}</span></td>
                    <td>{x.name}{x.kind === 'runner' && <span className="devc-dim"> · runner</span>}{x.kind === 'init' && <span className="devc-dim"> · one-time job</span>}{x.about && <div className="devc-dim" style={{ fontSize: 'var(--fs-xs)' }}>{x.about}</div>}</td>
                    <td className="devc-dim">{x.status}{x.restartCount > 0 ? ` · ${x.restartCount} restart${x.restartCount === 1 ? '' : 's'}` : ''}{x.memMb != null ? ` · ${x.memMb} MB` : ''}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : <div className="devc-dim">{data ? 'No services reported.' : 'Loading…'}</div>}
        </div>
        <div className="devc-card">
          <div className="devc-card-title">Control plane</div>
          {plane ? (
            <div className="devc-eco-kv">
              <div><span className="devc-dim">{s.viewer.role === 'uat' ? 'production' : 'instance'}</span><span>{plane.instance?.name || plane.instance?.slug} · {plane.instance?.status}{plane.instance?.version ? ` · ${plane.instance.version}` : ''}</span></div>
              <div><span className="devc-dim">monitor</span><span><span className={`devc-chip ${LEVEL_CHIP[s.monitor.level]}`}>{plane.monitor?.state || 'unknown'}</span>{plane.monitor?.lastCheckAt ? ` ${fmtAgo(plane.monitor.lastCheckAt)}` : ''}{plane.monitor?.lastLatencyMs != null ? ` · ${plane.monitor.lastLatencyMs}ms` : ''}{plane.monitor?.tlsDaysLeft != null ? ` · TLS ${plane.monitor.tlsDaysLeft}d` : ''}</span></div>
              <div><span className="devc-dim">{s.viewer.role === 'uat' ? 'uat twin (this host)' : 'uat twin'}</span><span>{plane.twin?.exists ? <>{plane.twin.status}{plane.twin.lastDeploy ? ` · last deploy ${plane.twin.lastDeploy.status} ${fmtAgo(plane.twin.lastDeploy.at)}` : ''}</> : 'none'}</span></div>
              <div><span className="devc-dim">prod deploy</span><span>{plane.prod?.activeDeploy ? `${plane.prod.activeDeploy.status} · ${plane.prod.activeDeploy.stage || ''}` : plane.prod?.lastDeploy ? `${plane.prod.lastDeploy.status} ${fmtAgo(plane.prod.lastDeploy.at)}` : '—'}</span></div>
              <div><span className="devc-dim">security</span><span><span className={`devc-chip ${LEVEL_CHIP[s.security.level]}`}>{s.security.scanning ? 'scanning' : s.security.level === 'off' ? 'no scan' : s.security.level}</span>{plane.scan?.lastScanAt ? ` ${fmtAgo(plane.scan.lastScanAt)} · ${plane.scan.counts?.critical || 0} critical · ${plane.scan.counts?.high || 0} high · ${plane.scan.counts?.medium || 0} medium` : ''}</span></div>
              <div><span className="devc-dim">incidents</span><span>{plane.incidents?.length ? plane.incidents.map((i) => `${i.status} ${fmtAgo(i.openedAt)}${i.reason ? ` (${i.reason})` : ''}`).join(' · ') : 'none in 24h'}</span></div>
              <div><span className="devc-dim">updates</span><span>{plane.updates?.versionBehind ? `kit ${plane.updates.latestKitVersion || 'update'} available` : 'up to date'}</span></div>
              <div><span className="devc-dim">edge</span><span>{plane.edge?.domains?.length ? plane.edge.domains.map((d) => d.host).join(' · ') : '—'}</span></div>
              <div><span className="devc-dim">license</span><span>{data.license?.state || '—'}{data.license?.graceUntil ? ` · grace until ${new Date(data.license.graceUntil).toLocaleDateString()}` : ''}</span></div>
            </div>
          ) : (
            <div className="devc-dim">
              {!data ? 'Loading…' : data.license?.state === 'dormant' || !data.license
                ? 'No control plane attached (LICENSE_KEY / CONTROL_PLANE_URL unset) — showing local services only.'
                : 'Control plane unreachable — showing local services; monitor, twin, scan and edge data resume when it answers.'}
            </div>
          )}
        </div>
      </div>
      <div className="devc-card">
        <div className="devc-card-title">Recent activity</div>
        {s.events.length ? <EcosystemTicker events={s.events} limit={30} /> : <div className="devc-dim">{data ? 'Quiet.' : 'Loading…'}</div>}
      </div>
    </>
  );
  // Command mode: the one-screen stage; every HUD panel opens the classic
  // cards in the drawer (same data, same words).
  const drawer = useMemo(() => {
    const all = { title: 'Ecosystem detail', render: () => <div className="devc-eco-panel">{classic}</div> };
    const costs = {
      title: `Costs · ${data?.period || ''}`,
      render: () => (data?.costs ? (
        <table className="devc-eco-table"><tbody>
          {Object.entries(data.costs.byService || {}).map(([k, v]) => <tr key={k}><td>{k}</td><td className="devc-dim">${v.toFixed(2)}</td></tr>)}
          <tr><td><strong>month to date</strong></td><td><strong>${data.costs.mtdUsd.toFixed(2)}</strong></td></tr>
          <tr><td>burn / day</td><td className="devc-dim">${data.costs.burnPerDayUsd.toFixed(2)}</td></tr>
          <tr><td>projected</td><td className="devc-dim">${data.costs.projectedUsd.toFixed(2)}</td></tr>
        </tbody></table>
      ) : <div className="devc-dim">No AWS cost data yet — the cost worker pulls Cost Explorer daily.</div>),
    };
    const usage = {
      title: `Usage · ${data?.period || ''}`,
      render: () => (data?.usage ? (
        <table className="devc-eco-table"><tbody>
          {Object.entries(data.usage.counters || {}).filter(([k]) => !k.startsWith('tok_')).sort((a, b) => b[1] - a[1]).map(([k, v]) => <tr key={k}><td>{k}</td><td className="devc-dim">{Number(v).toLocaleString()}</td></tr>)}
        </tbody></table>
      ) : <div className="devc-dim">No metered usage this month.</div>),
    };
    const traffic = {
      title: `Traffic & flows · ${telemetry.window.label}`,
      render: () => (
        <>
          {slug && <div className="cmd-panel-body" style={{ marginBottom: 8 }}><RuntimeRows t={telemetry} slug={slug} /></div>}
          <FlowTable t={telemetry} />
        </>
      ),
    };
    const anomalies = { title: `Anomalies · ${telemetry.window.label}`, render: () => <AnomalyTable t={telemetry} /> };
    return { 'ap-identity': all, 'ap-health': all, 'ap-services': all, 'ap-eco': all, 'ap-security': all, 'ap-envs': all, 'ap-costs': costs, 'ap-usage': usage, 'ap-traffic': traffic, 'ap-anomalies': anomalies };
  }, [data, err, telemetry, slug]); // eslint-disable-line react-hooks/exhaustive-deps -- classic derives from data/err

  return (
    <div className={`devc-panel devc-eco-panel ${cmd.on ? 'is-command' : ''}`}>
      {cmd.supported && <div className="devc-eco-toggle"><CommandToggle on={cmd.on} onChange={cmd.set} /></div>}
      {cmd.on ? (
        <CommandView data={data} derive={deriveCommand} scene="command" panels={panels} drawer={drawer} events={cs.events || []} caption={data ? cs.caption : (err ? `feed error: ${err}` : 'loading…')} className="is-devc" telemetry={telemetry} defaultInspector={data ? inspectorFor(null) : null} inspectorFor={inspectorFor} columns={data ? topologyColumns(cs, telemetry) : null}>
          <TelemetryControls controls={controls} compact />
          <ActivityStrip telemetry={telemetry} />
        </CommandView>
      ) : (
        <>
          <EcosystemFrame data={data} ticker={false} />
          {classic}
        </>
      )}
    </div>
  );
}

function EcosystemWidget({ onOpen }) {
  const { data } = useEcosystemFeed(true);
  return (
    <div className="devc-eco-widget">
      {/* Wide and short: the hero has to fit above the composer without scrolling. */}
      <EcosystemFrame data={data} compact aspect="16 / 7" onClick={onOpen} />
    </div>
  );
}

function OpsPanel() {
  const [health, setHealth] = useState(null);
  const [group, setGroup] = useState('backend');
  const [range, setRange] = useState(15 * 60 * 1000);
  const [filter, setFilter] = useState('');
  const [events, setEvents] = useState(null);
  const [auto, setAuto] = useState(false);
  const [err, setErr] = useState(null);
  const logEnd = useRef(null);

  const loadHealth = async () => {
    try { setHealth(await api('/dev/ops/health')); } catch (e) { setErr(e.message); }
  };
  const loadLogs = async () => {
    try {
      const qs = new URLSearchParams({ group, sinceMs: String(range), ...(filter ? { filter } : {}) });
      const out = await api(`/dev/ops/logs?${qs}`);
      setEvents(out.events);
      setErr(null);
      setTimeout(() => logEnd.current?.scrollIntoView({ block: 'end' }), 50);
    } catch (e) {
      setErr(e.message);
    }
  };
  useEffect(() => { loadHealth(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { loadLogs(); }, [group, range]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!auto) return undefined;
    const t = setInterval(() => { loadLogs(); loadHealth(); }, 15000);
    return () => clearInterval(t);
  }, [auto, group, range, filter]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="devc-panel">
      <div className="devc-card">
        <div className="devc-card-title">
          Service health
          {health && <button className="devc-btn" style={{ marginLeft: 8 }} onClick={loadHealth}>↻</button>}
          <span className="devc-dim" style={{ fontWeight: 400 }}>&nbsp;· the backend service as a whole — per-container state (what each one is, restarts, memory) is under Ecosystem › Services</span>
        </div>
        {health ? (
          (health.tiers || [health]).map((t) => (
            <div className="devc-ops-health" key={t.service}>
              <span className={`devc-chip ${t.env === 'uat' ? 'blue' : 'gray'}`}>{(t.env || 'prod').toUpperCase()}</span>
              <span className={`devc-chip ${t.rollout === 'COMPLETED' ? 'green' : t.rollout === 'FAILED' ? 'red' : 'blue'}`}>
                {t.rollout || t.status}
              </span>
              <span>{t.service}</span>
              <span className="devc-dim">running {t.running}/{t.desired}{t.pending ? ` (+${t.pending} pending)` : ''}</span>
              <span className="devc-dim">task-def {t.taskDefinition}</span>
              {t.deployedAt && <span className="devc-dim">last deploy {fmtAgo(new Date(t.deployedAt).getTime())}</span>}
            </div>
          ))
        ) : <div className="devc-dim">Loading…</div>}
        {health?.events?.length > 0 && (
          <details className="devc-ops-events">
            <summary className="devc-dim">Recent service events (this environment)</summary>
            {health.events.map((e, i) => (
              <div key={i} className="devc-dim">{fmtAgo(new Date(e.at).getTime())} — {e.text}</div>
            ))}
          </details>
        )}
      </div>
      <div className="devc-card devc-ops-logcard">
        <div className="devc-card-title">
          CloudWatch logs
          <span className="devc-dim" style={{ fontWeight: 400 }}>&nbsp;read-only diagnostics for this environment</span>
        </div>
        <div className="devc-ops-controls">
          <div className="devc-filters">
            {[['backend', 'API + workers'], ['runner', 'Deploy/job runners']].map(([g, label]) => (
              <button key={g} className={`devc-filter${group === g ? ' active' : ''}`} onClick={() => setGroup(g)}>{label}</button>
            ))}
          </div>
          <div className="devc-filters">
            {LOG_RANGES.map(([label, ms]) => (
              <button key={label} className={`devc-filter${range === ms ? ' active' : ''}`} onClick={() => setRange(ms)}>{label}</button>
            ))}
          </div>
          <input
            className="devc-search devc-ops-filter"
            placeholder='filter (CloudWatch syntax, e.g. "error")'
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') loadLogs(); }}
          />
          <button className="devc-btn" onClick={loadLogs}>↻ Refresh</button>
          <label className="devc-dim devc-tab-opt">
            <input type="checkbox" checked={auto} onChange={(e) => setAuto(e.target.checked)} /> auto (15s)
          </label>
        </div>
        {err && <div className="devc-err">{err}</div>}
        <div className="devc-ops-logs">
          {events === null ? (
            <div className="devc-dim">Loading…</div>
          ) : events.length ? (
            events.map((e, i) => (
              <div key={i} className="devc-ops-line">
                <span className="devc-dim">{new Date(e.ts).toLocaleTimeString()}</span> {e.text}
              </div>
            ))
          ) : (
            <div className="devc-dim">No log events in this window{filter ? ' matching the filter' : ''}.</div>
          )}
          <div ref={logEnd} />
        </div>
      </div>
    </div>
  );
}

// ---- main page -------------------------------------------------------------------
// Lab reports — the control plane's browser tests of this app (App Builder →
// Lab), synced down with the app bundle. Read-only here; approvals,
// screenshots and new runs live on the plane.
function LabReportsPanel() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [open, setOpen] = useState(null);
  useEffect(() => {
    api('/dev/ops/lab-reports').then(setData).catch((e) => setErr(e.message));
  }, []);
  const reports = data?.reports || [];
  return (
    <div className="devc-panel">
      <div className="devc-card">
        <div className="devc-card-title">
          Lab reports
          <span className="devc-dim" style={{ fontWeight: 400 }}>&nbsp;browser tests and performance runs of this app from the control plane · last {reports.length}</span>
        </div>
        {err && <div className="devc-err">{err}</div>}
        {!data && !err && <div className="devc-dim">Loading…</div>}
        {data && !reports.length && (
          <div className="devc-dim">No Lab test reports have been synced yet. Run a test from App Builder → Lab on the control plane; the next design sync brings its report here.</div>
        )}
        {reports.map((r) => (
          <div key={r.id} className="devc-ops-health" style={{ display: 'block' }}>
            <button className="devc-btn" style={{ display: 'flex', gap: 8, alignItems: 'center', width: '100%', textAlign: 'left' }} onClick={() => setOpen(open === r.id ? null : r.id)}>
              <span className={`devc-chip ${r.verdict === 'pass' ? 'green' : 'red'}`}>{r.verdict === 'pass' ? 'passed' : 'failed'}</span>
              <span className={`devc-chip ${r.env === 'live' ? 'gray' : 'blue'}`}>{(r.env || '').toUpperCase()}</span>
              {r.kind === 'perf' && <span className="devc-chip">perf</span>}
              <span style={{ flex: 1 }}>{r.brief}</span>
              <span className="devc-dim">{r.finishedAt ? fmtAgo(r.finishedAt) : ''}</span>
            </button>
            {open === r.id && (
              <div style={{ padding: '6px 0 4px 8px' }}>
                {r.summary && <div className="devc-dim" style={{ marginBottom: 6 }}>{r.summary}</div>}
                {r.perf && (
                  <table className="devc-usage-table" style={{ marginBottom: 6 }}>
                    <thead><tr><th>page</th><th>TTFB</th><th>LCP</th><th>CLS</th><th>KB</th><th>req</th></tr></thead>
                    <tbody>
                      {(r.perf.pages || []).map((p) => (
                        <tr key={p.path} style={p.over?.length ? { color: 'var(--danger, #e5484d)' } : undefined}>
                          <td>{p.path}</td><td>{p.ttfbMs ?? '—'}</td><td>{p.lcpMs ?? '—'}</td><td>{p.cls ?? '—'}</td><td>{p.transferBytes != null ? Math.round(p.transferBytes / 1024) : '—'}</td><td>{p.requests ?? '—'}</td>
                        </tr>
                      ))}
                      {(r.perf.agent || []).map((a, i) => (
                        <tr key={`a${i}`} style={a.over ? { color: 'var(--danger, #e5484d)' } : undefined}><td colSpan={2}>agent: {a.q}</td><td colSpan={4}>{a.ms} ms{a.ok ? '' : ' · no answer'}</td></tr>
                      ))}
                    </tbody>
                  </table>
                )}
                {(r.steps || []).map((s) => (
                  <div key={s.i} className="devc-dim" style={{ display: 'flex', gap: 8, alignItems: 'baseline', padding: '2px 0' }}>
                    <span style={{ color: s.ok ? 'var(--ok, #3ecf8e)' : 'var(--danger, #e5484d)' }}>{s.ok ? '✓' : '✗'}</span>
                    <span style={{ flex: 1 }}>
                      {s.kind === 'check' ? 'check: ' : ''}{s.instruction}{s.write ? ' · writes' : ''}
                      {s.observed ? <div style={{ opacity: 0.8 }}>{s.observed}</div> : null}
                      {s.error ? <div style={{ color: 'var(--danger, #e5484d)' }}>{s.error}</div> : null}
                    </span>
                  </div>
                ))}
                {r.url && <div className="devc-dim" style={{ marginTop: 6 }}>target {r.url}</div>}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

// Self-test — the derived feature suite (backend shared/selfTest). Cases come
// from the routes file + entity registry + tool catalog + the synced design,
// so the list grows with the kit; design screens without a built route show
// as pending until the build lands. The usage reporter carries the latest
// summary to the control plane (Apps → Health → Features).
const ST_STATUS = { pass: ['green', '✓'], fail: ['red', '✗'], skip: ['gray', '–'], pending: ['blue', '…'] };
function SelfTestPanel() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(null);
  const [run, setRun] = useState(null);
  const load = useCallback(() => api('/dev/ops/selftest').then(setData).catch((e) => setErr(e.message)), []);
  useEffect(() => { load(); }, [load]);
  const latest = data?.latest;
  const suite = data?.suite;
  const fresh = suite ? suite.cases.filter((c) => !c.baseline) : [];
  const runNow = async () => {
    setBusy(true); setErr(null);
    try { await api('/dev/ops/selftest/run', {}); await load(); } catch (e) { setErr(e.message); } finally { setBusy(false); }
  };
  const loadRun = async (id) => {
    if (open === id) { setOpen(null); return; }
    setOpen(id); setRun(null);
    try { setRun(await api(`/dev/ops/selftest/runs/${id}`)); } catch (e) { setErr(e.message); }
  };
  return (
    <div className="devc-panel" data-nova-note="Instance self-test: a derived feature suite (routes, entities, tools, surfaces, model, synced design). Pending = design screen without a built route yet.">
      <div className="devc-card">
        <div className="devc-card-title" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          Self-test
          <span className="devc-dim" style={{ fontWeight: 400, flex: 1 }}>
            {suite ? `${suite.total} cases derived from this build${data.baseline ? ` · ${data.baseline.total} shipped with ${latest?.kitRef || 'the kit'}` : ''}` : ''}
          </span>
          <button className="devc-btn" onClick={runNow} disabled={busy || data?.running}>{busy || data?.running ? 'Running…' : 'Run now'}</button>
        </div>
        {err && <div className="devc-err">{err}</div>}
        {!data && !err && <div className="devc-dim">Loading…</div>}
        {data && !latest && <div className="devc-dim">No pass filed yet — the worker runs one every {data.config?.intervalMin || 30} min, or run now.</div>}
        {latest && (
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginBottom: 8 }}>
            <span className={`devc-chip ${latest.ok ? 'green' : 'red'}`}>{latest.ok ? 'passing' : 'failing'}</span>
            <span>{latest.passed}/{latest.total} pass</span>
            {latest.failed ? <span style={{ color: 'var(--danger, #e5484d)' }}>{latest.failed} failing</span> : null}
            {latest.pending ? <span className="devc-chip blue">{latest.pending} design pending</span> : null}
            {latest.skipped ? <span className="devc-dim">{latest.skipped} skipped</span> : null}
            {latest.design?.screens ? <span className="devc-dim">design {latest.design.built}/{latest.design.screens} screens built</span> : null}
            <span className="devc-dim">{fmtAgo(latest.at)} · {latest.requestedBy}</span>
          </div>
        )}
        {latest?.failing?.length ? (
          <div style={{ marginBottom: 8 }}>
            {latest.failing.map((f) => (
              <div key={f.id} className="devc-dim" style={{ display: 'flex', gap: 8, padding: '2px 0' }}>
                <span style={{ color: 'var(--danger, #e5484d)' }}>✗</span><code>{f.id}</code><span style={{ flex: 1 }}>{f.error}</span>
              </div>
            ))}
          </div>
        ) : null}
        {fresh.length ? (
          <div className="devc-dim" style={{ marginBottom: 8 }}>
            New since the shipped baseline: {fresh.map((c) => c.id).join(', ')}
          </div>
        ) : null}
        {suite && (
          <details>
            <summary className="devc-dim" style={{ cursor: 'pointer' }}>All {suite.total} cases</summary>
            <table className="devc-usage-table" style={{ marginTop: 6 }}>
              <thead><tr><th>case</th><th>group</th><th>checks</th></tr></thead>
              <tbody>
                {suite.cases.map((c) => (
                  <tr key={c.id}><td><code>{c.id}</code>{c.model ? <span className="devc-chip" style={{ marginLeft: 6 }}>model</span> : null}</td><td>{c.group}</td><td>{c.label}</td></tr>
                ))}
              </tbody>
            </table>
          </details>
        )}
      </div>
      {data?.runs?.length ? (
        <div className="devc-card">
          <div className="devc-card-title">Recent passes</div>
          {data.runs.map((r) => (
            <div key={r.runId} className="devc-ops-health" style={{ display: 'block' }}>
              <button className="devc-btn" style={{ display: 'flex', gap: 8, alignItems: 'center', width: '100%', textAlign: 'left' }} onClick={() => loadRun(r.runId)}>
                <span className={`devc-chip ${r.ok ? 'green' : 'red'}`}>{r.ok ? 'pass' : 'fail'}</span>
                <span style={{ flex: 1 }}>{r.passed}/{r.total}{r.pending ? ` · ${r.pending} pending` : ''}{r.failed ? ` · ${r.failed} failing` : ''}</span>
                <span className="devc-dim">{fmtAgo(r.at)}</span>
              </button>
              {open === r.runId && (
                <div style={{ padding: '6px 0 4px 8px' }}>
                  {!run && <div className="devc-dim">Loading…</div>}
                  {(run?.results || []).map((x) => (
                    <div key={x.id} className="devc-dim" style={{ display: 'flex', gap: 8, alignItems: 'baseline', padding: '2px 0' }}>
                      <span className={`devc-chip ${ST_STATUS[x.status]?.[0] || 'gray'}`}>{ST_STATUS[x.status]?.[1] || x.status}</span>
                      <code>{x.id}</code>
                      <span style={{ flex: 1 }}>{x.detail || x.error || ''}</span>
                      <span>{x.ms} ms</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      ) : null}
    </div>
  );
}

// The Design view — the Lab → Dev Console handoff (shared/devOps/builds.js).
// What App Builder synced down (the accepted concept, the accepted user
// stories, the concept's screens), the first build's state, and — after it —
// every story and screen with where it stands here (not built / building /
// built / shipped) and a Build button per row. The same handoffs arrive
// automatically from App Builder (Ready to ship → first build; a story's
// "Build this" → a small build); this view shows those too. Shipping a build
// session stamps the rows and App Builder's board.
const BUILD_STATE = {
  none: ['gray', 'not started'],
  underway: ['blue', 'underway'],
  built: ['green', 'built and deployed'],
  blocked: ['red', 'blocked'],
};
const ROW_STATE = {
  underway: ['blue', 'building'],
  built: ['green', 'built'],
  shipped: ['green', 'shipped'],
};
function BuildPanel({ onOpenSession }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false); // false | true (first build) | story/screen id
  const [notes, setNotes] = useState('');
  const [showStories, setShowStories] = useState(false);
  const load = () => api('/dev/ops/build').then(setData).catch((e) => setErr(e.message));
  useEffect(() => { load(); const t = setInterval(load, 30000); return () => clearInterval(t); }, []);
  const b = data?.build || { state: 'none' };
  const [tone, label] = BUILD_STATE[b.state] || BUILD_STATE.none;
  const canStart = data && b.state !== 'underway' && b.state !== 'built' && (data.concept?.title || data.stories?.count || data.app?.goal);
  const start = async () => {
    setBusy(true); setErr(null);
    try {
      const out = await api('/dev/ops/build', { notes });
      await load();
      if (out.sessionId && !out.alreadyUnderway) onOpenSession(out.sessionId);
    } catch (e) { setErr(e.message); }
    setBusy(false);
  };
  // One story / screen → one small build session.
  const buildOne = async (kind, id) => {
    setBusy(id); setErr(null);
    try {
      const out = await api('/dev/ops/build', kind === 'story' ? { storyIds: [id] } : { screenIds: [id] });
      await load();
      if (out.sessionId && !out.alreadyUnderway) onOpenSession(out.sessionId);
    } catch (e) { setErr(e.message); }
    setBusy(false);
  };
  const backlog = data?.backlog || null;
  const rowState = (r) => (r.build ? ROW_STATE[r.build.state] || ROW_STATE.underway : ['gray', 'not built']);
  const Row = ({ kind, r, text }) => {
    const [t, l] = rowState(r);
    const building = r.build?.state === 'underway';
    return (
      <tr>
        <td style={{ whiteSpace: 'nowrap' }}><span className={`devc-chip ${t}`}>{l}</span></td>
        <td>
          {text}
          {kind === 'story' && r.acceptance?.length ? <div className="devc-dim" style={{ fontSize: 12 }}>{r.acceptance.length} acceptance criteri{r.acceptance.length === 1 ? 'on' : 'a'}{r.status === 'verified' ? ' · verified in a browser' : r.lastVerdict === 'fail' ? ' · failing its last browser run' : ''}</div> : null}
          {r.build?.deployEnv || r.build?.shippedAt || r.build?.deployedAt ? <div className="devc-dim" style={{ fontSize: 12 }}>{r.build.state === 'shipped' ? 'shipped' : 'deployed'}{r.build.deployEnv ? ` to ${r.build.deployEnv}` : ''} {fmtAgo(r.build.shippedAt || r.build.deployedAt)}</div> : null}
        </td>
        <td style={{ whiteSpace: 'nowrap', textAlign: 'right' }}>
          {r.build?.sessionId ? <button className="devc-btn" onClick={() => onOpenSession(r.build.sessionId)}>session</button> : null}
          {r.build?.prUrl ? <> <a href={r.build.prUrl} target="_blank" rel="noreferrer">PR ↗</a></> : null}
          {r.build?.previewUrl ? <> <a href={r.build.previewUrl} target="_blank" rel="noreferrer">preview ↗</a></> : null}
          {' '}
          <button className={`devc-btn ${r.build ? '' : 'primary'}`} disabled={!!busy || building} title={building ? 'A session is building this — ship it to mark it built' : r.build ? 'Start another session for this item' : 'Start a coding-agent session for just this item'} onClick={() => buildOne(kind, r.id)}>{busy === r.id ? 'Starting…' : building ? 'building…' : r.build ? 'Build again' : 'Build this'}</button>
        </td>
      </tr>
    );
  };
  return (
    <div className="devc-panel">
      <div className="devc-card">
        <div className="devc-card-title">
          First build
          <span className={`devc-chip ${tone}`} style={{ marginLeft: 8 }}>{label}</span>
          <span className="devc-dim" style={{ fontWeight: 400 }}>&nbsp;· what App Builder on the control plane handed over, and the session that builds it</span>
        </div>
        {err && <div className="devc-err">{err}</div>}
        {!data && !err && <div className="devc-dim">Loading…</div>}
        {data && (
          <>
            <table className="devc-usage-table" style={{ marginBottom: 10 }}>
              <tbody>
                <tr><td>App</td><td>{data.app.name || '—'}{data.app.goal ? <span className="devc-dim"> — {data.app.goal}</span> : null}</td></tr>
                <tr><td>Concept</td><td>{data.concept?.title ? `"${data.concept.title}" v${data.concept.version || 1}${data.concept.screens?.length ? ` · ${data.concept.screens.length} screens` : ''}` : <span className="devc-dim">none synced — the build goes from the stories and the goal</span>}</td></tr>
                <tr><td>Stories</td><td>{data.stories?.count ? <>{data.stories.count} accepted{data.stories.verified ? ` · ${data.stories.verified} verified in a browser` : ''}{data.stories.ideas ? ` · ${data.stories.ideas} ideas` : ''} <button className="devc-btn" style={{ marginLeft: 6 }} onClick={() => setShowStories(!showStories)}>{showStories ? 'hide' : 'read'}</button></> : <span className="devc-dim">none synced</span>}</td></tr>
                <tr><td>Synced</td><td>{data.app.syncedAt ? `${fmtAgo(data.app.syncedAt)} · bundle ${data.app.bundleHash || ''}` : <span className="devc-dim">never — send the design from App Builder → Ready to ship</span>}</td></tr>
                <tr><td>Build</td><td>
                  {label}
                  {b.requestedAt ? <span className="devc-dim"> · requested {fmtAgo(b.requestedAt)}{data.raw?.requestedBy ? ` by ${data.raw.requestedBy}` : ''}</span> : null}
                  {b.sessionId ? <> · <button className="devc-btn" onClick={() => onOpenSession(b.sessionId)}>open session</button></> : null}
                  {b.prUrl ? <> · <a href={b.prUrl} target="_blank" rel="noreferrer">pull request ↗</a></> : null}
                  {b.previewUrl ? <> · <a href={b.previewUrl} target="_blank" rel="noreferrer">preview ↗</a></> : null}
                  {b.state === 'blocked' && b.reason ? <div className="devc-err" style={{ marginTop: 4 }}>{b.reason}</div> : null}
                </td></tr>
              </tbody>
            </table>
            {showStories && data.stories?.content && <pre className="devc-mono" style={{ maxHeight: 360, overflow: 'auto', whiteSpace: 'pre-wrap' }}>{data.stories.content}</pre>}
            {canStart ? (
              <div style={{ display: 'grid', gap: 6 }}>
                <textarea className="devc-cfg-raw" style={{ minHeight: 56 }} rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Extra wishes for the build (optional)" />
                <div><button className="devc-btn primary" disabled={busy} onClick={start}>{busy ? 'Starting…' : b.state === 'blocked' ? 'Try the build again' : 'Start the first build'}</button></div>
              </div>
            ) : b.state === 'underway' ? (
              <div className="devc-dim">A coding-agent session is building the screens from the brief above and will open a pull request. Preview it from the session's PR & Deploy tab, then deploy — that marks the first build done.</div>
            ) : b.state === 'built' ? (
              <div className="devc-dim">Built and deployed{b.at ? ` ${fmtAgo(b.at)}` : ''}. Ask for specific changes with a new session.</div>
            ) : (
              <div className="devc-dim">Nothing to build from yet. In the studio on nventr.io: App Builder → Lab (accept a concept or write stories), then Ready to ship → "Send to the Dev Console and start the build".</div>
            )}
          </>
        )}
      </div>
      {backlog && (backlog.stories?.length || backlog.screens?.length) ? (
        <div className="devc-card" style={{ marginTop: 12 }}>
          <div className="devc-card-title">
            Stories and screens
            <span className="devc-dim" style={{ fontWeight: 400 }}>&nbsp;· {backlog.stories.filter((s) => s.build && s.build.state !== 'underway').length} of {backlog.stories.length} stories built here · each row is one small build; shipping the session marks it built on App Builder's board too</span>
          </div>
          {backlog.stories.length ? (
            <table className="devc-usage-table" style={{ marginBottom: 10 }}>
              <tbody>
                {backlog.stories.map((s) => <Row key={s.id} kind="story" r={s} text={<>{s.title ? <strong>{s.title}</strong> : null}{s.title ? <br /> : null}As a {s.asA || 'user'}, I want {s.iWant}{s.soThat ? <span className="devc-dim"> so that {s.soThat}</span> : null}</>} />)}
              </tbody>
            </table>
          ) : <div className="devc-dim" style={{ marginBottom: 10 }}>No accepted stories synced yet — App Builder → Lab → Stories, accept some, then Ready to ship sends them here.</div>}
          {backlog.screens.length ? (
            <>
              <div className="devc-dim" style={{ margin: '4px 0' }}>Concept screens</div>
              <table className="devc-usage-table">
                <tbody>
                  {backlog.screens.map((x) => <Row key={x.id} kind="screen" r={x} text={<><strong>{x.name}</strong>{x.purpose ? <span className="devc-dim"> — {x.purpose}</span> : null}{x.primaryAction ? <div className="devc-dim" style={{ fontSize: 12 }}>primary action: {x.primaryAction}</div> : null}</>} />)}
                </tbody>
              </table>
            </>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

const TABS = [
  ['chat', 'Chat'],
  ['activity', 'Activity'],
  ['terminal', 'Terminal'],
  ['pr', 'PR & Deploy'],
  ['preview', 'Preview'],
  ['artifacts', 'Artifacts'],
  ['usage', 'Usage'],
  ['details', 'Details'],
];

export default function DevConsole() {
  const { internal, role } = useClaims();
  const [selected, setSelected] = useState(null); // session id | null (new)
  // The dock's voice bridge follows the open session (dev/liveDocs.js).
  useEffect(() => { setActiveDevSession(selected); return () => setActiveDevSession(null); }, [selected]);
  const [view, setView] = useState('sessions'); // sessions | config | access | ops | agents
  // Phone posture (≤699px, DevConsole.css): one pane at a time — the rail is
  // a sheet behind the ☰ button, the config row folds behind "Options", and
  // the composer rides above the soft keyboard (visualViewport → --devc-kb).
  // Deep link from a push notification (sw.js → /dev?session=<id>).
  useEffect(() => {
    const id = new URLSearchParams(window.location.search).get('session');
    if (id) { setSelected(id); setView('sessions'); window.history.replaceState(null, '', window.location.pathname); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const [pushState, setPushState] = useState(''); // '' | 'on' | 'unsupported' | 'no-key' (web push half of notifications)
  const phone = usePhone();
  const narrow = useNarrow(); // ≤900px: book band + phone — review surfaces lead with the summary
  const [railOpen, setRailOpen] = useState(false);
  const [optionsOpen, setOptionsOpen] = useState(false);
  useEffect(() => { if (phone) setRailOpen(false); }, [selected, view, phone]);
  useEffect(() => {
    if (!phone || !window.visualViewport) return undefined;
    const vv = window.visualViewport;
    const root = document.documentElement;
    const apply = () => {
      const kb = Math.max(0, window.innerHeight - vv.height - vv.offsetTop);
      root.style.setProperty('--devc-kb', `${Math.round(kb)}px`);
    };
    apply();
    vv.addEventListener('resize', apply);
    vv.addEventListener('scroll', apply);
    return () => { vv.removeEventListener('resize', apply); vv.removeEventListener('scroll', apply); root.style.removeProperty('--devc-kb'); };
  }, [phone]);
  const [ideRequest, setIdeRequest] = useState(null); // { branch, sessionId } — "Open in IDE" from a session
  const [graphFocus, setGraphFocus] = useState(null); // graph id the Graphs view opens on
  const [tab, setTab] = useState('chat');
  const [prompt, setPrompt] = useState('');
  const [mode, setMode] = useState('auto'); // 'auto' (console decides) | SDK AgentModeOption
  const [routingMode, setRoutingMode] = useState(''); // conversation profile id (shared/devModes.js); '' = server default
  const [repoId, setRepoId] = useState('default'); // dev.repos entry a NEW session works (foundry: kit / plane)
  const [model, setModel] = useState(''); // '' = account default / keep current
  const [modelParams, setModelParams] = useState({}); // paramId -> value for the picked model
  const [agentOpts, setAgentOpts] = useState(DEFAULT_AGENT_OPTIONS);
  const [files, setFiles] = useState([]); // [{ file, relPath, pin? }] — attachments incl. whole folders
  const [refsStamp, setRefsStamp] = useState(0); // bumps when project references change (ReferencesStrip refetch)
  const [upload, setUpload] = useState(null); // live upload progress for the tray
  const [notice, setNotice] = useState(''); // non-fatal attachment note (skipped folders, unreachable pasted image)
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [setup, setSetup] = useState(null); // /dev/status result
  const [setupFailed, setSetupFailed] = useState(false); // /dev/status request itself failed (network/CORS/backend down)
  const [statusBusy, setStatusBusy] = useState(false); // model-catalog Retry in flight
  const [showThinking, setShowThinking] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all'); // all | live | finished | failed
  const [editingTitle, setEditingTitle] = useState(null); // string while editing
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [termFilter, setTermFilter] = useState('all'); // all | agent | deploy
  const [autoScroll, setAutoScroll] = useState(true);
  const [dragging, setDragging] = useState(false);
  const [retryFamily, setRetryFamily] = useState(null); // groups retries into one thread
  const [reportEnvelope, setReportEnvelope] = useState(null); // element-picker capture
  const [activeTemplate, setActiveTemplate] = useState(null); // structured chip id
  const [fieldValues, setFieldValues] = useState({});
  // Per-stage model for plan sessions: the execution run can use a different
  // (typically cheaper) model than the planning run. '' = keep the plan model.
  const [execModel, setExecModel] = useState('');
  const [execParams, setExecParams] = useState({});
  const [settings, setSettings] = useState(loadSettings);
  useEffect(() => {
    // Re-sync a stored subscription on load (a new VAPID key or a cleared
    // browser leaves the row stale); never prompts.
    if (!settings.notify || !pushSupported() || Notification.permission !== 'granted') return;
    enablePush(api).then((r) => setPushState(r.ok ? 'on' : r.reason)).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const dragDepth = useRef(0); // dragenter/leave fire per child — count nesting
  const fileInput = useRef(null);
  const folderInput = useRef(null);
  const uploadAbort = useRef(null);
  const chatEnd = useRef(null);
  const termEnd = useRef(null);
  const composerInput = useRef(null);
  const dictateBase = useRef(''); // typed draft when dictation started (DictateButton)
  const prevStatuses = useRef(new Map()); // notification edge detection
  const sweptRef = useRef(new Set()); // auto-archive one-shot guard

  const saveSettings = (patch) => setSettings((prev) => {
    const next = { ...prev, ...patch };
    try { localStorage.setItem(SETTINGS_KEY, JSON.stringify(next)); } catch { /* private mode */ }
    return next;
  });

  const sessions = useOrdered(internal ? 'devSessions' : null, 'createdAt', 'desc', 50);
  const session = useDoc(selected ? `devSessions/${selected}` : null);
  const events = useOrdered(selected ? `devSessions/${selected}/events` : null, 'seq', 'asc', 1000);
  const jobLogs = useOrdered(session?.deployJobId ? `devJobs/${session.deployJobId}/logs` : null, 'seq', 'asc', 1000);

  const running = session && LIVE.includes(session.status);
  useTick(!!running || sessions.some((s) => LIVE.includes(s.status)));

  useEffect(() => {
    if (!internal) return;
    api('/dev/status')
      .then((s) => { setSetup(s); setSetupFailed(false); })
      .catch(() => { setSetup(null); setSetupFailed(true); });
  }, [internal]);

  // Manual model-catalog retry: ?refresh=1 bypasses the backend's status
  // cache so a transient Cursor API failure doesn't leave the picker empty.
  // Also the recovery path when /dev/status itself was unreachable.
  const refreshStatus = async () => {
    setStatusBusy(true);
    try {
      setSetup(await api('/dev/status?refresh=1'));
      setSetupFailed(false);
    } catch {
      setSetupFailed(true); // keep whatever setup we had
    } finally {
      setStatusBusy(false);
    }
  };

  // Element-picker handoff: consume a stashed report (envelope + screenshot)
  // and pre-fill the composer with the captured facts.
  useEffect(() => {
    if (!internal) return;
    const report = takeReport();
    if (!report) return;
    const el = report.envelope?.element;
    if (el) {
      setPrompt(
        `On ${report.envelope.route}, the element below needs a change: <describe expected vs. actual>\n\n`
        + `Element: ${el.component ? `${el.component} — ` : ''}${el.domPath}\n`
        + `Size: ${el.boundingBox.width}×${el.boundingBox.height}px, font-size ${el.computedStyles['font-size']}`
      );
    }
    setReportEnvelope(report.envelope || null);
    if (report.screenshot) {
      fetch(report.screenshot)
        .then((r) => r.blob())
        .then((blob) => setFiles([new File([blob], `report-${Date.now()}.jpg`, { type: 'image/jpeg' })]))
        .catch(() => {});
    }
    setSelected(null);
  }, [internal]);

  // Reset per-session UI state on switch. Model/params go back to "no
  // override" — follow-up sends keep the conversation's current (sticky)
  // selection unless the user picks one.
  useEffect(() => {
    setEditingTitle(null); setConfirmDelete(false); setError(null);
    setModel(''); setModelParams({});
    setExecModel(''); setExecParams({});
    setActiveTemplate(null); setFieldValues({});
  }, [selected]);

  // Follow the session's conversation mode (last mode a send used).
  useEffect(() => {
    setMode((selected && session?.mode) || 'auto');
  }, [selected, session?.mode]);

  // Restore the last-used mode/model whenever the NEW-session composer is
  // shown and the live catalog is known. restoreModelPick() re-validates
  // against the catalog: a model that is no longer available is dropped
  // (falls back to the account default) and stale param values reset to that
  // model's defaults. Runs after the reset effects above (definition order),
  // so the restore wins on the new-session composer only.
  useEffect(() => {
    if (selected || !setup?.models?.length) return;
    const stored = loadStoredConfig();
    if (stored.mode && (setup.modes || FALLBACK_MODES).includes(stored.mode)) setMode(stored.mode);
    const profiles = setup.router?.modes || [];
    if (profiles.length) setRoutingMode(profiles.some((m) => m.id === stored.routingMode) ? stored.routingMode : (profiles.some((m) => m.id === setup.router?.defaultMode) ? setup.router.defaultMode : profiles[0].id));
    const pick = restoreModelPick(setup.models, stored);
    if (pick) { setModel(pick.id); setModelParams(pick.picks); }
    else { setModel(''); setModelParams({}); } // stored model vanished — account default
  }, [selected, setup]);

  const modelDef = useMemo(
    () => setup?.models?.find((m) => m.id === model) || null,
    [setup, model]
  );

  // The execution-model override is a per-send SendOptions.model, which is
  // sticky — follow-ups on the executed session keep the execution model.
  const execModelDef = useMemo(
    () => setup?.models?.find((m) => m.id === execModel) || null,
    [setup, execModel]
  );
  const pickExecModel = (id) => {
    setExecModel(id);
    setExecParams(defaultParamsFor(setup?.models?.find((m) => m.id === id)));
  };

  // Plan review state: the plan events a plan-mode run wrote (a Revise
  // follow-up appends a new one — the LATEST is what executes), and the
  // model Approve & Execute will actually send (the "Execute with" override,
  // else the session's sticky model), resolved against the live catalog so
  // an invalid pick is caught at review time instead of at execution time.
  const planEvents = useMemo(() => events.filter((e) => e.type === 'plan'), [events]);
  // Thumbs render on the newest result only — one verdict per session.
  const lastResultId = useMemo(() => { const r = events.filter((e) => e.type === 'result'); return r.length ? r[r.length - 1].id : null; }, [events]);
  const latestPlan = planEvents.length ? planEvents[planEvents.length - 1] : null;
  const planPending = session?.mode === 'plan' && !session?.planExecuted;
  const execTargetRaw = execModel || session?.model || '';
  const execTargetDef = useMemo(
    () => findCatalogModel(setup?.models, execTargetRaw),
    [setup, execTargetRaw]
  );
  // Only block when the catalog is loaded AND positively lacks the model —
  // a missing catalog must not freeze approvals (Cursor stays the authority).
  const execTargetInvalid = !!(setup?.models?.length && execTargetRaw && !execTargetDef);
  const execTargetId = execTargetDef?.id || execTargetRaw;
  const canApprovePlan = !!latestPlan?.text && !execTargetInvalid;

  // Pick handlers persist the choice (new-session composer only — follow-up
  // overrides are per-conversation, not a browser preference). A model change
  // resets params to that model's defaults (catalog default variant, else
  // each parameter's first allowed value).
  const pickMode = (m) => {
    setMode(m);
    if (!selected) saveStoredConfig({ mode: m });
  };
  const pickModel = (id) => {
    const picks = defaultParamsFor(setup?.models?.find((m) => m.id === id));
    setModel(id);
    setModelParams(picks);
    if (!selected) saveStoredConfig({ model: id || null, params: id ? picks : null });
  };
  const pickParams = (picks) => {
    setModelParams(picks);
    if (!selected) saveStoredConfig({ params: picks });
  };

  // Browser notifications: fire when a session leaves a live state while the
  // console is open (background tab included). Edge-triggered off the
  // realtime session list — no polling.
  useEffect(() => {
    const prev = prevStatuses.current;
    sessions.forEach((s) => {
      const was = prev.get(s.id);
      if (
        was && LIVE.includes(was) && !LIVE.includes(s.status)
        && settings.notify && typeof Notification !== 'undefined' && Notification.permission === 'granted'
      ) {
        const [label] = CHIP[s.status] || [s.status];
        try {
          const n = new Notification(`${s.title || 'Dev session'}: ${label}`, {
            tag: `devc-${s.id}`,
            body: s.prUrl ? 'A pull request is ready to review.' : undefined,
          });
          n.onclick = () => { window.focus(); setSelected(s.id); n.close(); };
        } catch { /* notification constructor can throw on some platforms */ }
      }
      prev.set(s.id, s.status);
    });
  }, [sessions, settings.notify]);

  // Auto-archive failed sessions older than 24h (opt-in, client-side sweep on
  // the live list — no background job needed).
  useEffect(() => {
    if (!settings.autoArchive) return;
    sessions.forEach((s) => {
      const age = Date.now() - (s.updatedAt || s.createdAt || Date.now());
      if (!s.archived && FAILED.includes(s.status) && age > 24 * 3600 * 1000 && !sweptRef.current.has(s.id)) {
        sweptRef.current.add(s.id);
        api(`/dev/sessions/${s.id}/archive`, {}).catch(() => sweptRef.current.delete(s.id));
      }
    });
  }, [sessions, settings.autoArchive]);

  // Terminal feed: agent shell output + deploy runner logs, chronological.
  const terminal = useMemo(() => {
    const rows = [];
    if (termFilter !== 'deploy') {
      events.forEach((e) => {
        if (e.type !== 'tool') return;
        const mark = e.status === 'running' ? '▶' : (e.status === 'error' ? '✗' : '✔');
        const arg = toolArg(e);
        if (e.name === 'shell') {
          rows.push({ ts: e.ts, text: `${mark} $ ${arg || e.args || ''}${e.result ? `\n${e.result}` : ''}` });
        } else {
          // One line per call: "✔ read src/App.js · 54 lines". Results that
          // are more than a summary go on their own lines.
          const r = String(e.result || '');
          const inline = r && !r.includes('\n') && r.length <= 120;
          rows.push({ ts: e.ts, text: `${mark} ${e.name}${arg ? ` ${arg}` : ''}${inline ? ` · ${r}` : ''}${r && !inline ? `\n${r}` : ''}` });
        }
      });
    }
    if (termFilter !== 'agent') {
      jobLogs.forEach((l) => rows.push({ ts: l.ts, text: l.text, deploy: true }));
    }
    return rows.sort((a, b) => a.ts - b.ts);
  }, [events, jobLogs, termFilter]);

  const lastEventTs = events.length ? events[events.length - 1].ts : session?.updatedAt;
  // The newest streaming thinking/assistant fragment, shown under the
  // working row so a long tool-less stretch still reads as progress.
  const liveThought = useMemo(() => {
    if (!running) return '';
    const last = [...events].reverse().find((e) => e.type === 'thinking' || e.type === 'assistant');
    if (!last || Date.now() - last.ts > 60000) return '';
    const t = String(last.text || '').trim().replace(/\s+/g, ' ');
    return t.length > 160 ? `…${t.slice(-160)}` : t;
  }, [events, running]);

  // Liveness = newest of the last event, the runner heartbeat and the last
  // tool tally — a quiet agent mid-tool-call still counts as alive.
  const liveTs = Math.max(lastEventTs || 0, session?.heartbeatAt || 0, session?.updatedAt || 0) || null;
  const stalled = !!(running && liveTs && Date.now() - liveTs > STALL_MS && session?.waitingFor !== 'capacity');

  // Chat view: fold runs of consecutive tool events into one group row so
  // the conversation stays readable while still showing what the agent did.
  const groupedEvents = useMemo(() => {
    const out = [];
    for (const e of events) {
      if (e.type === 'tool') {
        const prev = out[out.length - 1];
        if (prev && prev.type === 'toolgroup') prev.items.push(e);
        else out.push({ type: 'toolgroup', id: `tg-${e.id}`, ts: e.ts, items: [e] });
      } else {
        out.push(e);
      }
    }
    return out;
  }, [events]);

  useEffect(() => { if (autoScroll) chatEnd.current?.scrollIntoView({ block: 'end' }); }, [events.length, autoScroll, tab]);
  useEffect(() => { if (autoScroll) termEnd.current?.scrollIntoView({ block: 'end' }); }, [terminal.length, autoScroll, tab]);

  // Filtered sidebar list, with retry families collapsed to their newest
  // attempt (sessions arrive newest-first) — no more stacked failure cards.
  const visibleSessions = useMemo(() => {
    const q = search.trim().toLowerCase();
    const filtered = sessions.filter((s) => {
      if (q && !(s.title || '').toLowerCase().includes(q)) return false;
      // Archived sessions live under their own filter; everywhere else they
      // stay out of the way.
      if (statusFilter === 'archived') return !!s.archived;
      if (s.archived) return false;
      if (statusFilter === 'live') return LIVE.includes(s.status);
      if (statusFilter === 'finished') return s.status === 'finished';
      if (statusFilter === 'failed') return FAILED.includes(s.status);
      return true;
    });
    const byFamily = new Map();
    const byGraph = new Map();
    const out = [];
    for (const s of filtered) {
      // Graph nodes (workers / verifiers / judges) fold into one row per
      // graph — the Graphs view shows them; the root session keeps its row.
      if (s.graphId && ['worker', 'verifier', 'judge'].includes(s.kind)) {
        if (byGraph.has(s.graphId)) { byGraph.get(s.graphId).nodes += 1; continue; }
        const row = { ...s, id: `graph:${s.graphId}`, graphRow: true, graphId: s.graphId, nodes: 1, attempts: 1, title: s.graphTitle || `Graph ${s.graphId.slice(0, 6)}` };
        byGraph.set(s.graphId, row);
        out.push(row);
        continue;
      }
      const fam = s.familyId || s.id;
      if (byFamily.has(fam)) { byFamily.get(fam).attempts += 1; continue; }
      const row = { ...s, attempts: 1 };
      byFamily.set(fam, row);
      out.push(row);
    }
    return out;
  }, [sessions, search, statusFilter]);

  // All attempts in the selected session's family (oldest first), for the
  // attempt strip above the tabs.
  const familyAttempts = useMemo(() => {
    if (!session) return [];
    const fam = session.familyId || selected;
    return sessions.filter((s) => (s.familyId || s.id) === fam).sort((a, b) => a.createdAt - b.createdAt);
  }, [sessions, session, selected]);

  if (!internal) {
    return (
      <div className="devc-gate">
        <h2>Dev Console</h2>
        <p>This page is limited to internal accounts. Ask an admin to grant the <code>internal: true</code> custom claim.</p>
      </div>
    );
  }

  // ---- actions ------------------------------------------------------------------
  // A structured template counts as content once any of its fields is filled.
  const templateReady = !selected && activeTemplate
    && Object.values(fieldValues).some((v) => (v || '').trim());
  const canSend = !!prompt.trim() || templateReady;

  const send = async () => {
    if (!canSend || busy) return;
    // Compile the active template's fields plus any free text into the final
    // prompt — the structured part leads, extra context follows.
    let text = prompt.trim();
    if (!selected && activeTemplate) {
      const tpl = TEMPLATES.find((t) => t.id === activeTemplate);
      if (tpl) text = compileTemplate(tpl, fieldValues) + (text ? `\n\nAdditional context: ${text}` : '');
    }
    setBusy(true); setError(null);
    try {
      // Attachments: screenshots → SDK image blocks, documents → extracted
      // text, folders/overflow → a data package (manifest the backend reads,
      // staged in the runner's workspace). Small files go one presigned PUT
      // each; big ones ride the resumable multipart path.
      let images = []; let docs = []; let pkg = null;
      if (files.length) {
        if (!isUploadConfigured()) throw new Error('Upload API not configured');
        const ctl = new AbortController();
        uploadAbort.current = ctl;
        const out = await uploadAttachments(files, {
          prefix: `items/dev/${selected || 'new'}/`,
          signal: ctl.signal,
          onProgress: (p) => setUpload({ ...p, onCancel: () => ctl.abort() }),
        });
        uploadAbort.current = null;
        setUpload(null);
        images = out.images; docs = out.files; pkg = out.package;
        if (out.failures?.length) setNotice(`${out.failures.length} file${out.failures.length > 1 ? 's' : ''} failed to upload and were left out (${out.failures[0].path}: ${out.failures[0].error}).`);
        // "Keep for this project": pin the uploaded copies as project
        // references so every later session on this repo gets them.
        const pinPaths = new Set(files.filter((e) => e.pin).map((e) => e.relPath));
        const pins = (out.uploaded || []).filter((u) => pinPaths.has(u.relPath));
        if (pins.length) {
          const forRepo = selected ? (session?.repoRef?.id || 'default') : (repoId || 'default');
          await api('/dev/references', { repoId: forRepo, items: pins.map(({ key, name, type, size, kind }) => ({ key, name, type, size, kind })) })
            .then(() => setRefsStamp((n) => n + 1))
            .catch((e) => setNotice(`Sent, but the files could not be kept for the project: ${e.message}`));
        }
      }
      // Model override carries the per-model params (effort level etc.);
      // params always ride along explicitly — omitted params silently fall
      // back to each parameter's first allowed value on Cursor's side.
      const modelPayload = model
        ? { model, modelParams: buildParamsArray(modelDef, modelParams) }
        : {};
      if (selected) {
        // `mode` follows the session's conversation mode, so Revise
        // follow-ups on an unexecuted plan session stay in plan mode.
        await api(`/dev/sessions/${selected}/messages`, {
          prompt: text, images, files: docs, ...(pkg ? { package: pkg } : {}), mode: mode === 'auto' ? (session?.mode || 'agent') : mode, ...modelPayload,
        });
      } else {
        // New sessions carry the context envelope (context.json v1): route,
        // build SHA, viewport, scrubbed console/network ring buffer, and any
        // element-picker capture. AGENTS.md tells agents to parse it first.
        const envelope = reportEnvelope || buildEnvelope();
        // No explicit title: the backend auto-titles from the prompt intent.
        const out = await api('/dev/sessions', {
          prompt: text + envelopeBlock(envelope),
          images,
          files: docs,
          ...(pkg ? { package: pkg } : {}),
          mode,
          ...(routingMode ? { modeId: routingMode } : {}),
          template: activeTemplate || '',
          ...(repoId && repoId !== 'default' ? { repoId } : {}),
          ...modelPayload,
          agentOptions: agentOptionsPayload(agentOpts),
          ...(retryFamily ? { familyId: retryFamily } : {}),
        });
        setSelected(out.id);
        setTab('chat');
      }
      setPrompt('');
      setFiles([]);
      setRetryFamily(null);
      setReportEnvelope(null);
      setActiveTemplate(null);
      setFieldValues({});
    } catch (e) {
      uploadAbort.current = null;
      setUpload(null);
      setError(e?.name === 'AbortError' ? 'Upload cancelled — your files are still attached.' : e.message);
    } finally {
      setBusy(false);
    }
  };

  // Answers to a ```questions form go back as the next turn (same mode).
  const answerQuestions = async (ev, answers) => {
    if (!selected || busy) return;
    setBusy(true); setError(null);
    try {
      const lines = (ev.questions || []).map((q) => {
        const a = answers[q.id];
        const v = Array.isArray(a) ? a.join('; ') : String(a ?? '').trim();
        return `- ${q.question}\n  → ${v || '(no answer — use your recommendation)'}`;
      });
      await api(`/dev/sessions/${selected}/messages`, {
        prompt: `Answers to your questions:\n\n${lines.join('\n')}\n\nContinue with these decisions.`,
        answers: { questionsEventId: ev.id, ...answers },
        mode: session?.mode || 'agent',
      });
    } catch (e) { setError(e.message); } finally { setBusy(false); }
  };

  // Plan approval flow: Approve & Execute sends the canned execute follow-up
  // in agent mode; Revise focuses the composer (still plan mode); Discard
  // archives the session.
  const approvePlan = async () => {
    if (!canApprovePlan) return;
    setBusy(true); setError(null);
    try {
      await api(`/dev/sessions/${selected}/messages`, {
        prompt: EXECUTE_PROMPT,
        mode: 'agent',
        ...(execModel ? { model: execModel, modelParams: buildParamsArray(execModelDef, execParams) } : {}),
      });
    } catch (e) { setError(e.message); } finally { setBusy(false); }
  };
  // The planning turn produced no plan card: ask for one explicitly (plan
  // mode stays on, nothing is edited).
  const requestPlan = async () => {
    if (!selected || busy) return;
    setBusy(true); setError(null);
    try {
      await api(`/dev/sessions/${selected}/messages`, {
        prompt: 'Write the complete implementation plan for my request now — every phase in full, with a one-line summary of how many phases there are. Do not change any files.',
        mode: 'plan',
      });
    } catch (e) { setError(e.message); } finally { setBusy(false); }
  };
  const discardPlan = async () => {
    try {
      await api(`/dev/sessions/${selected}/archive`, {});
      setSelected(null);
    } catch (e) { setError(e.message); }
  };

  const toggleArchive = async () => {
    try {
      await api(`/dev/sessions/${selected}/${session?.archived ? 'unarchive' : 'archive'}`, {});
      if (!session?.archived) setSelected(null);
    } catch (e) { setError(e.message); }
  };

  // The Ship strip's non-deploy actions land in the chat: answer the open
  // questions, approve the plan, or write the follow-up. Ship / Retry go
  // straight to POST /dev/ship from the strip itself.
  const scrollChatTo = (selector) => {
    setTimeout(() => document.querySelector(selector)?.scrollIntoView({ behavior: 'smooth', block: 'center' }), 60);
  };
  const shipAction = (action) => {
    if (action === 'review') {
      // The merge gate: a review graph (verifiers + a judge from another
      // model family) on this session's PR; the Graphs view follows it.
      api('/dev/graphs', { template: 'review', rootId: selected })
        .then((out) => { setGraphFocus(out.id); setView('graphs'); })
        .catch((e) => setError(e.message));
      return;
    }
    setTab('chat');
    if (action === 'answer') scrollChatTo('.devc-questions:not(.is-answered)');
    else if (action === 'approve') { if (canApprovePlan && !busy) approvePlan(); else scrollChatTo('.devc-approve'); }
    else if (action === 'followup') composerInput.current?.focus();
  };

  // Notifications: tab notifications when the console is open, and — where
  // the browser and the instance support it — web push (dev/push.js) so the
  // phone hears about questions, plans, PRs and ships while the app is
  // closed. One switch; the push half degrades silently.
  const enableNotifications = async (on) => {
    if (!on) { saveSettings({ notify: false }); setPushState(''); disablePush(api).catch(() => {}); return; }
    if (typeof Notification === 'undefined') { setError('This browser does not support notifications.'); return; }
    const perm = Notification.permission === 'granted' ? 'granted' : await Notification.requestPermission();
    if (perm !== 'granted') { setError('Notifications are blocked for this site — allow them in the browser settings.'); return; }
    saveSettings({ notify: true });
    try {
      const r = await enablePush(api);
      setPushState(r.ok ? 'on' : r.reason);
    } catch (e) { setPushState('error'); setError(`Push setup failed: ${e.message}`); }
  };

  const stop = async () => {
    try { await api(`/dev/sessions/${selected}/cancel`, {}); } catch (e) { setError(e.message); }
  };

  // Retry: start a NEW session with this session's original prompt (failed
  // agents never started, so there is nothing to resume). The retry joins the
  // same familyId so attempts collapse into one sidebar thread.
  const retry = () => {
    const first = events.find((e) => e.type === 'user');
    // Strip a prior context envelope — send() attaches a fresh one.
    const raw = (first?.text || session?.title || '').split('\n---\ncontext.json')[0].trimEnd();
    setRetryFamily(session?.familyId || selected);
    setSelected(null);
    setPrompt(raw);
    setTab('chat');
  };

  const rename = async () => {
    const title = (editingTitle || '').trim();
    setEditingTitle(null);
    if (!title || title === session?.title) return;
    try { await api(`/dev/sessions/${selected}/rename`, { title }); } catch (e) { setError(e.message); }
  };

  const remove = async () => {
    setConfirmDelete(false);
    try {
      await api(`/dev/sessions/${selected}/delete`, {});
      setSelected(null);
    } catch (e) {
      setError(e.message);
    }
  };

  // Attach from any source (picker, paste, drop, folder pick). Any type;
  // whole folders keep their structure; vendored trees are skipped; limits
  // come from live config (dev.attachments) via /dev/status.
  const addEntries = (incoming, note = '') => {
    const { kept, skipped } = applySkips(incoming || [], setup?.attachments?.skip);
    setFiles((prev) => {
      const merged = [...prev];
      const seen = new Set(prev.map((e) => `${e.relPath}\u0000${e.file.size}`));
      for (const en of kept) {
        const sig = `${en.relPath}\u0000${en.file.size}`;
        if (!seen.has(sig)) { seen.add(sig); merged.push(en); }
      }
      const problem = checkLimits(merged, setup?.attachments);
      if (problem) { setError(problem); return prev; }
      return merged;
    });
    const notes = [note, skipped ? `${skipped} file${skipped > 1 ? 's' : ''} skipped (node_modules, .git, build output, empty files).` : ''].filter(Boolean);
    if (notes.length) setNotice(notes.join(' '));
  };
  const addFiles = (fileList) => addEntries(collectFromFileList(fileList));
  const removeEntry = (i) => setFiles((prev) => prev.filter((_, j) => j !== i));
  const togglePin = (i) => setFiles((prev) => prev.map((e, j) => (j === i ? { ...e, pin: !e.pin } : e)));
  const removeFolder = (name) => setFiles((prev) => prev.filter((e) => e.relPath.split('/')[0] !== name || !e.relPath.includes('/')));

  // Paste anywhere in the composer (textarea, template fields, tray):
  // screenshots, copied files, or an image inside pasted HTML.
  const onPaste = async (e) => {
    const cd = e.clipboardData;
    const hasFile = Array.from(cd?.items || []).some((it) => it.kind === 'file') || cd?.files?.length > 0;
    const hasHtmlImg = !hasFile && /<img[^>]+src=/i.test(cd?.getData?.('text/html') || '');
    if (!hasFile && !hasHtmlImg) return; // plain text paste — let the field handle it
    e.preventDefault();
    const out = await collectFromPaste(e);
    if (out.entries.length) addEntries(out.entries, out.note);
    else if (out.text) { setPrompt((p) => `${p}${p ? '\n' : ''}${out.text}`); if (out.note) setNotice(out.note); }
    else if (out.note) setNotice(out.note);
  };

  // Folder picker: File System Access API where available, else the hidden
  // webkitdirectory input (set imperatively — React drops the attribute).
  const pickFolder = async () => {
    const out = await pickFolderEntries();
    if (out === null) { folderInput.current?.click(); return; }
    if (out.length) addEntries(out);
  };
  const onDragEnter = (e) => {
    if (!Array.from(e.dataTransfer?.types || []).includes('Files')) return;
    e.preventDefault();
    dragDepth.current += 1;
    setDragging(true);
  };
  const onDragLeave = (e) => {
    e.preventDefault();
    dragDepth.current = Math.max(0, dragDepth.current - 1);
    if (dragDepth.current === 0) setDragging(false);
  };
  const onDrop = async (e) => {
    e.preventDefault();
    dragDepth.current = 0;
    setDragging(false);
    addEntries(await collectFromDataTransfer(e.dataTransfer));
  };

  const cost = costUsd(session?.usage);
  const elapsed = running && session?.createdAt ? Date.now() - (session.startedAt || session.createdAt) : session?.durationMs;

  // ---- render -------------------------------------------------------------------
  return (
    <div className={`devc${phone ? ' is-phone' : ''}${railOpen ? ' rail-open' : ''}`}>
      {phone && railOpen && <div className="devc-rail-backdrop" onClick={() => setRailOpen(false)} aria-hidden="true" />}
      <aside className="devc-side" aria-hidden={phone && !railOpen ? 'true' : undefined}>
        <div className="devc-side-top">
          <button className="devc-btn primary devc-new" onClick={() => { setSelected(null); setPrompt(''); setView('sessions'); setRailOpen(false); }}>
            + New session
          </button>
          <button
            className={`devc-btn devc-gear${settingsOpen ? ' active' : ''}`}
            title="Console settings: notifications and auto-archive"
            aria-label="Console settings"
            onClick={() => setSettingsOpen(!settingsOpen)}
          >
            ⚙
          </button>
        </div>
        {/* Operator views — session-independent surfaces (config / access / ops). */}
        <div className="devc-views">
          {[
            ['config', 'Config', 'Live settings the backend re-reads within a minute — no deploy'],
            ['access', 'Access', 'Who can sign in and what they can do (admin / product / tester / waitlist)'],
            ['secrets', 'Secrets', 'API keys this instance uses — write-only; paste KEY=value lines, mirrored to the UAT twin'],
            ['ops', 'Ops', 'Health, logs and recent service events for this environment'],
            ['ecosystem', 'Ecosystem', 'Live picture of this app: services, monitor, security, twin, control plane'],
            ['agents', 'Agents', 'Eval runs and the policy denial trail for the embedded agents'],
            ['lab', 'Lab', 'Browser-test reports of this app from the control plane\'s Lab'],
            ['build', 'Design', 'What App Builder handed over (concept, stories, screens), which of it is built or shipped here, and a Build button per story'],
            ['selftest', 'Self-test', 'The derived feature suite: routes, entities, tools, surfaces, model and synced design screens — pass/fail per case'],
            ['routing', 'Routing', 'Smart model routing: engine, tier → model map, frontier cap, the conversation profiles and this month\'s ledger'],
            ['infra', 'Infra', 'Live map of the cloud resources this app runs on, estimated vs billed cost per resource, and add-on blueprints the agent can provision'],
            ['graphs', 'Graphs', 'Graphs of agent sessions: fan a task list out to parallel workers, run an arena of model families on one ask, or review a PR with independent verifiers and a judge'],
            ['routines', 'Routines', 'The outer loop: the trust-ladder level and the routines that open sessions from causes — failing self-tests, verify loops that gave up, thumbs-downs, brain notes, plane reports, the weekly gardener'],
            ['ide', 'IDE', 'A full Eclipse Theia workspace on the repo: browse, edit, diff, commit and push, then hand the branch to the Ship strip'],
          ].map(([v, label, tip]) => (
            <button
              key={v}
              className={`devc-filter${view === v ? ' active' : ''}${['ide', 'ecosystem'].includes(v) ? ' devc-desktop-only' : ''}`}
              title={tip}
              onClick={() => setView(view === v ? 'sessions' : v)}
            >
              {label}
            </button>
          ))}
        </div>
        {settingsOpen && (
          <div className="devc-settings">
            <label>
              <input
                type="checkbox"
                checked={settings.notify}
                onChange={(e) => enableNotifications(e.target.checked)}
              />
              Notify when a session needs you or finishes
              {settings.notify && pushState === 'on' && <span className="devc-chip green" title="This phone gets a push even with the app closed">push on</span>}
              {settings.notify && pushState === 'unsupported' && <span className="devc-chip gray" title="Add the app to the home screen for push while it is closed">tab only</span>}
              {settings.notify && pushState === 'no-key' && <span className="devc-chip gray" title="Push keys are not set on this instance (scripts/push-keys.mjs)">tab only</span>}
            </label>
            {settings.notify && pushState === 'on' && (
              <button type="button" className="devc-filter" onClick={() => api('/dev/push/test', {}).then((r) => setNotice(r.sent ? 'Test push sent.' : 'No subscription found for this account.')).catch((e) => setError(e.message))}>Send a test push</button>
            )}
            <label>
              <input
                type="checkbox"
                checked={settings.autoArchive}
                onChange={(e) => saveSettings({ autoArchive: e.target.checked })}
              />
              Auto-archive failed sessions after 24h
            </label>
          </div>
        )}
        {setupFailed && !setup && (
          <div className="devc-warn">
            Can&apos;t reach the Dev Console API from this origin — status, the model list, and new
            agent runs will all fail. On a PR <em>preview</em> URL this means the live backend
            doesn&apos;t allow preview-channel origins yet: merge &amp; deploy the latest backend
            (<code>backend-fast</code>) from the production console, then Retry here.
          </div>
        )}
        {setup?.repo && (
          <a className="devc-repo-link" href={setup.repo} target="_blank" rel="noopener noreferrer" title={`The layer repo agents work in — opens on your git provider\n${setup.repo}`}>
            <span className="devc-dim">⎇ Layer repo</span>
            <strong>{repoLabel(setup.repo)}</strong>
          </a>
        )}
        {setup?.key && (
          <div className={`devc-key ${setup.key.ok ? 'ok' : 'bad'}`} title={setup.key.ok ? setup.key.email : ''}>
            <span className="devc-key-dot" />
            {setup.key.ok
              ? <span className="devc-key-line">Cursor key <strong>{setup.key.name}</strong></span>
              : <span className="devc-key-line">Cursor API key rejected — rotate SSM <code>/&lt;project-slug&gt;/cursor-api-key</code> and restart the backend.</span>}
          </div>
        )}
        {setup?.modelSeam && (
          <div
            className="devc-key ok"
            title={`The app's own model calls (chat, retrieval, embeddings, images, video) — config/app models.*. ${setup.modelSeam.viaNventr ? `Routed through the nventr gateway at ${setup.modelSeam.gateway || '(no URL)'}: nventr/auto picks a tier per request and escalates on failure; spend meters to your organization's model credits. Bring your own keys to route direct.` : 'Direct to the vendor on this instance\u2019s own keys.'}${Object.keys(setup.modelSeam.stages || {}).length ? ` Stage pins: ${Object.entries(setup.modelSeam.stages).map(([k, v]) => `${k}→${v}`).join(', ')}.` : ''}`}
          >
            <span className="devc-key-dot" />
            <span className="devc-key-line">Models <strong>{setup.modelSeam.viaNventr ? 'nventr smart routing' : setup.modelSeam.provider}</strong></span>
            <span className="devc-key-detail">
              {[['chat', setup.modelSeam.chat], ['embed', setup.modelSeam.embed], ['image', setup.modelSeam.image], ['video', setup.modelSeam.video]].map(([k, v]) => (
                <span key={k} title={`${k}: ${v || '—'}`}><span className="devc-dim">{k}</span> {v || '—'}</span>
              ))}
            </span>
          </div>
        )}
        {setup && !setup.ready && (
          <div className="devc-warn">
            {setup.runtime === 'runner'
              ? (!setup.repoConfigured
                ? <>No repo configured — set <code>DEV_REPO</code> for this instance to enable agent runs.</>
                : setup.driverKey === false
                  ? <>The <code>{setup.driver}</code> agent driver needs its key — store it at <code>{setup.driverKeyHint}</code> (or switch <code>dev.agentDriver</code> in live config).</>
                  : <>Git token missing for <code>{setup.gitProvider}</code> — the runner cannot clone or open PRs.</>)
              : <>Cursor API key missing. Store one at SSM <code>/&lt;project-slug&gt;/cursor-api-key</code> to enable agent runs.</>}
          </div>
        )}
        {setup?.preflight && (setup.preflight.blocking?.length > 0 || setup.preflight.warnings?.length > 0) && (
          <div className={`devc-preflight${setup.preflight.blocking?.length ? ' bad' : ''}`} data-nova-note="Dev Console launch gate: each line is a check that failed, with its fix. Red blocks sessions and deploys; amber is a warning.">
            <div className="devc-preflight-head">
              <span className="devc-key-dot" />
              <strong>{setup.preflight.blocking?.length ? 'Not ready to launch' : 'Preflight warnings'}</strong>
              <span className="devc-dim">{setup.preflight.checks?.filter((c) => c.ok && !c.skipped).length || 0} checks green</span>
            </div>
            {[...(setup.preflight.blocking || []), ...(setup.preflight.warnings || [])].map((c) => (
              <div key={c.id} className={`devc-preflight-row ${c.level}`}>
                <span className="devc-preflight-label">{c.label}</span>
                <span className="devc-preflight-detail">{c.detail}</span>
                {c.fix && <span className="devc-preflight-fix">Fix: {c.fix}</span>}
              </div>
            ))}
          </div>
        )}
        {setup?.slo && setup.slo.ok === false && (
          <div className="devc-preflight" data-nova-note="Dev Console SLO breach: one of the four reliability targets (session start p95, stuck runs, ship success, CodeCommit exhausted) is out of target over the last 24 h. Sessions still launch; the number tells you what to chase.">
            <div className="devc-preflight-head">
              <span className="devc-key-dot" />
              <strong>SLO breach</strong>
              <span className="devc-dim">targets in live config <code>dev.slo</code></span>
            </div>
            {(setup.slo.results || []).filter((r) => !r.ok && !r.na).map((r) => (
              <div key={r.id} className="devc-preflight-row warn">
                <span className="devc-preflight-label">{r.id}</span>
                <span className="devc-preflight-detail">{r.text}</span>
              </div>
            ))}
          </div>
        )}
        {setup?.ready && setup.repoConnected === false && (
          <div className="devc-warn">
            The repo isn&apos;t connected to Cursor cloud agents — runs will fail to start.
            Fix: <a href="https://cursor.com/dashboard/integrations" target="_blank" rel="noopener noreferrer">cursor.com/dashboard → Integrations</a> →
            grant the Cursor GitHub App access to <code>{setup.repo}</code>.
          </div>
        )}
        <input
          className="devc-search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search sessions…"
        />
        <div className="devc-filters">
          {[['all', 'All'], ['live', 'Live'], ['finished', 'Done'], ['failed', 'Failed'], ['archived', 'Archived']].map(([k, label]) => (
            <button
              key={k}
              className={`devc-filter${statusFilter === k ? ' active' : ''}`}
              onClick={() => setStatusFilter(k)}
            >
              {label}
              {k === 'live' && sessions.some((s) => LIVE.includes(s.status)) && <span className="devc-pulse" />}
            </button>
          ))}
        </div>
        {visibleSessions.map((s) => {
          const c = costUsd(s.usage);
          return (
            <button
              key={s.id}
              className={`devc-sess${selected === s.id ? ' active' : ''}`}
              onClick={() => { if (s.graphRow) { setGraphFocus(s.graphId); setView('graphs'); } else setSelected(s.id); }}
              title={s.graphRow ? 'A graph of sessions — opens the Graphs view' : undefined}
            >
              <div className="devc-sess-title">
                {LIVE.includes(s.status) && <span className="devc-pulse" />}
                {s.graphRow ? '⌥ ' : ''}{s.title || s.id}
              </div>
              <div className="devc-sess-meta">
                {s.graphRow ? <span className="devc-chip blue">graph · {s.nodes} node{s.nodes === 1 ? '' : 's'}</span> : <Chip status={s.status} />}
                {!s.graphRow && s.graphId && <span className="devc-chip blue" title="This session has a graph (review / fan-out / arena) — open it in Graphs">graph</span>}
                {s.kind && s.kind !== 'task' && !s.graphRow && <span className="devc-chip gray">{s.kind}</span>}
                {s.routineId && !s.graphRow && <span className="devc-chip gray" title={`Opened by routine ${s.routineId} for ${s.cause?.key || 'a cause'} — see Routines`}>routine</span>}
                {s.mode === 'plan' && !s.planExecuted && <span className="devc-chip blue">Plan</span>}
                {s.attempts > 1 && <span className="devc-chip amber">×{s.attempts} tries</span>}
                {s.prUrl && <span className="devc-chip gray">PR</span>}
                {s.repoRef?.id && s.repoRef.id !== 'default' && <span className="devc-chip gray" title={s.repoRef.slug || ''}>{s.repoRef.label}</span>}
                {shipBadge(s)}
                <span className="devc-dim">{fmtAgo(s.createdAt)}{c != null ? ` · $${c.toFixed(2)}` : ''}</span>
              </div>
            </button>
          );
        })}
        {!visibleSessions.length && (
          <div className="devc-dim" style={{ padding: 10 }}>
            {sessions.length ? 'No sessions match.' : 'No sessions yet — describe a change below to start one.'}
          </div>
        )}
      </aside>

      <main
        className="devc-main"
        onPaste={onPaste}
        onDragEnter={onDragEnter}
        onDragOver={(e) => e.preventDefault()}
        onDragLeave={onDragLeave}
        onDrop={onDrop}
      >
        {dragging && (
          <div className="devc-drop" aria-hidden="true">
            <div>Drop files or whole folders to attach</div>
          </div>
        )}

        {/* Operator views cover the session workspace (sidebar toggles back). */}
        {view !== 'sessions' && (
          <div className="devc-view-overlay">
            {view === 'config' && <ConfigPanel isAdmin={role === 'admin'} />}
            {view === 'access' && <AccessPanel isAdmin={role === 'admin'} />}
            {view === 'secrets' && <SecretsPanel isAdmin={role === 'admin'} />}
            {view === 'ops' && <OpsPanel />}
            {view === 'ecosystem' && <EcosystemPanel />}
            {view === 'agents' && <AgentsPanel />}
            {view === 'lab' && <LabReportsPanel />}
            {view === 'build' && <BuildPanel onOpenSession={(id) => { setSelected(id); setView('sessions'); }} />}
            {view === 'selftest' && <SelfTestPanel />}
            {view === 'routing' && <RoutingPanel />}
            {view === 'infra' && <InfraPanel internal onOpenSession={(id) => { setSelected(id); setView('sessions'); }} />}
            {view === 'graphs' && <GraphPanel sessions={sessions} initialId={graphFocus} onOpenSession={(id) => { setSelected(id); setView('sessions'); }} />}
            {view === 'routines' && <RoutinesPanel onOpenSession={(id) => { setSelected(id); setView('sessions'); }} />}
            {view === 'ide' && <IdePanel internal request={ideRequest} onOpenSession={(id) => { setSelected(id); setView('sessions'); setTab('pr'); }} />}
          </div>
        )}

        <DeployBanner
          sessions={sessions}
          onOpen={(j) => { if (j.sessionId) { setSelected(j.sessionId); setView('sessions'); setTab('terminal'); setTermFilter('deploy'); } }}
        />
        <div className="devc-head">
          {phone && (
            <button type="button" className="devc-btn devc-btn-icon devc-rail-btn" aria-label="Sessions and views" aria-expanded={railOpen} onClick={() => setRailOpen((v) => !v)}>
              ☰
            </button>
          )}
          <div className="devc-head-title">
            {session ? (
              editingTitle !== null ? (
                <input
                  className="devc-title-input"
                  value={editingTitle}
                  autoFocus
                  onChange={(e) => setEditingTitle(e.target.value)}
                  onBlur={rename}
                  onKeyDown={(e) => { if (e.key === 'Enter') rename(); if (e.key === 'Escape') setEditingTitle(null); }}
                />
              ) : (
                <h2 title="Click to rename" onClick={() => setEditingTitle(session.title || '')}>
                  {session.title || selected}
                  <span className="devc-edit-hint">✎</span>
                </h2>
              )
            ) : (
              <h2>Dev Console</h2>
            )}
            <div className="devc-dim">
              {session
                ? <>
                    {session.mode && session.mode !== 'agent' && <span className="devc-chip blue">{session.mode}</span>}
                    {session.model && (
                      <span className="devc-chip gray">
                        {session.model}
                        {session.modelParams?.length ? ` · ${session.modelParams.map((p) => p.value).join(' · ')}` : ''}
                      </span>
                    )}
                    {session.route?.tier && (
                      <span
                        className={`devc-chip ${session.route.tier === 'frontier' ? 'amber' : session.route.tier === 'bulk' ? 'green' : 'blue'}`}
                        title={`Smart routing (${session.route.engine}${session.route.jev && !session.route.jev.applied && session.route.jev.tier ? `, Jev said ${session.route.jev.tier}` : ''}${session.route.pipeline && !session.route.pipeline.applied && session.route.pipeline.tier ? `, policy ${session.route.pipeline.id} said ${session.route.pipeline.tier}` : ''}): ${(session.route.reasons || []).join('; ')}`}
                      >
                        {session.route.escalated ? `${session.route.escalated.from} → ${session.route.tier}` : session.route.tier}
                      </span>
                    )}
                    {' '}{running ? <><strong className="devc-phase">{phaseLine(session)}</strong> · {fmtDur(elapsed)}</> : session.durationMs ? `took ${fmtDur(session.durationMs)}` : ''}
                    {!running && session.toolCalls ? <> · {session.toolCalls} tool call{session.toolCalls === 1 ? '' : 's'}</> : null}
                    {cost != null && <> · ${cost.toFixed(2)}</>}
                    {session.usage && <> · {fmtTok(session.usage.inputTokens)} in / {fmtTok(session.usage.outputTokens)} out</>}
                    {running && lastEventTs && <> · last activity {fmtAgo(lastEventTs)}</>}
                  </>
                : <>
                    The agent works on{' '}
                    {setup?.repo
                      ? <a className="devc-head-repo" href={setup.repo} target="_blank" rel="noopener noreferrer" title={setup.repo}>{repoLabel(setup.repo)}</a>
                      : 'the repo'}
                    {' '}and opens a PR — nothing deploys until you press Ship. The strip above each session says what it still needs.
                  </>}
            </div>
          </div>
          <div className="devc-head-actions">
            {session && <Chip status={session.status} />}
            {session?.mode === 'plan' && !session.planExecuted && <span className="devc-chip blue" title={session.modeAuto ? `Auto: ${session.modeReason}` : 'Plan first'}>Plan{session.modeAuto ? ' · auto' : ''}</span>}
            {session?.awaitingAnswers && <span className="devc-chip gray">Waiting for your answers</span>}
            {session?.archived && <span className="devc-chip gray">Archived</span>}
            {session?.agentId && (
              <a
                className="devc-btn"
                href={`https://cursor.com/agents?id=${session.agentId}`}
                target="_blank"
                rel="noopener noreferrer"
                title="Inspect or continue this agent in Cursor's own UI"
              >
                Open in Cursor ↗
              </a>
            )}
            {running && <button className="devc-btn danger" onClick={stop} data-agent-noclick>■ Stop</button>}
            {session && !running && <button className="devc-btn" title="Start a new session with this prompt" onClick={retry}>↻ Retry</button>}
            {session && !running && (
              <button className="devc-btn" title={session.archived ? 'Restore to the main list' : 'Move out of the main list (reversible)'} onClick={toggleArchive}>
                {session.archived ? '⬆ Unarchive' : '📥 Archive'}
              </button>
            )}
            {session && !running && (
              confirmDelete
                ? <>
                    <button className="devc-btn danger" onClick={remove} data-agent-noclick>Confirm delete</button>
                    <button className="devc-btn" onClick={() => setConfirmDelete(false)}>Keep</button>
                  </>
                : <button className="devc-btn" onClick={() => setConfirmDelete(true)}>🗑</button>
            )}
          </div>
        </div>

        {selected && (
          <ShipStrip
            session={session}
            sessionId={selected}
            busy={busy}
            onAction={shipAction}
            onOpenTerminal={() => { setTab('terminal'); setTermFilter('deploy'); }}
          />
        )}

        {selected && (
          <div className="devc-tabs">
            {TABS.map(([k, label]) => (
              <button key={k} className={`devc-tab${tab === k ? ' active' : ''}`} onClick={() => setTab(k)}>
                {label}
                {k === 'pr' && session?.prUrl && <span className="devc-tab-dot green" />}
                {k === 'preview' && session?.previewUrl && <span className="devc-tab-dot green" />}
                {k === 'terminal' && running && <span className="devc-tab-dot blue" />}
              </button>
            ))}
            <label className="devc-dim devc-tab-opt">
              <input type="checkbox" checked={showThinking} onChange={(e) => setShowThinking(e.target.checked)} /> thinking
            </label>
            <label className="devc-dim devc-tab-opt">
              <input type="checkbox" checked={autoScroll} onChange={(e) => setAutoScroll(e.target.checked)} /> auto-scroll
            </label>
          </div>
        )}

        {selected && familyAttempts.length > 1 && (
          <div className="devc-attempts">
            <span className="devc-dim">Attempts:</span>
            {familyAttempts.map((a, i) => (
              <button
                key={a.id}
                className={`devc-filter${a.id === selected ? ' active' : ''}`}
                title={`${CHIP[a.status]?.[0] || a.status} · ${fmtAgo(a.createdAt)}`}
                onClick={() => setSelected(a.id)}
              >
                #{i + 1} {FAILED.includes(a.status) ? '✗' : a.status === 'finished' ? '✓' : '…'}
              </button>
            ))}
          </div>
        )}

        <div className="devc-panes">
          {(!selected || tab === 'chat') && (
            <section className={`devc-chat${!selected ? ' devc-chat-hero' : ''}`}>
              {!selected && (
                <div className="devc-hero">
                  <h3>Start a dev cycle</h3>
                  <p>
                    Describe the change you want — bug fix, new feature, copy tweak, or “build an app that…”.
                    Paste screenshots, drop files or whole folders (specs, data, exports, code — any type, thousands of
                    files are fine); the agent gets them with the prompt. Big or vague asks get a plan you approve first;
                    small ones go straight to a pull request you review and ship from here.
                  </p>
                  <div className="devc-tpl-grid">
                    {TEMPLATES.map((t) => (
                      <button
                        key={t.id}
                        className={`devc-tpl-card${activeTemplate === t.id ? ' active' : ''}`}
                        onClick={() => {
                          setActiveTemplate(activeTemplate === t.id ? null : t.id);
                          setFieldValues({});
                        }}
                      >
                        <span className="devc-tpl-icon" aria-hidden>{t.icon}</span>
                        <span className="devc-tpl-label">{t.label}</span>
                        <span className="devc-tpl-desc">{t.desc}</span>
                      </button>
                    ))}
                  </div>
                  <div className="devc-hero-hint devc-dim">
                    Pick a starting point above, or just type below. <strong>Auto</strong> plans first when the ask is
                    feature-sized and asks you questions when a decision is yours; force <strong>Plan</strong> or
                    <strong>Agent</strong> any time.
                  </div>
                  {view === 'sessions' && <EcosystemWidget onOpen={() => setView('ecosystem')} />}
                </div>
              )}
              {groupedEvents.map((e) => {
                if (e.type === 'toolgroup') return <ToolGroup key={e.id} items={e.items} />;
                if (e.type === 'status') {
                  const at = new Date(e.ts).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' });
                  return (
                    <div key={e.id} className={`devc-step${e.status === 'warn' ? ' is-warn' : ''}`} role="status">
                      <span className="devc-step-dot" aria-hidden="true" />
                      <span className="devc-step-text">{e.message}</span>
                      <span className="devc-step-at">{at}</span>
                    </div>
                  );
                }
                if (e.type === 'user') {
                  // The context envelope travels with the prompt but renders
                  // as a compact chip, not a wall of JSON.
                  const [intent] = (e.text || '').split('\n---\ncontext.json');
                  const hasEnvelope = intent.length !== (e.text || '').length;
                  return (
                    <div key={e.id} className="devc-msg user">
                      <div className="devc-msg-who">
                        You{e.images ? ` · ${e.images} image${e.images > 1 ? 's' : ''}` : ''}
                        {(e.attachments || []).filter((a) => a.kind !== 'image').length > 0 && ` · ${(e.attachments || []).filter((a) => a.kind !== 'image').length} file${(e.attachments || []).filter((a) => a.kind !== 'image').length > 1 ? 's' : ''}`}
                        {e.package && <span className="devc-chip gray" style={{ marginLeft: 8 }} title={e.package.name}>🗂 {e.package.name} · {Number(e.package.files).toLocaleString()} files</span>}
                        {e.answers && <span className="devc-chip blue" style={{ marginLeft: 8 }}>answers</span>}
                        {hasEnvelope && <span className="devc-chip gray" style={{ marginLeft: 8 }}>context.json attached</span>}
                      </div>
                      <ReactMarkdown remarkPlugins={[remarkGfm]} components={mdComponents}>{intent.trimEnd()}</ReactMarkdown>
                    </div>
                  );
                }
                if (e.type === 'assistant') {
                  return (
                    <div key={e.id} className="devc-msg agent">
                      <div className="devc-msg-who">Agent</div>
                      <ReactMarkdown remarkPlugins={[remarkGfm]} components={mdComponents}>{e.text}</ReactMarkdown>
                    </div>
                  );
                }
                if (e.type === 'thinking' && showThinking) {
                  return <div key={e.id} className="devc-msg thinking">{e.text}</div>;
                }
                if (e.type === 'questions') {
                  const answered = events.some((u) => u.type === 'user' && u.seq > e.seq);
                  return <QuestionsCard key={e.id} e={e} answered={answered} busy={busy || running} onAnswer={(answers) => answerQuestions(e, answers)} />;
                }
                if (e.type === 'plan') {
                  return (
                    <PlanCard
                      key={e.id}
                      e={e}
                      superseded={e.id !== latestPlan?.id}
                      pending={e.id === latestPlan?.id && planPending}
                      execLabel={execTargetId}
                      execInvalid={execTargetInvalid}
                    />
                  );
                }
                if (e.type === 'result') {
                  // The run's final text usually repeats the assistant bubbles
                  // the member just read — show the status strip only then.
                  const repeated = resultRepeatsAssistant(e, events);
                  return (
                    <div key={e.id} className={`devc-msg result ${e.status}${repeated || !e.text ? ' slim' : ''}`}>
                      <div className="devc-msg-who">Run {e.status}{repeated && e.prUrl ? ' · pull request opened' : ''}</div>
                      {e.error && <div className="devc-err">{e.error}</div>}
                      {e.text && !repeated && <ReactMarkdown remarkPlugins={[remarkGfm]} components={mdComponents}>{e.text}</ReactMarkdown>}
                      {e.prUrl && <a className="devc-link" href={e.prUrl} target="_blank" rel="noopener noreferrer">{e.prUrl}</a>}
                      {e.status === 'finished' && e.prUrl && (
                        <div className="devc-result-actions">
                          {session?.previewUrl
                            ? <button className="devc-btn" onClick={() => setTab('preview')}>Open preview ↗</button>
                            : <button className="devc-btn" onClick={() => setTab('pr')}>Build preview</button>}
                          <button className="devc-btn" onClick={() => setTab('pr')}>Review diff</button>
                          <button className="devc-btn primary" onClick={() => document.querySelector('.devc-ship')?.scrollIntoView({ behavior: 'smooth', block: 'center' })}>Ship ↑</button>
                        </div>
                      )}
                      {!running && e.id === lastResultId && <SessionFeedback sessionId={selected} feedback={session?.feedback} />}
                    </div>
                  );
                }
                return null;
              })}
              {running && (
                <div className={`devc-working${stalled ? ' is-stalled' : ''}`} role="status">
                  <span className={`devc-pulse${stalled ? ' is-stalled' : ''}`} />
                  {session?.status === 'starting' && !session?.phase
                    ? <>Waiting for the runner to start{elapsed ? ` · ${fmtDur(elapsed)}` : ''}…</>
                    : <>{phaseLine(session)}{liveTs ? ` — last activity ${fmtAgo(liveTs)}` : ''}…</>}
                  {liveThought && <span className="devc-working-thought">{liveThought}</span>}
                  {stalled && (
                    <span className="devc-stall-note">
                      No activity for {fmtDur(Date.now() - liveTs)}. A long tool call can look like this; if the runner died it is marked failed automatically within a few minutes — or Stop and Retry now.
                    </span>
                  )}
                </div>
              )}
              {session && FAILED.includes(session.status) && (session.errorUser || session.errorMessage) && (
                <FailureCard
                  failure={session}
                  style={{ margin: '8px 0' }}
                  onRetry={retry}
                  onFollowup={() => { setTab('chat'); document.querySelector('.devc-composer textarea, textarea[name="prompt"]')?.focus(); }}
                  onStatus={() => window.open('/dev/status', '_blank')}
                />
              )}
              {planPending && session.status === 'finished' && (
                <div className="devc-approve">
                  {latestPlan?.text ? (
                    <div className="devc-approve-label">
                      Review the plan above — the agent hasn&apos;t touched any files. Execute it, refine it, or drop it.
                    </div>
                  ) : (
                    // Approval is gated on a rendered plan: no plan card in
                    // chat (empty, failed, or a pre-plan-event session) means
                    // nothing reviewable will execute — regenerate first.
                    <div className="devc-approve-label devc-approve-warn">
                      The agent finished without writing a plan. Ask it to write the complete plan now, or switch
                      to Agent mode and describe what to build directly.
                      <div><button className="devc-btn" style={{ marginTop: 8 }} disabled={busy} onClick={requestPlan}>Ask for the full plan</button></div>
                    </div>
                  )}
                  {setup?.models?.length > 0 && (
                    <div className="devc-approve-config">
                      <span className="devc-param">
                        <span>Execute with</span>
                        <ModelSelector
                          models={setup.models}
                          value={execModel}
                          onChange={pickExecModel}
                          emptyLabel={`${session?.model || 'plan model'} (keep)`}
                          excludeIds={['default']}
                        />
                      </span>
                      <ModelParamsSelector modelDef={execModelDef} picks={execParams} onChange={setExecParams} />
                      <span className="devc-dim">
                        Planning is done — a cheaper model can often execute a good plan.
                      </span>
                    </div>
                  )}
                  {execTargetInvalid && (
                    <div className="devc-err" style={{ marginBottom: 10 }}>
                      Model &lsquo;{execTargetRaw}&rsquo; isn&apos;t in the current model catalog, so executing
                      would fail at run start. Pick a model above, or Retry the model list from the composer.
                    </div>
                  )}
                  <div className="devc-approve-actions" data-agent-guard>
                    <button
                      className="devc-btn primary"
                      disabled={busy || !canApprovePlan}
                      title={!latestPlan?.text
                        ? 'A plan must render in chat before it can be approved'
                        : execTargetInvalid ? 'The selected execution model is not in the catalog' : ''}
                      onClick={approvePlan}
                    >
                      ✓ Approve &amp; Execute
                    </button>
                    <button className="devc-btn" onClick={() => composerInput.current?.focus()}>Revise plan…</button>
                    <button className="devc-btn danger" onClick={discardPlan}>Discard</button>
                  </div>
                </div>
              )}
              {selected && session?.prUrl && tab === 'chat' && (
                <button className="devc-btn" style={{ alignSelf: 'flex-start' }} onClick={() => setTab('pr')}>
                  Review diff → <span className="devc-dim">(ship from the strip above)</span>
                </button>
              )}
              <div ref={chatEnd} />
            </section>
          )}

          {selected && tab === 'activity' && (
            <section className="devc-chat devc-activity">
              {events.filter((e) => e.type !== 'thinking' || showThinking).map((e) => <ActivityRow key={e.id} e={e} />)}
              {!events.length && <div className="devc-dim">Every event — messages, tool calls, tokens, results — lands here in real time.</div>}
              <div ref={chatEnd} />
            </section>
          )}

          {selected && tab === 'terminal' && (
            <section className="devc-term devc-term-full">
              <div className="devc-term-head">
                Terminal — agent shell + deploy output
                <span className="devc-term-tools">
                  {[['all', 'All'], ['agent', 'Agent'], ['deploy', 'Deploy']].map(([k, label]) => (
                    <button key={k} className={`devc-filter${termFilter === k ? ' active' : ''}`} onClick={() => setTermFilter(k)}>{label}</button>
                  ))}
                  <button
                    className="devc-filter"
                    onClick={() => navigator.clipboard?.writeText(terminal.map((r) => r.text).join('\n')).catch(() => {})}
                  >
                    Copy
                  </button>
                  <button
                    className="devc-filter"
                    title="Download the complete session record — every event plus deploy logs"
                    onClick={() => {
                      const lines = events.map((e) => {
                        const at = e.ts ? new Date(e.ts).toISOString() : '';
                        if (e.type === 'tool') return `[${at}] TOOL ${e.name} (${e.status})\n${e.args || ''}\n${e.result || ''}`;
                        if (e.type === 'result') return `[${at}] RESULT ${e.status} ${e.error || ''}\n${e.text || ''}`;
                        return `[${at}] ${String(e.type).toUpperCase()}\n${e.text || e.message || ''}`;
                      });
                      jobLogs.forEach((l) => lines.push(`[${new Date(l.ts).toISOString()}] DEPLOY\n${l.text}`));
                      const blob = new Blob([lines.join('\n\n')], { type: 'text/plain' });
                      const a = document.createElement('a');
                      a.href = URL.createObjectURL(blob);
                      a.download = `dev-session-${selected}.txt`;
                      a.click();
                      URL.revokeObjectURL(a.href);
                    }}
                  >
                    Download
                  </button>
                </span>
              </div>
              <div className="devc-term-body">
                {terminal.map((r, i) => (
                  // eslint-disable-next-line react/no-array-index-key
                  <pre key={i} className={r.deploy ? 'deploy' : ''}>{r.text}</pre>
                ))}
                {!terminal.length && <div className="devc-dim">Shell commands and deploy logs stream here.</div>}
                <div ref={termEnd} />
              </div>
            </section>
          )}

          {selected && tab === 'pr' && (
            <section className="devc-chat">
              <PreviewCard session={session} sessionId={selected} onReady={() => setTab('preview')} />
              <DiffViewer sessionId={selected} prUrl={session?.prUrl} summaryFirst={narrow} />
              {session?.branch && (
                <div className="devc-dim devc-ide-cta">
                  Want to edit this branch by hand?{' '}
                  <button type="button" className="devc-btn" onClick={() => { setIdeRequest({ branch: session.branch, sessionId: selected }); setView('ide'); }} title="Open a full IDE workspace on this session's branch; pushes update this PR">
                    Open in IDE
                  </button>
                </div>
              )}
              <details className="devc-advanced">
                <summary>Advanced: push one target at a time (the Ship strip above does all of them)</summary>
                <DeployCard session={session} sessionId={selected} setup={setup} />
              </details>
            </section>
          )}

          {selected && tab === 'preview' && (
            <section className="devc-preview">
              {session?.previewUrl ? (
                <>
                  <div className="devc-preview-bar">
                    <span className="devc-dim">Preview of PR branch — production is untouched.</span>
                    <a className="devc-link" href={session.previewUrl} target="_blank" rel="noopener noreferrer">{session.previewUrl}</a>
                    <span className="devc-preview-tools">
                      <button className="devc-filter" onClick={() => window.open(session.previewUrl, '_blank', 'noopener')}>Open in new tab ↗</button>
                      <button className="devc-filter" onClick={() => setTab('pr')}>Rebuild / Deploy →</button>
                    </span>
                  </div>
                  <iframe className="devc-preview-frame" src={session.previewUrl} title="UI preview" />
                  <div className="devc-dim" style={{ padding: '6px 2px' }}>
                    The preview runs on its own temporary domain, so sign in once inside the frame (the domain is
                    auto-authorized). Expires after 7 days.
                  </div>
                </>
              ) : (
                <div className="devc-empty" style={{ padding: 16 }}>
                  <h3>No preview yet</h3>
                  <p>
                    Once the agent opens a PR, build a preview from the <strong>PR &amp; Deploy</strong> tab — you get a
                    temporary URL of the app with the change applied, before anything is merged or deployed.
                  </p>
                  <PreviewCard session={session} sessionId={selected} />
                </div>
              )}
            </section>
          )}

          {selected && tab === 'artifacts' && (
            <section className="devc-chat">
              <ArtifactsPanel sessionId={selected} running={!!running} />
            </section>
          )}

          {selected && tab === 'usage' && (
            <section className="devc-chat">
              <UsagePanel sessionId={selected} />
            </section>
          )}

          {selected && tab === 'details' && session && (
            <section className="devc-chat">
              <div className="devc-card">
                <div className="devc-card-title">Session details</div>
                <div className="devc-details">
                  <div><span>Status</span><Chip status={session.status} /></div>
                  <div>
                    <span>Mode</span>
                    {session.mode === 'plan'
                      ? (session.planExecuted ? 'plan → executed' : 'plan (awaiting approval)')
                      : (session.mode || '—')}
                  </div>
                  <div><span>Model</span>{session.model || '—'}</div>
                  <div>
                    <span>Model params</span>
                    {session.modelParams?.length
                      ? session.modelParams.map((p) => `${p.id}=${p.value}`).join(', ')
                      : '—'}
                  </div>
                  <div>
                    <span>Agent options</span>
                    {session.agentOptions
                      ? [
                          session.agentOptions.autoCreatePR === false && 'no auto-PR',
                          session.agentOptions.skipReviewerRequest === false && 'request reviewer',
                          session.agentOptions.workOnCurrentBranch && 'current branch',
                          session.agentOptions.openAsCursorGithubApp === true && 'PR as Cursor app',
                          session.agentOptions.openAsCursorGithubApp === false && 'PR as key owner',
                          session.agentOptions.startingRef && `from ${session.agentOptions.startingRef}`,
                        ].filter(Boolean).join(', ') || 'defaults'
                      : '—'}
                  </div>
                  <div><span>Created</span>{session.createdAt ? new Date(session.createdAt).toLocaleString() : '—'}</div>
                  <div><span>By</span>{session.createdByEmail || session.createdBy || '—'}</div>
                  <div><span>Duration</span>{fmtDur(session.durationMs)}</div>
                  <div><span>Tokens in</span>{fmtTok(session.usage?.inputTokens)}</div>
                  <div><span>Tokens out</span>{fmtTok(session.usage?.outputTokens)}</div>
                  <div><span>Cache read</span>{fmtTok(session.usage?.cacheReadTokens)}</div>
                  <div><span>Cost</span>{cost != null ? `$${cost.toFixed(3)}` : session.usage ? 'pending' : '—'}</div>
                  <div><span>Runs</span>{session.usage?.runs ?? '—'}</div>
                  <div>
                    <span>Agent</span>
                    {session.agentId
                      ? <a className="devc-link" href={`https://cursor.com/agents/${session.agentId}`} target="_blank" rel="noopener noreferrer">{session.agentId}</a>
                      : '—'}
                  </div>
                  <div><span>PR</span>{session.prUrl ? <a className="devc-link" href={session.prUrl} target="_blank" rel="noopener noreferrer">{session.prUrl}</a> : '—'}</div>
                  <div><span>Branch</span>{session.branch || '—'}</div>
                  <div><span>Deploy job</span>{session.deployJobId || '—'}</div>
                </div>
                <div className="devc-dim" style={{ marginTop: 8 }}>
                  Cost reconciles hourly (Cursor billing is eventually consistent). Session costs roll up on the
                  Cost Report page.
                </div>
              </div>
            </section>
          )}
        </div>

        <div className="devc-compose">
          {selected && (
            <div className="devc-compose-ctx">
              <span className="devc-dim">
                {session?.mode === 'plan' && !session?.planExecuted ? 'Refining plan:' : 'Follow-up to:'}
              </span>
              <strong>{session?.title || selected}</strong>
              <button
                className="devc-filter"
                title="Switch to a new session"
                onClick={() => { setSelected(null); setPrompt(''); }}
              >
                ✕ new session
              </button>
            </div>
          )}
          {!selected && activeTemplate && (
            <div className="devc-fields">
              {(TEMPLATES.find((t) => t.id === activeTemplate)?.fields || []).map((f) => (
                <label key={f.key} className="devc-field">
                  <span>{f.label}</span>
                  <input
                    value={fieldValues[f.key] || ''}
                    placeholder={f.ph}
                    onChange={(e) => setFieldValues({ ...fieldValues, [f.key]: e.target.value })}
                  />
                </label>
              ))}
              <button
                className="devc-filter"
                title="Clear template"
                onClick={() => { setActiveTemplate(null); setFieldValues({}); }}
              >
                ✕ {TEMPLATES.find((t) => t.id === activeTemplate)?.label}
              </button>
            </div>
          )}
          <ReferencesStrip repoId={selected ? (session?.repoRef?.id || 'default') : (repoId || 'default')} stamp={refsStamp} />
          <AttachmentTray
            entries={files}
            upload={upload}
            onRemove={removeEntry}
            onRemoveFolder={removeFolder}
            onClear={() => setFiles([])}
            onPin={togglePin}
          />
          {notice && (
            <div className="devc-notice" role="status">
              {notice}
              <button type="button" className="devc-filter" onClick={() => setNotice('')}>Dismiss</button>
            </div>
          )}
          {error && <div className="devc-err">{error}</div>}
          {phone && (
            <div className="devc-options-row">
              <button type="button" className={`devc-filter${optionsOpen ? ' active' : ''}`} aria-expanded={optionsOpen} onClick={() => setOptionsOpen((v) => !v)}>
                Options{!optionsOpen && <span className="devc-dim"> · {mode}{model ? ` · ${model}` : ''}{!selected && repoId && repoId !== 'default' ? ` · ${repoId}` : ''}</span>}
              </button>
            </div>
          )}
          <div className={`devc-config${phone && !optionsOpen ? ' is-folded' : ''}`}>
            <ModeSelector modes={setup?.modes} value={mode} onChange={pickMode} />
            {!selected && <RoutingModeSelector modes={setup?.router?.modes} value={routingMode} onChange={(id) => { setRoutingMode(id); saveStoredConfig({ routingMode: id }); }} />}
            {!selected && <RepoSelector repos={setup?.repos} value={repoId} onChange={setRepoId} />}
            {selected && session?.repoRef?.id && session.repoRef.id !== 'default' && <span className="devc-param" title={`This conversation works ${session.repoRef.slug || session.repoRef.label}`}><span>Repo</span><span className="devc-chip">{session.repoRef.label}</span></span>}
            {selected && session?.modeId && <span className="devc-param" title="The profile this conversation was started in"><span>Profile</span><span className="devc-chip">{session.modeId}</span></span>}
            {(setup?.models?.length > 0) ? (
              <ModelSelector
                models={setup.models}
                value={model}
                onChange={pickModel}
                // '' keeps the conversation's current model on follow-ups;
                // on new sessions the backend routes each ask to a tier
                // (bulk / strong / frontier) and picks the model for it.
                emptyLabel={selected ? `${session?.model || 'current model'} (keep)` : `Auto — smart routing${setup.router?.engine && setup.router.engine !== 'rules' ? ` (${setup.router.engine})` : ''}`}
                excludeIds={['default']}
              />
            ) : (
              // Catalog unavailable: either /dev/status itself is unreachable
              // (setupFailed — nothing on this page will work) or the status
              // loaded but Cursor's model list failed (runs still work on the
              // server-side default model). Retry re-fetches with cache bypass.
              <span
                className="devc-param devc-model-fallback"
                title={
                  setupFailed
                    ? 'Dev Console API unreachable from this origin — see the warning in the sidebar'
                    : setup?.modelsError || 'Model catalog unavailable — runs use the default model'
                }
              >
                <span>Model</span>
                <select disabled>
                  <option>
                    {setup ? `${setup.model} (default)` : setupFailed ? 'API unreachable' : 'loading…'}
                  </option>
                </select>
                {(setup || setupFailed) && (
                  <button type="button" className="devc-filter" onClick={refreshStatus} disabled={statusBusy} title="Re-fetch the model list from Cursor (runs still work with the default model meanwhile)">
                    {statusBusy ? 'Retrying…' : 'Retry model list'}
                  </button>
                )}
              </span>
            )}
            <ModelParamsSelector modelDef={modelDef} picks={modelParams} onChange={pickParams} />
            {setup?.modelsError && !(setup?.models?.length > 0) && (
              <span className="devc-dim" title={setup.modelsError}>model list failed — using default</span>
            )}
            {!selected && <AdvancedAgentOptions value={agentOpts} onChange={setAgentOpts} />}
          </div>
          <div className="devc-compose-row">
            <button
              className="devc-btn devc-btn-icon"
              aria-label="Attach files"
              title={`Attach files — screenshots, documents (${DOC_EXT_LIST}), data, code… any type`}
              onClick={() => fileInput.current?.click()}
            >
              📎
            </button>
            <button
              className="devc-btn devc-btn-icon"
              aria-label="Attach a folder"
              title="Attach a whole folder (structure preserved — hundreds or thousands of files are fine)"
              onClick={pickFolder}
            >
              📁
            </button>
            <DictateButton
              disabled={busy}
              onStart={() => { dictateBase.current = prompt; }}
              onText={(t) => setPrompt(`${dictateBase.current}${dictateBase.current && !/\s$/.test(dictateBase.current) ? ' ' : ''}${t}`)}
            />
            <input
              ref={fileInput}
              type="file"
              multiple
              style={{ display: 'none' }}
              onChange={(e) => { addFiles(e.target.files); e.target.value = ''; }}
            />
            <input
              ref={(el) => { folderInput.current = el; if (el) { el.setAttribute('webkitdirectory', ''); el.setAttribute('directory', ''); } }}
              type="file"
              multiple
              style={{ display: 'none' }}
              onChange={(e) => { addFiles(e.target.files); e.target.value = ''; }}
            />
            <textarea
              ref={composerInput}
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              onKeyDown={(e) => {
                if (e.key !== 'Enter' || e.nativeEvent?.isComposing) return;
                if (e.shiftKey || e.altKey) return; // new line
                e.preventDefault();
                send();
              }}
              placeholder={
                selected
                  ? (session?.mode === 'plan' && !session?.planExecuted
                    ? 'Refine the plan… (↵ to send, ⇧↵ for a new line)'
                    : 'Follow up with the agent… paste screenshots, drop files or folders (↵ to send, ⇧↵ for a new line)')
                  : activeTemplate
                    ? 'Anything else to add? (optional — fields above carry the ask)'
                    : 'Describe what to build… paste screenshots, drop files or whole folders (↵ to send, ⇧↵ for a new line)'
              }
              rows={3}
            />
            <div className="devc-compose-side">
              <button className="devc-btn primary" disabled={busy || !canSend || (running && !!selected)} onClick={send} data-agent-noclick>
                {busy ? (upload ? 'Uploading…' : 'Sending…') : selected ? 'Send' : mode === 'plan' ? 'Start plan' : mode === 'auto' ? 'Start' : 'Start session'}
              </button>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
